import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import MenuImage from '../../components/MenuImage';
import FirebaseOrderService from '../../services/firebaseOrderService';
import { firebaseMenuService } from '../../services/firebaseMenuService';
import { firestorePrintService } from '../../services/firestorePrintService';
import { firebaseTableService } from '../../services/firebaseTableService';
import ManagerPinDialog from '../../components/ManagerPinDialog';
import {
  Box,
  Grid,
  Typography,
  Button,
  IconButton,
  Card,
  CardContent,
  Chip,
  Divider,
  TextField,
  InputAdornment,
  Dialog,
  Drawer,
  Badge,
  useMediaQuery,
} from '@mui/material';
import { ShoppingCart as ShoppingCartIcon } from '@mui/icons-material';
import {
  Add,
  Remove,
  Search,
  TableRestaurant,
  Edit,
} from '@mui/icons-material';
import CheckoutDialog, { CheckoutData } from '../../components/CheckoutDialog';
import SplitTableDialog from '../../components/SplitTableDialog';
import PayPalQRPayment from '../../components/PayPalQRPayment';
import AddItemWithAddOnsModal from '../../components/AddItemWithAddOnsModal';
import OpenShiftDialog from '../../components/OpenShiftDialog';
import { useLanguage } from '../../contexts/LanguageContext';
import { useInstantTheme } from '../../contexts/InstantThemeContext';
import SplitTableDraftService, { SplitTableDraft } from '../../services/splitTableDraftService';
import CheckoutDraftService, { CheckoutDraft } from '../../services/checkoutDraftService';
import { Drafts as DraftIcon, PlayArrow as ResumeIcon, Close as CloseIcon, Person } from '@mui/icons-material';
import { applyPriceOverride } from '../../utils/priceOverrides';
import FirebaseAuthService from '../../services/firebaseAuthService';
import { doc, getDoc } from '../../services/firestoreCompat';
import { db } from '../../services/firebaseConfig';
import { useLocation, useNavigate } from 'react-router-dom';

// BUILD DEBUG — change this on every deploy so you can confirm the browser has the latest code
const POS_BUILD_VERSION = 'v2.0.4-20260309-2100';
const POS_BUILD_PRINT = 'firestorePrintService'; // confirms which print service is active

// Logged-in User Display Component
const LoggedInUserDisplay: React.FC = () => {
  const [userInfo, setUserInfo] = useState<{ name: string; email: string } | null>(null);

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const authService = FirebaseAuthService.getInstance();
        const firebaseUser = authService.getCurrentUser();
        
        if (firebaseUser && firebaseUser.uid) {
          // Try to get user profile from Firestore
          const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
          
          if (userDoc.exists()) {
            const data = userDoc.data();
            const name = data.name || data.username || '';
            const email = data.email || firebaseUser.email || '';
            
            setUserInfo({
              name: name || email || 'Staff Member',
              email: email || ''
            });
          } else {
            // Fallback to Firebase Auth email
            const email = firebaseUser.email || '';
            setUserInfo({
              name: email || 'Staff Member',
              email: email
            });
          }
        }
      } catch (error) {
        console.error('❌ [DASHBOARD] Failed to fetch current user:', error);
      }
    };
    
    fetchCurrentUser();
  }, []);

  if (!userInfo) return null;

  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      p: 1,
      borderRadius: 1,
      backgroundColor: '#2d3048',
      border: '1px solid #3a3d52'
    }}>
      <Person sx={{ color: '#1976D2', fontSize: 20 }} />
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#e0e0e0', lineHeight: 1.2 }}>
          {userInfo.name}
        </Typography>
        {userInfo.email && userInfo.email !== userInfo.name && (
          <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.7rem', display: 'block' }}>
            {userInfo.email}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const firebaseOrderService = FirebaseOrderService.getInstance();

const MinimalistDashboard: React.FC = () => {
  const { t } = useLanguage();
  const { appTheme } = useInstantTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:768px)');
  const [mobileCartOpen, setMobileCartOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [cart, setCart] = useState<any[]>(() => {
    try {
      const saved = localStorage.getItem('saheeh_pos_cart');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch {}
    return [];
  });
  const [products, setProducts] = useState<any[]>([]);
  const [availableCategories, setAvailableCategories] = useState<Array<{ id: string; name: string; icon: string; image_url?: string }>>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [checkoutDialogOpen, setCheckoutDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [splitDialogOpen, setSplitDialogOpen] = useState(false);
  const [draftOrders, setDraftOrders] = useState<SplitTableDraft[]>([]);
  const [selectedDraft, setSelectedDraft] = useState<SplitTableDraft | null>(null);
  const [checkoutDrafts, setCheckoutDrafts] = useState<CheckoutDraft[]>([]);
  const [selectedCheckoutDraft, setSelectedCheckoutDraft] = useState<CheckoutDraft | null>(null);
  const [tableContext, setTableContext] = useState<any | null>(null);
  // Set when staff clicks "+ Add Items" on a pickup/takeaway order from the Orders page.
  // Drives the green banner at the top of the dashboard and routes checkout through
  // addItemsToOrder + printKitchenAddition instead of creating a new order.
  //
  // CRITICAL: This MUST be cleared the moment the user navigates away from the
  // dashboard — otherwise coming back creates a confusing half-state where the
  // staff thinks they're making a new order but items silently get added to the
  // previous target. See the useEffect cleanup a few lines below.
  const [editOrderContext, setEditOrderContext] = useState<{ id: string; number: string; customer: string; type: string } | null>(null);
  // The items already on the order being edited — shown in the banner so staff
  // sees what's there before adding more. Fetched fresh when edit mode starts.
  const [editOrderExistingItems, setEditOrderExistingItems] = useState<any[]>([]);
  const [editOrderExistingTotal, setEditOrderExistingTotal] = useState<number>(0);
  const [editOrderDetailsOpen, setEditOrderDetailsOpen] = useState(false);
  const [tableActiveOrders, setTableActiveOrders] = useState<any[]>([]); // Existing orders for the active table
  const [tableCustomerName, setTableCustomerName] = useState<string>(''); // Customer name from table's existing orders
  const [tableOrdersRefreshCount, setTableOrdersRefreshCount] = useState(0); // Trigger to reload table orders
  const [tableDetailsExpanded, setTableDetailsExpanded] = useState(false); // Collapsed by default — prevents accidental item additions to open tables
  const [occupiedTables, setOccupiedTables] = useState<any[]>([]); // All occupied tables for quick selection
  // Discount state
  const [discountDialogOpen, setDiscountDialogOpen] = useState(false);
  const [discountType, setDiscountType] = useState<'percentage' | 'fixed'>('percentage');
  const [discountValue, setDiscountValue] = useState('');
  const [discountReason, setDiscountReason] = useState('');
  const [discountOrderId, setDiscountOrderId] = useState('');

  // Void-item state (client request 2026-04-29: cashier may remove a
  // mistakenly-added item from an active order). The dialog requires an
  // explicit confirmation + a free-text reason that is logged to the audit
  // trail. We never hard-delete the item without an audit entry — GoBD.
  const [voidItemTarget, setVoidItemTarget] = useState<{
    orderId: string;
    orderNumber: string;
    itemIndex: number;
    itemLabel: string;
    itemPrice: number;
  } | null>(null);
  const [voidItemReason, setVoidItemReason] = useState('');
  const [voidItemBusy, setVoidItemBusy] = useState(false);

  // Manager PIN dialog state — fraud prevention #2
  const [managerPinOpen, setManagerPinOpen] = useState(false);
  const [managerPinAction, setManagerPinAction] = useState<{
    label: string;
    requireReason?: boolean;
    onApproved: (managerEmail: string, reason: string) => void;
  } | null>(null);

  // PayPal QR Payment state (rendered outside CheckoutDialog to stay visible)
  const [paypalQRDialogOpen, setPaypalQRDialogOpen] = useState(false);
  const [paypalOrderId, setPaypalOrderId] = useState<string | null>(null);
  const [paypalApprovalUrl, setPaypalApprovalUrl] = useState<string | null>(null);
  const [paypalAmount, setPaypalAmount] = useState<number>(0);
  const [paypalOriginalCartItems, setPaypalOriginalCartItems] = useState<any[]>([]); // CRITICAL: Store original cart for confirmation receipt
  const [paypalOrderType, setPaypalOrderType] = useState<'dine_in' | 'takeaway' | 'delivery' | null>(null); // CRITICAL: Store order_type for correct VAT calculation

  // Today's Revenue state (used by Orders page, not displayed on Dashboard)

  // Add item with add-ons modal state
  const [addItemModalOpen, setAddItemModalOpen] = useState(false);
  const [selectedProductForAddOns, setSelectedProductForAddOns] = useState<any>(null);

  // F3/F4 Custom Item state
  const [f3DialogOpen, setF3DialogOpen] = useState(false);
  const [f4DialogOpen, setF4DialogOpen] = useState(false);
  const [customItemName, setCustomItemName] = useState('');
  const [customItemPrice, setCustomItemPrice] = useState('');
  const [f3TargetCartId, setF3TargetCartId] = useState<string | null>(null);
  const customNameInputRef = useRef<HTMLInputElement>(null);

  // Persist cart to localStorage so it survives refresh/back navigation
  useEffect(() => {
    if (cart.length > 0) {
      localStorage.setItem('saheeh_pos_cart', JSON.stringify(cart));
    } else {
      localStorage.removeItem('saheeh_pos_cart');
    }
  }, [cart]);

  // QR Order notification state
  const [qrNotification, setQrNotification] = useState<{open: boolean, message: string}>({open: false, message: ''});
  const qrSeenOrderIds = useRef<Set<string>>(new Set());
  const qrIsInitialLoad = useRef<boolean>(true);

  // BUILD DEBUG: Logs on mount so you can verify the browser has latest code
  useEffect(() => {
    console.log(`%c[POS BUILD] ${POS_BUILD_VERSION}`, 'color: #00ff00; font-size: 16px; font-weight: bold; background: #222; padding: 4px 8px; border-radius: 4px;');
    console.log(`%c[POS PRINT] Active print service: ${POS_BUILD_PRINT}`, 'color: #00ccff; font-size: 14px; font-weight: bold; background: #222; padding: 4px 8px; border-radius: 4px;');
    console.log('[POS DEBUG] If you see "cloudPrintService" above, clear browser cache! Expected: "firestorePrintService"');
  }, []);

  // --- F3/F4 Keyboard Shortcuts ---
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger if typing in an input/textarea or if a dialog is already open
      if (f3DialogOpen || f4DialogOpen || checkoutDialogOpen) return;

      if (e.key === 'F3') {
        e.preventDefault();
        // F3 = Add-on linked to last cart item
        if (cart.length === 0) {
          alert('Bitte zuerst einen Artikel zum Warenkorb hinzufügen.');
          return;
        }
        const lastItem = cart[cart.length - 1];
        setF3TargetCartId(lastItem.cartId);
        setCustomItemName('');
        setCustomItemPrice('');
        setF3DialogOpen(true);
        setTimeout(() => customNameInputRef.current?.focus(), 100);
      } else if (e.key === 'F4') {
        e.preventDefault();
        // F4 = Standalone custom item
        setCustomItemName('');
        setCustomItemPrice('');
        setF4DialogOpen(true);
        setTimeout(() => customNameInputRef.current?.focus(), 100);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [cart, f3DialogOpen, f4DialogOpen, checkoutDialogOpen]);

  // F3: Add add-on to target cart item
  const handleF3Submit = useCallback(() => {
    if (!customItemName.trim() || !f3TargetCartId) return;
    const price = parseFloat(customItemPrice) || 0;
    setCart(prevCart => prevCart.map(item => {
      if (item.cartId === f3TargetCartId) {
        const newAddon = {
          id: `custom_${Date.now()}`,
          name: customItemName.trim(),
          name_de: customItemName.trim(),
          price: price,
          quantity: 1,
        };
        const updatedAddons = [...(item.addons || []), newAddon];
        return {
          ...item,
          price: item.price + price,
          addons: updatedAddons,
        };
      }
      return item;
    }));
    setF3DialogOpen(false);
    setCustomItemName('');
    setCustomItemPrice('');
    setF3TargetCartId(null);
  }, [customItemName, customItemPrice, f3TargetCartId]);

  // F4: Add standalone custom item
  const handleF4Submit = useCallback(() => {
    if (!customItemName.trim()) return;
    const price = parseFloat(customItemPrice) || 0;
    const customItem = {
      id: `custom_${Date.now()}`,
      name: customItemName.trim(),
      base_price: price,
      price_with_tax: price,
      price: price,
      quantity: 1,
      cartId: `custom_${Date.now()}`,
      image_url: '',
      category_name: 'Sonderwunsch',
      category: 'Sonderwunsch',
      addons: [],
      notes: '',
      menu_item_id: `custom_${Date.now()}`,
      is_custom: true,
    };
    setCart(prev => [...prev, customItem]);
    setF4DialogOpen(false);
    setCustomItemName('');
    setCustomItemPrice('');
  }, [customItemName, customItemPrice]);

  // Revenue listener removed — revenue display moved to Orders page only

  // --- QR Order Notification: sound + real-time listener ---
  const playQrOrderAlert = useCallback(() => {
    try {
      const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const now = audioCtx.currentTime;
      const playNote = (time: number, freq: number, duration: number, vol: number, type: OscillatorType = 'sine') => {
        const osc = audioCtx.createOscillator();
        const gain = audioCtx.createGain();
        osc.connect(gain);
        gain.connect(audioCtx.destination);
        osc.frequency.value = freq;
        osc.type = type;
        gain.gain.setValueAtTime(vol, now + time);
        gain.gain.exponentialRampToValueAtTime(0.001, now + time + duration);
        osc.start(now + time);
        osc.stop(now + time + duration);
      };
      playNote(0, 523.25, 0.6, 0.25, 'sine');
      playNote(0, 659.25, 0.6, 0.2, 'sine');
      playNote(0, 783.99, 0.6, 0.15, 'triangle');
      playNote(0.35, 659.25, 0.6, 0.25, 'sine');
      playNote(0.35, 783.99, 0.6, 0.2, 'sine');
      playNote(0.35, 1046.5, 0.6, 0.15, 'triangle');
      playNote(0.7, 1318.5, 0.8, 0.15, 'sine');
      playNote(0.7, 1568.0, 0.8, 0.1, 'triangle');
    } catch (e) {
      console.warn('Audio alert failed:', e);
    }
  }, []);

  useEffect(() => {
    let unsubscribe: (() => void) | null = null;

    const setupQrOrderListener = async () => {
      try {
        const { collection, query, onSnapshot, limit } = await import('../../services/firestoreCompat');

        console.log('%c[QR-ALERT] Setting up QR notification listener on Dashboard...', 'color: #9c27b0; font-weight: bold; font-size: 14px;');

        const { orderBy: fsOrderBy, deleteDoc: fsDeleteDoc, doc: fsDoc } = await import('../../services/firestoreCompat');

        // Only listen to recent notifications (last 10, ordered by creation time)
        const q = query(
          collection(db, 'qr_notifications'),
          fsOrderBy('created_at', 'desc'),
          limit(10)
        );

        unsubscribe = onSnapshot(q, (snapshot) => {
          const currentIds = new Set<string>();
          snapshot.forEach((docSnap) => currentIds.add(docSnap.id));

          console.log(`[QR-ALERT] Dashboard snapshot: ${currentIds.size} notifications, initialLoad=${qrIsInitialLoad.current}`);

          if (qrIsInitialLoad.current) {
            qrSeenOrderIds.current = currentIds;
            qrIsInitialLoad.current = false;
            console.log(`%c[QR-ALERT] ✅ Dashboard listener active! Tracking ${currentIds.size} existing notifications`, 'color: #4caf50; font-weight: bold;');

            // Clean up old notifications (older than 1 hour) to prevent stale data
            snapshot.forEach((docSnap) => {
              const data = docSnap.data();
              const createdAt = data.created_at?.toDate?.() || (data.created_at?.seconds ? new Date(data.created_at.seconds * 1000) : null);
              if (createdAt && Date.now() - createdAt.getTime() > 60 * 60 * 1000) {
                fsDeleteDoc(fsDoc(db, 'qr_notifications', docSnap.id)).catch(() => {});
              }
            });
            return;
          }

          currentIds.forEach((id) => {
            if (!qrSeenOrderIds.current.has(id)) {
              const docSnap = snapshot.docs.find((d) => d.id === id);
              const data = docSnap?.data();
              const tableNumber = data?.table_number || '?';
              const orderNumber = data?.order_number || id.slice(-6);
              const total = data?.total != null ? Number(data.total).toFixed(2) : '0.00';
              const merged = data?.merged ? ' (hinzugefügt)' : '';

              console.log(`%c[QR-ALERT] 🔔 NEW QR ORDER! Table ${tableNumber} — #${orderNumber} (€${total})${merged}`, 'color: #ff5722; font-weight: bold; font-size: 16px;');
              playQrOrderAlert();
              setQrNotification({
                open: true,
                message: `Tisch ${tableNumber} — #${orderNumber} (€${total})${merged}`,
              });

              // Delete processed notification after 5 seconds to prevent reprocessing
              setTimeout(() => {
                fsDeleteDoc(fsDoc(db, 'qr_notifications', id)).catch(() => {});
              }, 5000);
            }
          });

          qrSeenOrderIds.current = currentIds;
        }, (error) => {
          console.error('%c[QR-ALERT] ❌ Dashboard listener error:', 'color: red; font-weight: bold;', error);
        });
      } catch (error) {
        console.error('%c[QR-ALERT] ❌ Dashboard setup failed:', 'color: red; font-weight: bold;', error);
      }
    };

    setupQrOrderListener();

    return () => {
      if (unsubscribe) {
        unsubscribe();
        console.log('[QR-ORDER] Listener removed');
      }
    };
  }, [playQrOrderAlert]);

  // ---------------------------------------------------------------
  // Auto-print listener for Takeaway/Delivery orders from ANY source.
  //
  // Dine-in orders already auto-print a Zwischenrechnung via the checkout
  // flow. Takeaway/Delivery orders created from the POS also print via the
  // same flow. But orders arriving from EXTERNAL sources (webshop /
  // takeaway.com / QR app) bypass the POS checkout and were never printing
  // the cashier BELEG + kitchen tickets. This listener closes that gap.
  //
  // Safe for POS-originated orders too: firestorePrintService uses a
  // dedup_key of `${orderId}_${type}_${printerIp}`, so a second call for
  // the same order is short-circuited to "duplicate prevented".
  // ---------------------------------------------------------------
  useEffect(() => {
    let unsub: (() => void) | null = null;
    const seenPrinted = new Set<string>();

    const setupAutoPrintListener = async () => {
      try {
        const {
          collection, query, where, onSnapshot, orderBy, limit,
          updateDoc, doc: fsDoc,
        } = await import('../../services/firestoreCompat');

        // Pull recent takeaway/delivery orders so we can detect new ones live.
        const q = query(
          collection(db, 'orders'),
          where('order_type', 'in', ['takeaway', 'delivery']),
          orderBy('created_at', 'desc'),
          limit(50),
        );

        let isInitialLoad = true;

        unsub = onSnapshot(q, (snap) => {
          snap.docChanges().forEach((change) => {
            if (change.type !== 'added' && change.type !== 'modified') return;

            const id = change.doc.id;
            const data = change.doc.data() as any;

            if (seenPrinted.has(id)) return;

            // Skip on the initial snapshot — we only auto-print NEW orders.
            if (isInitialLoad) {
              if (data.printed_auto) seenPrinted.add(id);
              return;
            }

            // Skip if already auto-printed (flag set by POS flow or this listener).
            if (data.printed_auto === true) { seenPrinted.add(id); return; }
            // Skip cancelled orders.
            if (data.status === 'cancelled') { seenPrinted.add(id); return; }

            // Guard against replaying ancient orders — only print if created
            // in the last 15 minutes. Older rows shouldn't fire on reconnect.
            let createdAt: Date;
            if (data.created_at?.toDate) createdAt = data.created_at.toDate();
            else if (data.created_at?.seconds) createdAt = new Date(data.created_at.seconds * 1000);
            else if (typeof data.created_at === 'string') createdAt = new Date(data.created_at);
            else createdAt = new Date();
            if (Date.now() - createdAt.getTime() > 15 * 60 * 1000) {
              seenPrinted.add(id);
              return;
            }

            seenPrinted.add(id);

            // Build receipt data shape matching the POS checkout path.
            const orderType = data.order_type as 'takeaway' | 'delivery';
            const fallbackName = orderType === 'takeaway' ? 'Abholung' : 'Lieferung';
            const customerName =
              (data.customer_name && data.customer_name.trim())
              || fallbackName;

            const items = (data.items || []).map((item: any, idx: number) => {
              const qty = item.quantity || 1;
              const unit = item.unit_price ?? item.price ?? (item.total && qty ? item.total / qty : 0);
              const addonsTotal = (item.addons || []).reduce(
                (s: number, a: any) => s + ((a.price || 0) * (a.quantity || 1)),
                0,
              );
              return {
                name: item.name || item.item_name || item.menu_item_name || 'Artikel',
                quantity: qty,
                price: unit,
                base_price: Math.max(0, unit - addonsTotal),
                total: unit * qty,
                category_name: item.category_name || item.category || '',
                addons: item.addons || [],
                notes: item.notes || '',
                itemNumber: idx + 1,
              };
            });

            // Delivery tour number (last 2 digits of order number) — matches
            // the existing POS flow so kitchen tickets stay consistent.
            let tripNumber: string | undefined;
            if (orderType === 'delivery') {
              const numStr = String(data.order_number || '');
              if (numStr.length >= 2) tripNumber = numStr.slice(-2);
              else if (numStr.length === 1) tripNumber = '0' + numStr;
            }

            const receiptData: any = {
              orderNumber: data.order_number,
              customerName,
              customerAddress: data.delivery_address
                || [data.customer_address, data.customer_postcode, data.customer_city].filter(Boolean).join(', ')
                || '',
              customerPhone: data.customer_phone || '',
              orderDate: createdAt.toLocaleDateString('en-US'),
              orderTime: createdAt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
              items,
              subtotal: data.subtotal || 0,
              tax: data.tax || 0,
              total: data.total || 0,
              delivery_fee: data.delivery_fee || 0,
              paymentMethod: data.payment_method || 'cash',
              paymentStatus: data.payment_status || 'completed',
              order_type: orderType,
              portal: data.source === 'webshop' || data.source === 'qr_order'
                ? (data.source === 'qr_order' ? 'qr' : 'takeaway.com')
                : (orderType === 'takeaway' ? 'takeaway.com' : undefined),
              tableNumber: null,
              isCopy: false,
              ...(tripNumber ? { trip_number: tripNumber } : {}),
              ...(data.discount_amount ? {
                discount_type: data.discount_type,
                discount_value: data.discount_value,
                discount_amount: data.discount_amount,
                discount_reason: data.discount_reason,
                total_before_discount: data.total_before_discount,
              } : {}),
            };

            console.log(
              `%c[AUTO PRINT] New ${orderType} order #${data.order_number} (source=${data.source || 'unknown'}) — printing cashier + kitchen`,
              'color: #00bcd4; font-weight: bold;',
            );

            firestorePrintService
              .printOrder(id, String(data.order_number), receiptData)
              .then(({ receipt, kitchen }) => {
                receipt.forEach(r => console.log('[AUTO PRINT] Cashier:', r.jobId, r.success ? 'ok' : r.message));
                kitchen.forEach(r => console.log('[AUTO PRINT] Kitchen:', r.jobId, r.success ? 'ok' : r.message));
                // Mark as printed so the listener never retries on this order.
                updateDoc(fsDoc(db, 'orders', id), {
                  printed_auto: true,
                  printed_auto_at: new Date(),
                }).catch((e) => console.warn('[AUTO PRINT] Could not update printed_auto flag:', e));
              })
              .catch((e) => {
                console.error('[AUTO PRINT] Print failed for order', data.order_number, e);
                // Allow retry on next snapshot change
                seenPrinted.delete(id);
              });
          });

          if (isInitialLoad) isInitialLoad = false;
        }, (error) => {
          console.error('[AUTO PRINT] Listener error:', error);
        });
      } catch (error) {
        console.error('[AUTO PRINT] Setup failed:', error);
      }
    };

    setupAutoPrintListener();

    return () => {
      if (unsub) {
        unsub();
        console.log('[AUTO PRINT] Listener removed');
      }
    };
  }, []);

  // Debug: Log PayPal state changes
  useEffect(() => {
    console.log('🔍 [DASHBOARD] PayPalQRPayment state changed:', {
      paypalQRDialogOpen,
      hasApprovalUrl: !!paypalApprovalUrl,
      hasOrderId: !!paypalOrderId,
      open: paypalQRDialogOpen && !!paypalApprovalUrl && !!paypalOrderId,
      orderId: paypalOrderId,
    });
  }, [paypalQRDialogOpen, paypalApprovalUrl, paypalOrderId]);

  const draftService = SplitTableDraftService.getInstance();
  const checkoutDraftService = CheckoutDraftService.getInstance();
  const { language } = useLanguage();

  // Dragging state for draft banner
  const [isDraggingBanner, setIsDraggingBanner] = useState(false);
  const [bannerDragOffset, setBannerDragOffset] = useState({ x: 0, y: 0 });
  const [bannerPosition, setBannerPosition] = useState({ x: 0, y: 80 });

  // Helper function to get emoji icon for category
  const getCategoryIcon = (categoryName: string): string => {
    const name = categoryName.toLowerCase().trim();
    if (name.includes('roll') || name.includes('sushi roll')) return '🍽️';
    if (name.includes('nigiri')) return '🍙';
    if (name.includes('sashimi')) return '🐟';
    if (name.includes('beverage') || name.includes('drink')) return '🍵';
    if (name.includes('dessert')) return '🍰';
    if (name.includes('salad')) return '🥗';
    if (name.includes('appetizer') || name.includes('starter')) return '🥢';
    if (name.includes('pizza')) return '🍕';
    if (name.includes('soup')) return '🍲';
    if (name.includes('rice') || name.includes('bowl')) return '🍚';
    return '🍱';
  };

  // Fetch draft orders (split table drafts)
  useEffect(() => {
    const loadDraftOrders = async () => {
      try {
        console.log('🔄 [DASHBOARD] Loading draft split orders...');
        const drafts = await draftService.getActiveDrafts();
        console.log('📋 [DASHBOARD] Loaded', drafts.length, 'draft split orders:', drafts);
        setDraftOrders(drafts);

        // Log each draft for debugging
        drafts.forEach((draft, index) => {
          console.log(`📝 [SPLIT DRAFT ${index + 1}]`, {
            id: draft.id,
            table_number: draft.table_number,
            status: draft.status,
            active_step: draft.active_step,
            people_count: draft.people.length,
            total: draft.original_order_total
          });
        });
      } catch (error) {
        console.error('❌ [DASHBOARD] Error loading draft split orders:', error);
      }
    };

    loadDraftOrders();

    // Subscribe to real-time updates
    const unsubscribe = draftService.subscribeToActiveDrafts((updatedDrafts) => {
      console.log('🔄 [DASHBOARD] Draft split orders updated:', updatedDrafts.length, 'drafts');
      setDraftOrders(updatedDrafts);
    });

    return () => unsubscribe();
  }, [draftService]);

  // Fetch checkout drafts
  useEffect(() => {
    const loadCheckoutDrafts = async () => {
      try {
        console.log('🔄 [DASHBOARD] Loading checkout drafts...');
        const drafts = await checkoutDraftService.getActiveDrafts();
        console.log('📋 [DASHBOARD] Loaded', drafts.length, 'checkout drafts:', drafts);
        setCheckoutDrafts(drafts);
      } catch (error) {
        console.error('❌ [DASHBOARD] Error loading checkout drafts:', error);
      }
    };

    loadCheckoutDrafts();

    // Subscribe to real-time updates
    const unsubscribe = checkoutDraftService.subscribeToActiveDrafts((updatedDrafts) => {
      console.log('🔄 [DASHBOARD] Checkout drafts updated:', updatedDrafts.length, 'drafts');
      setCheckoutDrafts(updatedDrafts);
    });

    return () => unsubscribe();
  }, [checkoutDraftService]);

  // Read table context when dashboard loads or is navigated to
  // ONLY opens a table if user explicitly clicked "Add Items" from Restaurant page
  // (which sets 'selectedTable' in localStorage). Normal navigation always starts fresh.
  useEffect(() => {
    const loadTableContext = async () => {
      console.log('🪑 [DASHBOARD] loadTableContext called, location.key:', location.key);

      // "+ Add Items" from the Orders page (pickup/takeaway/delivery).
      // If this flag is set we enter "add mode" — cart starts empty, banner shows,
      // checkout routes through addItemsToOrder.
      try {
        const editId = localStorage.getItem('saheeh_edit_order_id');
        const editNum = localStorage.getItem('saheeh_edit_order_number');
        const editCust = localStorage.getItem('saheeh_edit_order_customer');
        const editType = localStorage.getItem('saheeh_edit_order_type');
        if (editId) {
          setEditOrderContext({ id: editId, number: editNum || '', customer: editCust || '', type: editType || 'takeaway' });
          setTableContext(null);
          setCart([]);
          console.log('🟢 [DASHBOARD] Enter ADD-ITEMS mode for order', editNum);
          // Load existing items so staff can see what's already on the order
          (async () => {
            try {
              const { doc: dRef, getDoc: gd } = await import('../../services/firestoreCompat');
              const { db: dbRef } = await import('../../services/firebaseConfig');
              const snap = await gd(dRef(dbRef, 'orders', editId));
              if (snap.exists()) {
                const data = snap.data();
                setEditOrderExistingItems(data.items || []);
                setEditOrderExistingTotal(data.total || 0);
              }
            } catch (e) {
              console.warn('[DASHBOARD] Could not preload existing order items:', e);
            }
          })();
          return;
        }
        setEditOrderExistingItems([]);
        setEditOrderExistingTotal(0);
      } catch { /* ignore */ }

      setEditOrderContext(null);

      // Check localStorage — ONLY source: set by Restaurant page "Add Items" button
      try {
        const localContext = localStorage.getItem('selectedTable');
        if (localContext) {
          const parsed = JSON.parse(localContext);
          if (parsed && parsed.number) {
            console.log('✅ [DASHBOARD] Table context from Restaurant page click:', parsed);
            setTableContext(parsed);
            setTableDetailsExpanded(true); // User explicitly wants to add items — expand
            localStorage.removeItem('selectedTable');
            return;
          }
        }
      } catch { /* ignore */ }

      // No explicit table selection — clear any previous table context
      // User sees "Active Tables" chips and empty cart — must click a table to enter it
      console.log('📋 [DASHBOARD] No table selected — showing clean dashboard');
      setTableContext(null);
      setTableActiveOrders([]);
      setCart([]);

      // Clean up Firestore context doc
      try {
        const { doc: fsDoc, deleteDoc: fsDeleteDoc } = await import('../../services/firestoreCompat');
        const { db: fsDb } = await import('../../services/firebaseConfig');
        await fsDeleteDoc(fsDoc(fsDb, 'table_context', 'current'));
      } catch { /* ignore */ }
    };

    loadTableContext();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]); // Re-run when navigating to this page

  // Auto-exit "+ Add Items" edit mode when staff navigates away from the dashboard.
  // Reason: leaving mid-add and coming back was creating confusing half-state —
  // the banner would still be there, but the flow context (cart, totals) was lost.
  // Cleanup on unmount wipes localStorage + React state so the next dashboard
  // entry is always a clean slate. Staff must re-click "+ Add Items" from the
  // Orders page to enter edit mode again.
  useEffect(() => {
    return () => {
      try {
        localStorage.removeItem('saheeh_edit_order_id');
        localStorage.removeItem('saheeh_edit_order_number');
        localStorage.removeItem('saheeh_edit_order_customer');
        localStorage.removeItem('saheeh_edit_order_type');
      } catch { /* ignore */ }
    };
  }, []);

  // Load all occupied tables for quick selection (shows when no table is selected)
  useEffect(() => {
    const loadOccupiedTables = async () => {
      try {
        const tables = await firebaseTableService.getTables();
        const occupied = tables.filter(t => t.status === 'occupied');
        setOccupiedTables(occupied);
      } catch (e) {
        console.warn('Could not load occupied tables:', e);
      }
    };
    loadOccupiedTables();
    // Also set up real-time listener
    let contextSetTime = Date.now();
    const unsub = firebaseTableService.onTablesChange((tables) => {
      setOccupiedTables(tables.filter(t => t.status === 'occupied'));
      // Auto-clear tableContext if the selected table is no longer occupied in Firestore
      // BUT: skip if context was set recently (< 5 seconds) to avoid race conditions
      setTableContext((prev: any) => {
        if (!prev) return prev;
        // Grace period: don't auto-clear within 5 seconds of context being set
        if (Date.now() - contextSetTime < 5000) return prev;
        const live = tables.find(t => t.number === prev.number && (!prev.area || t.area === prev.area));
        if (live && live.status !== 'occupied') {
          console.log(`⚠️ [DASHBOARD] Table ${prev.number} is now ${live.status} — auto-clearing table context`);
          import('../../services/firestoreCompat').then(({ doc, deleteDoc }) => {
            deleteDoc(doc(null, 'table_context', 'current')).catch(() => {});
          });
          return null;
        }
        return prev;
      });
    });
    // Update contextSetTime when tableContext changes
    const updateContextTime = () => { contextSetTime = Date.now(); };
    // Listen for context changes via a custom event
    window.addEventListener('tableContextSet', updateContextTime);
    return () => {
      unsub();
      window.removeEventListener('tableContextSet', updateContextTime);
    };
  }, []);

  // When tableContext is loaded, fetch existing active orders for this table
  useEffect(() => {
    if (!tableContext || !tableContext.number) {
      console.log('⏸️ [TABLE ORDERS] Skipping — no tableContext or no number', { hasContext: !!tableContext, number: tableContext?.number });
      return;
    }

    const loadTableOrders = async () => {
      try {
        console.log('🪑 [TABLE ORDERS] Loading existing orders for table', tableContext.number, '(refreshCount:', tableOrdersRefreshCount, ')');
        const { collection, query, where, getDocs, doc: fsDocRef, getDoc: fsGetDoc, limit: fsLimit } = await import('../../services/firestoreCompat');
        const { db } = await import('../../services/firebaseConfig');

        const tableNum = tableContext.number;
        const ordersRef = collection(db, 'orders');

        // PRIORITY: Fetch live table from Firestore to get current active_order_id
        let liveActiveOrderId = tableContext.active_order_id || '';
        let liveActiveOrderNumber = tableContext.active_order_number || '';
        let liveTabOrders: string[] = [];
        let liveStartTime: Date | null = null;
        try {
          const tablesSnap = await getDocs(query(collection(db, 'restaurant_tables'), where('number', '==', tableNum)));
          tablesSnap.forEach(td => {
            const tData = td.data();
            console.log(`🔍 [TABLE DEBUG] Table ${tableNum} Firestore doc:`, {
              active_order_id: tData.active_order_id,
              active_order_number: tData.active_order_number,
              tab_orders: tData.tab_orders,
              status: tData.status,
            });
            if (tData.active_order_id) {
              liveActiveOrderId = tData.active_order_id;
              console.log(`✅ [TABLE DEBUG] Using active_order_id: ${liveActiveOrderId}`);
            }
            if (tData.active_order_number) liveActiveOrderNumber = String(tData.active_order_number);
            if (tData.tab_orders) liveTabOrders = tData.tab_orders;
            const raw = tData.start_time;
            if (raw?.toDate) liveStartTime = raw.toDate();
            else if (raw?.seconds) liveStartTime = new Date(raw.seconds * 1000);
            else if (typeof raw === 'string') liveStartTime = new Date(raw);
          });
        } catch (_) { /* fall through */ }
        // Safety: 12h cutoff if no start_time
        const sessionCutoff = liveStartTime || new Date(Date.now() - 12 * 60 * 60 * 1000);

        // Helper: check if an order belongs to the current session
        // Positive allow-list — see memory/pos-order-status-filter-rule.md.
        // Prevents ghost orders (status='paid', 'archived', '', etc.) from
        // being treated as "active" on a newly-seated table.
        const TABLE_ACTIVE_STATUSES = ['pending', 'confirmed', 'preparing', 'ready', 'assigned'];
        const isCurrentSession = (d: any) => {
          const s = (d.status || '').toLowerCase();
          if (!TABLE_ACTIVE_STATUSES.includes(s)) return false;
          if (d.is_split_payment) return false;
          let orderDate: Date | null = null;
          if (d.created_at?.toDate) orderDate = d.created_at.toDate();
          else if (d.created_at?.seconds) orderDate = new Date(d.created_at.seconds * 1000);
          else if (typeof d.created_at === 'string') orderDate = new Date(d.created_at);
          if (orderDate && sessionCutoff && orderDate < sessionCutoff) return false;
          return true;
        };

        // Try active_order_id first (direct doc lookup — fastest)
        // CRITICAL: If the table doc points to this order via active_order_id,
        // it IS the current order — only reject if completed/cancelled (skip session time filter)
        if (liveActiveOrderId) {
          try {
            const directSnap = await fsGetDoc(fsDocRef(db, 'orders', liveActiveOrderId));
            if (directSnap.exists()) {
              const d = directSnap.data();
              const s = (d.status || '').toLowerCase();
              // Positive allow-list match — even for a directly-linked order we
              // must not treat a ghost status as active.
              const isActiveStatus = TABLE_ACTIVE_STATUSES.includes(s);
              console.log(`🔍 [TABLE DEBUG] active_order_id ${liveActiveOrderId}: status=${s}, order_number=${d.order_number}, items=${(d.items||[]).length}, isActive=${isActiveStatus}`);
              if (isActiveStatus && !d.is_split_payment) {
                console.log(`✅ [TABLE ORDERS] Found active order via active_order_id (direct lookup, no session filter)`);
                setTableActiveOrders([{ id: directSnap.id, ...d }]);
                if (d.customer_name && d.customer_name !== 'Laufkunde') setTableCustomerName(d.customer_name);
                return;
              }
            }
          } catch (_) { /* fall through to query */ }
        }

        // Fallback: query by active_order_number when active_order_id is missing
        if (!liveActiveOrderId && liveActiveOrderNumber) {
          const liveOrderNumber = liveActiveOrderNumber;
          console.log(`🔍 [TABLE ORDERS] Fallback: querying by order_number=${liveOrderNumber}`);
          try {
            for (const numForm of [liveOrderNumber, parseInt(liveOrderNumber, 10)]) {
              const snap = await getDocs(query(ordersRef, where('order_number', '==', numForm), fsLimit(3)));
              for (const d of snap.docs) {
                const data = d.data();
                console.log(`🔍 [TABLE ORDERS] order_number match: ${d.id}, status=${data.status}, items=${(data.items||[]).length}`);
                if (isCurrentSession(data)) {
                  console.log(`✅ [TABLE ORDERS] Found active order via order_number fallback`);
                  setTableActiveOrders([{ id: d.id, ...data }]);
                  if (data.customer_name && data.customer_name !== 'Laufkunde') setTableCustomerName(data.customer_name);
                  return;
                }
              }
            }
          } catch (e) { console.warn('⚠️ order_number fallback failed:', e); }
        }

        if (liveTabOrders.length > 0) {
          console.log(`🔍 [TABLE ORDERS] Trying tab_orders path: ${liveTabOrders.length} order IDs`, liveTabOrders);
          try {
            const tabOrderDocs = await Promise.all(
              liveTabOrders.map(async (oid: string) => {
                try {
                  const snap = await fsGetDoc(fsDocRef(db, 'orders', oid));
                  if (snap.exists()) {
                    const d = snap.data();
                    console.log(`🔍 [TABLE ORDERS] tab_order ${oid}: status=${d.status}, items=${(d.items||[]).length}, isCurrentSession=${isCurrentSession(d)}`);
                    return { id: snap.id, ...d };
                  } else {
                    console.log(`⚠️ [TABLE ORDERS] tab_order ${oid}: document not found`);
                  }
                } catch (e) { console.warn(`⚠️ [TABLE ORDERS] tab_order ${oid} fetch error:`, e); }
                return null;
              })
            );
            // tab_orders are explicit IDs on the table doc — only reject completed/cancelled (no session time filter)
            const validOrders = tabOrderDocs.filter((o: any) => {
              if (!o) return false;
              const st = (o.status || '').toLowerCase();
              return st !== 'completed' && st !== 'cancelled' && st !== 'delivered' && !o.is_split_payment;
            });
            console.log(`🔍 [TABLE ORDERS] tab_orders: ${tabOrderDocs.filter(Boolean).length} found, ${validOrders.length} active (status filter only)`);
            if (validOrders.length > 0) {
              validOrders.sort((a: any, b: any) => {
                const da = a.created_at?.seconds || 0;
                const db2 = b.created_at?.seconds || 0;
                return db2 - da;
              });
              console.log(`✅ [TABLE ORDERS] Found ${validOrders.length} active orders from tab_orders (session-filtered)`);
              setTableActiveOrders(validOrders as any[]);
              const cn = (validOrders[0] as any)?.customer_name;
              if (cn && cn !== 'Laufkunde') setTableCustomerName(cn);
              return;
            }
          } catch (_) { /* fall through */ }
        }

        // Query for active orders on this table
        // NOTE: No orderBy - sort client-side to avoid needing composite index
        // Try both string and number types for table_number
        const tableNumStr = String(tableNum);
        const tableNumInt = typeof tableNum === 'number' ? tableNum : parseInt(tableNumStr, 10);

        let snapshot;
        try {
          // Try 1: with original type + order_type filter (limited to 20 most recent orders per table)
          const q1 = query(
            ordersRef,
            where('table_number', '==', tableNum),
            where('order_type', '==', 'dine_in'),
            fsLimit(20)
          );
          snapshot = await getDocs(q1);

          // Try 2: opposite type (string vs number)
          if (snapshot.empty) {
            const altTableNum = typeof tableNum === 'number' ? tableNumStr : tableNumInt;
            const q2 = query(
              ordersRef,
              where('table_number', '==', altTableNum),
              where('order_type', '==', 'dine_in'),
              fsLimit(20)
            );
            snapshot = await getDocs(q2);
          }

          // Try 3: just table_number without order_type filter
          if (snapshot.empty) {
            const q3 = query(ordersRef, where('table_number', '==', tableNum), fsLimit(20));
            snapshot = await getDocs(q3);
            if (snapshot.empty) {
              const q4 = query(ordersRef, where('table_number', '==', typeof tableNum === 'number' ? tableNumStr : tableNumInt), fsLimit(20));
              snapshot = await getDocs(q4);
            }
          }
        } catch (queryError: any) {
          console.error('❌ [TABLE ORDERS] Query error:', queryError.code, queryError.message);
          try {
            const qFallback = query(ordersRef, where('table_number', '==', tableNum), fsLimit(20));
            snapshot = await getDocs(qFallback);
            if (snapshot!.empty) {
              const qFallback2 = query(ordersRef, where('table_number', '==', typeof tableNum === 'number' ? tableNumStr : tableNumInt), fsLimit(20));
              snapshot = await getDocs(qFallback2);
            }
          } catch (fbError) {
            console.error('❌ [TABLE ORDERS] Fallback query also failed:', fbError);
            snapshot = null;
          }
        }

        console.log(`🪑 [TABLE ORDERS] Query returned ${snapshot?.size || 0} docs for table ${tableNum} (type: ${typeof tableNum})`);

        const activeOrders: any[] = [];
        let customerName = '';

        // Determine if the current user is an admin — admins see all active orders regardless of age
        const HARDCODED_ADMINS_DASH = ['admin@saheehpos.com', 'admin@saheehpos.com'];
        let dashAdminEmail = '';
        try {
          const { getAuth: getDashAuth } = await import('firebase/auth');
          dashAdminEmail = getDashAuth().currentUser?.email?.toLowerCase() || '';
        } catch { }
        if (!dashAdminEmail) {
          try {
            const raw = localStorage.getItem('saheeh_pos_user');
            if (raw) dashAdminEmail = (JSON.parse(raw).email || '').toLowerCase();
          } catch { }
        }
        const isDashAdmin = HARDCODED_ADMINS_DASH.includes(dashAdminEmail)
          || dashAdminEmail.includes('admin') || dashAdminEmail.includes('manager');

        const completedOrdersForAdmin: any[] = [];

        let debugCount = 0;
        snapshot?.forEach(doc => {
          const data = doc.data();
          const status = (data.status || '').toLowerCase();

          // Debug: log first 5 orders to understand why they're filtered
          if (debugCount < 5) {
            const s = (data.status || '').toLowerCase();
            let orderDate: Date | null = null;
            if (data.created_at?.toDate) orderDate = data.created_at.toDate();
            else if (data.created_at?.seconds) orderDate = new Date(data.created_at.seconds * 1000);
            else if (typeof data.created_at === 'string') orderDate = new Date(data.created_at);
            console.log(`🔍 [ORDER FILTER] Order ${data.order_number || doc.id}: status=${s}, isSplit=${!!data.is_split_payment}, created=${orderDate?.toISOString()}, sessionCutoff=${sessionCutoff?.toISOString()}, isAfterCutoff=${orderDate && sessionCutoff ? orderDate >= sessionCutoff : 'N/A'}, pass=${isCurrentSession(data)}`);
            debugCount++;
          }

          if (isCurrentSession(data)) {
              activeOrders.push({ id: doc.id, ...data });
              if (data.customer_name && data.customer_name !== 'Laufkunde' && !customerName) {
                customerName = data.customer_name;
              }
          } else if (isDashAdmin && status === 'completed') {
            // For admin: also collect completed orders so they can see the table history
            // and use Release Table if no active orders remain
            completedOrdersForAdmin.push({ id: doc.id, ...data, _isCompleted: true });
          }
        });

        // For admin: if no active orders found but completed ones exist from CURRENT session,
        // include the most recent completed order (marked) so the banner can show a "Release Table" button
        if (isDashAdmin && activeOrders.length === 0 && completedOrdersForAdmin.length > 0) {
          // Filter completed orders by session time too
          const sessionCompleted = completedOrdersForAdmin.filter(o => {
            let od: Date | null = null;
            if (o.created_at?.toDate) od = o.created_at.toDate();
            else if (o.created_at?.seconds) od = new Date(o.created_at.seconds * 1000);
            else if (typeof o.created_at === 'string') od = new Date(o.created_at);
            return !od || !sessionCutoff || od >= sessionCutoff;
          });
          if (sessionCompleted.length > 0) {
            sessionCompleted.sort((a, b) => {
              const dateA = a.created_at?.toDate?.() || (a.created_at?.seconds ? new Date(a.created_at.seconds * 1000) : new Date(0));
              const dateB = b.created_at?.toDate?.() || (b.created_at?.seconds ? new Date(b.created_at.seconds * 1000) : new Date(0));
              return dateB.getTime() - dateA.getTime();
            });
            activeOrders.push(sessionCompleted[0]);
            if (sessionCompleted[0].customer_name && sessionCompleted[0].customer_name !== 'Laufkunde') {
              customerName = sessionCompleted[0].customer_name;
            }
          }
        }

        // Sort client-side: newest first
        activeOrders.sort((a, b) => {
          const dateA = a.created_at?.toDate?.() || (a.created_at?.seconds ? new Date(a.created_at.seconds * 1000) : new Date(0));
          const dateB = b.created_at?.toDate?.() || (b.created_at?.seconds ? new Date(b.created_at.seconds * 1000) : new Date(0));
          return dateB.getTime() - dateA.getTime();
        });

        // LAST RESORT: if still 0 active orders but table is occupied with an order number,
        // query by order_number ignoring session filter (the order exists, just find it)
        if (activeOrders.length === 0 && liveActiveOrderNumber) {
          console.log(`🔄 [TABLE ORDERS] Last resort: querying by order_number=${liveActiveOrderNumber} without session filter`);
          try {
            for (const numForm of [liveActiveOrderNumber, parseInt(liveActiveOrderNumber, 10)]) {
              if (activeOrders.length > 0) break;
              const snap = await getDocs(query(ordersRef, where('order_number', '==', numForm), fsLimit(3)));
              for (const d of snap.docs) {
                const data = d.data();
                const s = (data.status || '').toLowerCase();
                if (s !== 'cancelled') { // Accept any non-cancelled order
                  console.log(`✅ [TABLE ORDERS] Last resort found order: ${d.id}, status=${s}, items=${(data.items||[]).length}`);
                  activeOrders.push({ id: d.id, ...data });
                  if (data.customer_name && data.customer_name !== 'Laufkunde') customerName = data.customer_name;
                  break;
                }
              }
            }
          } catch (e) { console.warn('⚠️ Last resort query failed:', e); }
        }

        console.log(`✅ [TABLE ORDERS] Found ${activeOrders.length} active orders for table ${tableNum}`);
        setTableActiveOrders(activeOrders);
        if (customerName) {
          setTableCustomerName(customerName);
        }

        // If no active orders remain OR all orders are completed/cancelled, consider auto-clearing
        // NEVER auto-clear for admin users — they may be viewing another user's table
        const hasNonCompletedOrder = activeOrders.some(o => {
          const s = (o.status || '').toLowerCase();
          return s !== 'completed' && s !== 'cancelled';
        });

        if (!isDashAdmin && (activeOrders.length === 0 || !hasNonCompletedOrder)) {
          // DON'T auto-clear if the table context was just set (user just clicked "New Order"
          // from Restaurant page and hasn't created an order yet)
          const contextAge = tableContext?.start_time
            ? Date.now() - new Date(tableContext.start_time).getTime()
            : Infinity;
          const isRecentContext = contextAge < 5 * 60 * 1000; // 5 minutes

          if (isRecentContext) {
            console.log('ℹ️ [TABLE ORDERS] No active orders yet, but table context is fresh (< 5 min) — keeping context for new order');
            // Clear any stale customer name from a previous session on this table
            setTableActiveOrders([]);
            setTableCustomerName('');
          } else {
            console.log('⚠️ [TABLE ORDERS] All orders completed/cancelled and context is old — auto-clearing');
            setTableContext(null);
            setTableActiveOrders([]);
            setTableCustomerName('');
            try {
              const { doc: docRef, deleteDoc } = await import('../../services/firestoreCompat');
              const { db: fireDb } = await import('../../services/firebaseConfig');
              await deleteDoc(docRef(fireDb, 'table_context', 'current'));
            } catch (e) { console.warn('Could not clear table context:', e); }
          }
          return;
        }
      } catch (error) {
        console.error('❌ [TABLE ORDERS] Error loading table orders:', error);
        setTableActiveOrders([]);
      }
    };

    loadTableOrders();
  }, [tableContext, tableOrdersRefreshCount]);

  // Fetch real menu items from Firebase and generate categories dynamically
  // Also subscribe to real-time updates
  useEffect(() => {
    let unsubscribeMenuItems: (() => void) | null = null;
    let currentCategories: any[] = [];

    const processMenuItems = (items: any[], categories: any[]) => {
      console.log(`📦 [DASHBOARD] Processing ${items?.length || 0} items and ${categories?.length || 0} categories`);

      if ((items?.length || 0) === 0) {
        console.warn('⚠️ [FIREBASE] No items found. Please upload menu to Firebase!');
        setProducts([]);
        setAvailableCategories([{ id: 'all', name: 'All', icon: '🍱' }]);
        setIsLoading(false);
        return;
      }

      const mapped = items
        .filter(item => item.is_available !== false) // Only show available items
        .map(item => {
          // Apply price overrides for local testing
          const overriddenItem = applyPriceOverride(item);
          return {
            id: overriddenItem.id,
            name: overriddenItem.name,
            category_name: overriddenItem.category?.trim() || 'Other',
            base_price: overriddenItem.base_price,
            price_with_tax: overriddenItem.price_with_tax,
            description: overriddenItem.description || overriddenItem.description_de || 'Fresh and delicious',
            name_de: overriddenItem.name_de,
            description_de: overriddenItem.description_de,
            tax_rate: overriddenItem.tax_rate,
            is_available: overriddenItem.is_available !== false,
            is_popular: overriddenItem.is_popular,
            sold_out: (overriddenItem as any).sold_out === true,
            display_order: typeof (overriddenItem as any).display_order === 'number' ? (overriddenItem as any).display_order : 999999,
            // Per-brand sort override (set from admin). POS prefers Saheeh order
            // (main brand) when present; falls back to `display_order` below.
            display_order_by_brand: (overriddenItem as any).display_order_by_brand || undefined,
            image_url: overriddenItem.image_url || '',
            // Carry structured modifier group references through so the Add-ons modal
            // can load Size / Sauce / Toppings groups (Domino's-style UX). Without
            // this, the modal only ever sees legacy flat `addons` and falls back to
            // the "No add-ons available" message for pizza items.
            modifier_group_ids: Array.isArray((overriddenItem as any).modifier_group_ids)
              ? (overriddenItem as any).modifier_group_ids
              : [],
            category: overriddenItem.category || '',
          };
        });

      // Create category map from Firestore categories (with images)
      const categoryMap = new Map<string, { id: string; name: string; icon: string; image_url?: string }>();

      // Normalize for exact comparison only (trim, lowercase, collapse spaces).
      // Do NOT strip plurals / substrings — that caused false merges like
      // "Pizza Pizza Brand Vorspeisen" being folded into "Pizza" or "Vorspeisen".
      // Admin-defined category names must match products' category_name exactly.
      const normalizeCategoryName = (name: string) => {
        return name.trim().toLowerCase().replace(/\s+/g, ' ');
      };

      // Only treat as a variation when the normalized names are exactly equal
      // OR when an explicit legacy-alias mapping exists. No substring matching.
      const LEGACY_ALIASES: Record<string, string> = {
        'crispy rolls': 'crispy roll',
        'desserts': 'dessert',
        'desserts & daifuku mochis': 'dessert',
        'desserts and daifuku mochis': 'dessert',
      };
      const isVariationOf = (menuCategory: string, firestoreCategory: string) => {
        const menuNorm = normalizeCategoryName(menuCategory);
        const firestoreNorm = normalizeCategoryName(firestoreCategory);
        if (menuNorm === firestoreNorm) return true;
        const menuAlias = LEGACY_ALIASES[menuNorm] || menuNorm;
        const firestoreAlias = LEGACY_ALIASES[firestoreNorm] || firestoreNorm;
        return menuAlias === firestoreAlias;
      };

      // First, add categories from Firestore (they have images) - these take priority
      categories.forEach((cat: any) => {
        if (cat.is_active !== false && cat.name) {
          const normalizedName = normalizeCategoryName(cat.name);
          const categoryId = cat.name.toLowerCase().replace(/\s+/g, '-');
          categoryMap.set(normalizedName, {
            id: categoryId,
            name: cat.name.trim(),
            icon: cat.icon || getCategoryIcon(cat.name),
            image_url: cat.image_url || '',
            display_order: typeof cat.display_order === 'number' ? cat.display_order : 999999,
            display_order_by_brand: (cat as any).display_order_by_brand || undefined,
          } as any);
        }
      });

      // Then, add categories from menu items ONLY if they're not variations of Firestore categories
      mapped.forEach(item => {
        if (item.category_name) {
          const normalizedName = normalizeCategoryName(item.category_name);

          // Check if this menu category is a variation of any existing Firestore category
          let isVariation = false;
          for (const existingCat of categoryMap.values()) {
            if (isVariationOf(item.category_name, existingCat.name)) {
              isVariation = true;
              break;
            }
          }

          // Only add if it's not a duplicate/variation
          if (!categoryMap.has(normalizedName) && !isVariation) {
            const categoryId = item.category_name.toLowerCase().replace(/\s+/g, '-');
            categoryMap.set(normalizedName, {
              id: categoryId,
              name: item.category_name.trim(),
              icon: getCategoryIcon(item.category_name),
              image_url: ''
            });
          }
        }
      });

      // POS prefers the Saheeh brand's sort slot (main kitchen), falls back
      // to the shared `display_order` for anything without a per-brand value.
      const posDisplayOrder = (e: any): number => {
        const perBrand = e?.display_order_by_brand?.saheeh;
        if (typeof perBrand === 'number') return perBrand;
        return typeof e?.display_order === 'number' ? e.display_order : 999999;
      };
      const dynamicCategories = Array.from(categoryMap.values())
        .sort((a: any, b: any) => {
          const da = posDisplayOrder(a);
          const db = posDisplayOrder(b);
          if (da !== db) return da - db;
          return a.name.localeCompare(b.name);
        });

      // Add "All" category at the beginning
      const allCategories = [
        { id: 'all', name: 'All', icon: '🍱' },
        ...dynamicCategories
      ];

      setAvailableCategories(allCategories);

      console.log(`✅ [DASHBOARD] Loaded ${mapped.length} menu items (after filtering)`);
      console.log(`📋 [DASHBOARD] Generated ${dynamicCategories.length} categories:`, dynamicCategories.map(c => c.name));
      console.log(`📋 [DASHBOARD] Sample items:`, mapped.slice(0, 3));
      setProducts(mapped);
      setIsLoading(false);
    };

    const fetchMenuItems = async () => {
      setIsLoading(true);
      try {
        console.log('🔄 [DASHBOARD] Fetching menu items and categories from Firebase...');

        // Subscribe to real-time menu item updates
        unsubscribeMenuItems = firebaseMenuService.subscribeToMenuItems((updatedItems) => {
          console.log('🔄 [DASHBOARD] Menu items updated in real-time!', updatedItems.length);
          // Process updated items with current categories
          processMenuItems(updatedItems, currentCategories);
        });

        const [firebaseItems, firebaseCategories] = await Promise.all([
          firebaseMenuService.getMenuItems(true), // Force refresh on initial load
          firebaseMenuService.getCategories()
        ]);

        currentCategories = firebaseCategories;
        processMenuItems(firebaseItems, firebaseCategories);

      } catch (error) {
        console.error('❌ [DASHBOARD] Failed to fetch menu items from Firebase:', error);
        setProducts([]);
        setAvailableCategories([{ id: 'all', name: 'All', icon: '🍱' }]);
        setIsLoading(false);
      }
    };

    fetchMenuItems();

    // Cleanup subscription on unmount
    return () => {
      if (unsubscribeMenuItems) {
        unsubscribeMenuItems();
      }
    };
  }, []);

  const addToCart = useCallback((product: any, selectedAddOns: any[] = [], notes: string = '', extra?: { selections?: any; modifierGroups?: any[]; resolvedPrice?: number }) => {
    // Clear search after adding so the user can see all items and search again
    setSearchQuery('');
    setCart(prevCart => {
      // When modifiers applied, resolvedPrice already accounts for size + option deltas
      const productPrice = (extra?.resolvedPrice !== undefined)
        ? extra.resolvedPrice
        : (product.price_with_tax || parseFloat(product.base_price) || 0);
      const addOnsTotal = selectedAddOns.reduce((sum, addon) => {
        const quantity = addon.quantity || 1;
        return sum + ((addon.price || 0) * quantity);
      }, 0);
      const totalPrice = productPrice + addOnsTotal;

      const cartItemId = `${product.id}_${Date.now()}`;

      return [...prevCart, {
        ...product,
        id: product.id,
        price: totalPrice,
        quantity: 1,
        cartId: cartItemId,
        image_url: product.image_url || '',
        category_name: product.category_name || product.category || '',
        addons: selectedAddOns.map(addon => ({
          id: addon.id,
          name: addon.name,
          name_de: addon.name_de,
          price: addon.price,
          quantity: addon.quantity || 1
        })),
        notes: notes || '',
        menu_item_id: product.id,
        // New modifier fields — carried through to order save & receipt/kitchen printing
        selections: extra?.selections || undefined,
        modifier_snapshot: extra?.modifierGroups || undefined,
      }];
    });
  }, []);

  const updateCartItem = useCallback((cartId: number, product: any, selectedAddOns: any[] = [], notes: string = '', extra?: { selections?: any; modifierGroups?: any[]; resolvedPrice?: number }) => {
    setCart(prevCart => {
      const productPrice = (extra?.resolvedPrice !== undefined)
        ? extra.resolvedPrice
        : (product.price_with_tax || parseFloat(product.base_price) || 0);
      const addOnsTotal = selectedAddOns.reduce((sum, addon) => {
        const quantity = addon.quantity || 1;
        return sum + ((addon.price || 0) * quantity);
      }, 0);
      const totalPrice = productPrice + addOnsTotal;

      return prevCart.map(item =>
        item.cartId === cartId
          ? {
              ...item,
              price: totalPrice,
              addons: selectedAddOns.map(addon => ({
                id: addon.id,
                name: addon.name,
                name_de: addon.name_de,
                price: addon.price,
                quantity: addon.quantity || 1
              })),
              notes: notes || '',
              selections: extra?.selections || item.selections,
              modifier_snapshot: extra?.modifierGroups || item.modifier_snapshot,
            }
          : item
      );
    });
  }, []);

  const handleItemClick = useCallback((product: any) => {
    // Sold-out items stay visible for staff awareness but cannot be added to cart.
    if (product?.sold_out === true) {
      console.log('🚫 [DASHBOARD] Item is sold out, click blocked:', product.name);
      return;
    }
    console.log('🖱️ [DASHBOARD] Item clicked:', product.name);
    setSelectedProductForAddOns(product);
    setAddItemModalOpen(true);
    console.log('✅ [DASHBOARD] Modal should open now');
  }, []);

  const updateQuantity = useCallback((cartId: number, newQuantity: number) => {
    setCart(prevCart => {
      if (newQuantity <= 0) {
        return prevCart.filter(item => item.cartId !== cartId);
      }
      return prevCart.map(item =>
        item.cartId === cartId
          ? { ...item, quantity: newQuantity }
          : item
      );
    });
  }, []);

  // Calculate cart totals with proper tax calculation based on order type and item category
  // Rules: Takeaway = 7%, Dine-in = 19%, Drinks always = 19%
  // VAT is included in item prices, so we calculate base price from total
  const cartTotals = useMemo(() => {
    let subtotal7 = 0; // Subtotal for 7% tax items
    let subtotal19 = 0; // Subtotal for 19% tax items
    let tax7 = 0; // Tax for 7% items
    let tax19 = 0; // Tax for 19% items

    // Default order type for dashboard (can be changed in checkout)
    // We'll use 'dine_in' as default since most orders from dashboard are dine-in
    // The actual order type will be set in checkout dialog
    // Note: Dashboard preview uses 'dine_in' as default, actual order type is set in checkout
    const defaultOrderType = 'dine_in' as 'takeaway' | 'dine_in' | 'delivery';

    cart.forEach(item => {
      const itemTotal = item.price * item.quantity; // price_with_tax already includes VAT

      // CRITICAL: Check if item is a drink (always 19% VAT in Germany - TSE rule)
      // German TSE rules: ALL drinks = 19% VAT regardless of order type (dine-in, takeaway, delivery)
      // and regardless of payment method (cash, card, PayPal)
      const categoryName = (item.category_name || '').toLowerCase();
      const itemNameLower = (item.name || '').toLowerCase();
      const isDrink = categoryName.includes('drink') ||
        categoryName.includes('getränk') ||
        categoryName.includes('beverage') ||
        categoryName.includes('getränke') ||
        itemNameLower.includes('cola') ||
        itemNameLower.includes('coca') ||
        itemNameLower.includes('pepsi') ||
        itemNameLower.includes('sprite') ||
        itemNameLower.includes('fanta') ||
        itemNameLower.includes('wasser') ||
        itemNameLower.includes('water') ||
        itemNameLower.includes('saft') ||
        itemNameLower.includes('juice') ||
        itemNameLower.includes('limo') ||
        itemNameLower.includes('softdrink') ||
        itemNameLower.includes('bier') ||
        itemNameLower.includes('beer') ||
        itemNameLower.includes('wein') ||
        itemNameLower.includes('wine') ||
        itemNameLower.includes('kaffee') ||
        itemNameLower.includes('coffee') ||
        itemNameLower.includes('tee') ||
        itemNameLower.includes('tea');

      // Determine tax rate:
      // CRITICAL GERMAN TSE RULES:
      // - Drinks: ALWAYS 19% VAT (regardless of order type: dine-in, takeaway, delivery)
      // - Drinks: ALWAYS 19% VAT (regardless of payment method: cash, card, PayPal)
      // - Food (Takeaway/Delivery): 7% VAT
      // - Food (Dine-in): 19% VAT
      let taxRate: number;
      if (isDrink) {
        taxRate = 19; // CRITICAL: Drinks ALWAYS 19% VAT in Germany (TSE rule)
      } else {
        // Use order type (default to dine_in for dashboard preview)
        // Actual order type will be set in checkout
        taxRate = (defaultOrderType === 'takeaway' || defaultOrderType === 'delivery') ? 7 : 19;
      }

      // Calculate base price (without tax) from price_with_tax
      // VAT is included in price, so: basePrice = total / (1 + VAT%)
      // Example: Chicken-Crispy Roll €10.90:
      //   Takeaway (7%): basePrice = 10.90 / 1.07 = 10.19, tax = 10.90 - 10.19 = 0.71
      //   Dine-in (19%): basePrice = 10.90 / 1.19 = 9.16, tax = 10.90 - 9.16 = 1.74
      // Note: Dashboard preview uses dine-in (19%) as default
      const basePrice = itemTotal / (1 + taxRate / 100);
      const itemTax = itemTotal - basePrice;

      if (taxRate === 7) {
        subtotal7 += basePrice;
        tax7 += itemTax;
      } else if (taxRate === 19) {
        subtotal19 += basePrice;
        tax19 += itemTax;
      } else {
        // Fallback to 7% for unknown tax rates
        subtotal7 += basePrice;
        tax7 += itemTax;
      }
    });

    const subtotal = subtotal7 + subtotal19;
    const tax = tax7 + tax19;
    const total = subtotal + tax;

    // Debug logging
    if (cart.length > 0) {
      console.log('💰 [TAX CALC] Cart totals:', {
        items: cart.length,
        subtotal7: subtotal7.toFixed(2),
        tax7: tax7.toFixed(2),
        subtotal19: subtotal19.toFixed(2),
        tax19: tax19.toFixed(2),
        total: total.toFixed(2)
      });
    }

    return {
      subtotal,
      tax,
      total,
      subtotal7,
      tax7,
      subtotal19,
      tax19
    };
  }, [cart]);

  const filteredProducts = useMemo(() => {
    let filtered = products.filter(p => p.is_available !== false);

    console.log(`🔍 [FILTER] Starting with ${filtered.length} available products`);
    console.log(`🔍 [FILTER] Selected category: ${selectedCategory}`);
    console.log(`🔍 [FILTER] Search query: "${searchQuery}"`);

    // Apply category filter - use actual category names from Firebase
    // When user is searching, ignore category filter to search across all items
    if (selectedCategory !== 'all' && !searchQuery.trim()) {
      const beforeCount = filtered.length;
      // Find the category name from availableCategories
      const selectedCat = availableCategories.find(cat => cat.id === selectedCategory);
      if (selectedCat) {
        // STRICT equality: admin-defined category names must match product
        // category_name exactly (trim + lowercase + collapsed whitespace).
        // Substring matching was incorrectly pulling in items from other
        // categories whose names happened to be contained in the target
        // (e.g. clicking "Pizza Pizza Brand Vorspeisen" showed all "Pizza"
        // and "Vorspeisen" items).
        const normalizeCategory = (name: string) => {
          return name.trim().toLowerCase().replace(/\s+/g, ' ');
        };

        const targetCategoryNormalized = normalizeCategory(selectedCat.name);
        const LEGACY_ALIASES: Record<string, string> = {
          'crispy rolls': 'crispy roll',
          'desserts': 'dessert',
          'desserts & daifuku mochis': 'dessert',
          'desserts and daifuku mochis': 'dessert',
        };
        const targetAlias = LEGACY_ALIASES[targetCategoryNormalized] || targetCategoryNormalized;

        filtered = filtered.filter(product => {
          if (!product.category_name) return false;
          const productCategoryNormalized = normalizeCategory(product.category_name);
          const productAlias = LEGACY_ALIASES[productCategoryNormalized] || productCategoryNormalized;
          return productAlias === targetAlias;
        });
        console.log(`🔍 [FILTER] After category filter (${selectedCat.name}): ${filtered.length} items (was ${beforeCount})`);
      } else {
        console.warn(`⚠️ [FILTER] Category "${selectedCategory}" not found in available categories`);
      }
    }

    // Apply search filter — by article name only (not description/ingredients)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const beforeCount = filtered.length;
      filtered = filtered.filter(product =>
        product.name?.toLowerCase().includes(query)
      );
      console.log(`🔍 [FILTER] After search filter: ${filtered.length} items (was ${beforeCount})`);
    }

    // Respect admin-defined sort: category → per-brand display_order → name.
    // POS prefers the Saheeh brand slot (kitchen's main menu view); falls back
    // to the shared `display_order` so unreordered items keep their legacy spot.
    const posDisplayOrderItem = (e: any): number => {
      const perBrand = e?.display_order_by_brand?.saheeh;
      if (typeof perBrand === 'number') return perBrand;
      return typeof e?.display_order === 'number' ? e.display_order : 999999;
    };
    filtered = [...filtered].sort((a, b) => {
      const catA = (a.category_name || a.category || '').toLowerCase();
      const catB = (b.category_name || b.category || '').toLowerCase();
      if (catA !== catB) return catA.localeCompare(catB);
      const da = posDisplayOrderItem(a);
      const db = posDisplayOrderItem(b);
      if (da !== db) return da - db;
      return (a.name || '').localeCompare(b.name || '');
    });

    console.log(`✅ [FILTER] Final filtered products: ${filtered.length}`);
    return filtered;
  }, [products, selectedCategory, searchQuery, availableCategories]);

  const handleCheckoutClick = () => {
    if (cart.length === 0) return;
    setCheckoutDialogOpen(true);
  };

  const handleSplitTableClick = () => {
    if (cart.length === 0) return;
    setSplitDialogOpen(true);
  };

  // Apply discount to an existing order
  const handleApplyDiscount = async (managerEmail?: string, managerReason?: string) => {
    console.log('🏷️ [DISCOUNT] Apply clicked:', { discountOrderId, discountValue, discountType, discountReason });
    if (!discountOrderId || !discountValue) {
      console.warn('🏷️ [DISCOUNT] Missing orderId or value, aborting');
      return;
    }

    // FRAUD PREVENTION #2: Manager PIN required for discounts > 10%
    const val = parseFloat(discountValue);
    const order = tableActiveOrders.find((o: any) => o.id === discountOrderId);
    if (!order) return;
    const orderTotal = order.total_before_discount || order.total || 0;
    const discountPct = discountType === 'percentage' ? val : (orderTotal > 0 ? (val / orderTotal) * 100 : 0);
    if (discountPct > 10 && !managerEmail) {
      // Trigger manager PIN dialog
      setManagerPinAction({
        label: discountType === 'percentage'
          ? `Apply ${val}% discount (over 10% requires manager approval)`
          : `Apply €${val.toFixed(2)} discount (${discountPct.toFixed(0)}% — over 10%)`,
        requireReason: false,
        onApproved: (mEmail, mReason) => handleApplyDiscount(mEmail, mReason),
      });
      setManagerPinOpen(true);
      return;
    }

    try {
      const firestore = await import('../../services/firestoreCompat');
      const { db } = await import('../../services/firebaseConfig');
      if (isNaN(val) || val <= 0) {
        console.warn('🏷️ [DISCOUNT] Invalid value:', discountValue);
        return;
      }
      const originalTotal = orderTotal;
      const discountAmount = discountType === 'percentage'
        ? Math.round((originalTotal * val / 100) * 100) / 100
        : Math.min(val, originalTotal);
      const newTotal = Math.round((originalTotal - discountAmount) * 100) / 100;
      console.log('🏷️ [DISCOUNT] Applying:', { originalTotal, discountAmount, newTotal, orderId: discountOrderId, managerEmail });

      const orderRef = firestore.doc(db, 'orders', discountOrderId);
      const updateFields: any = {
        discount_type: discountType,
        discount_value: val,
        discount_amount: discountAmount,
        discount_reason: discountReason || (discountType === 'percentage' ? `${val}% Rabatt` : `€${val} Rabatt`),
        total_before_discount: originalTotal,
        total: newTotal,
      };
      // Audit log: who approved this discount
      if (managerEmail) {
        updateFields.manager_pin_used = true;
        updateFields.last_modified_by = managerEmail;
        updateFields.last_modified_by_role = 'manager';
        if (managerReason) updateFields.last_modification_reason = managerReason;
      }
      await firestore.updateDoc(orderRef, updateFields);
      console.log('🏷️ [DISCOUNT] ✅ Order updated successfully');

      // Update table totals — find table by number query
      try {
        const tableNumber = tableContext?.number;
        if (tableNumber) {
          const tablesRef = firestore.collection(db, 'restaurant_tables');
          const q = firestore.query(tablesRef, firestore.where('number', '==', tableNumber), firestore.limit(1));
          const snap = await firestore.getDocs(q);
          if (!snap.empty) {
            const tableDocRef = snap.docs[0].ref;
            const tableTotal = tableActiveOrders.reduce((sum: number, o: any) => {
              if (o.id === discountOrderId) return sum + newTotal;
              return sum + (o.total || 0);
            }, 0);
            await firestore.updateDoc(tableDocRef, {
              tab_total: Math.round(tableTotal * 100) / 100,
              total_amount: Math.round(tableTotal * 100) / 100,
            });
            console.log('🏷️ [DISCOUNT] ✅ Table totals updated');
          }
        }
      } catch (tableErr) {
        console.warn('🏷️ [DISCOUNT] Table update failed (non-critical):', tableErr);
      }

      setDiscountDialogOpen(false);
      setDiscountValue('');
      setDiscountReason('');
      setDiscountOrderId('');
      setTableOrdersRefreshCount(c => c + 1);
    } catch (err) {
      console.error('🏷️ [DISCOUNT] ❌ Failed to apply discount:', err);
      alert('Failed to apply discount. Please try again.');
    }
  };

  // ─── Void item from an active order ─────────────────────────────────
  // Client request (2026-04-29): cashier mistakenly added "Pizza Vegetale"
  // to a table — let them remove ONE item and re-pick the right one without
  // closing/reopening the tab.
  //
  // GoBD/KassenSichV: removing items from a kassed order must be auditable.
  // We call logAuditEvent BEFORE the Firestore write so the audit trail
  // exists even if the update fails halfway, and the entry includes the
  // exact item snapshot + the reason the cashier typed.
  //
  // Recompute path mirrors submitQrOrder.js exactly: per-item tax_rate is
  // already snapshotted on each item, so we just sum what's left and rebuild
  // the tax_breakdown buckets. Discount (if any) re-applies on the new
  // subtotal — percentage re-multiplies, fixed clamps to subtotal.
  const handleVoidItem = async () => {
    if (!voidItemTarget) return;
    if (!voidItemReason.trim()) {
      alert('Please enter a reason for removing this item.');
      return;
    }
    setVoidItemBusy(true);
    try {
      // Resolve current cashier identity (for last_modified_by + audit)
      let cashierEmail = '';
      try {
        const raw = localStorage.getItem('saheeh_pos_user');
        if (raw) cashierEmail = JSON.parse(raw).email || '';
      } catch { /* fallback */ }

      const { voidOrderItem } = await import('../../services/voidItemService');
      const result = await voidOrderItem({
        orderId: voidItemTarget.orderId,
        itemIndex: voidItemTarget.itemIndex,
        reason: voidItemReason.trim(),
        cashierEmail,
      });
      if (!result.success) {
        alert(result.error || 'Failed to remove item. Please try again.');
        return;
      }
      setVoidItemTarget(null);
      setVoidItemReason('');
      setTableOrdersRefreshCount((c) => c + 1);
    } catch (err) {
      console.error('[VOID] failed:', err);
      alert('Failed to remove item. Please try again.');
    } finally {
      setVoidItemBusy(false);
    }
  };

  // Remove discount from an existing order
  const handleRemoveDiscount = async () => {
    if (!discountOrderId) return;
    try {
      const firestore = await import('../../services/firestoreCompat');
      const { db } = await import('../../services/firebaseConfig');
      const order = tableActiveOrders.find((o: any) => o.id === discountOrderId);
      if (!order) return;
      const originalTotal = order.total_before_discount || order.total || 0;
      const orderRef = firestore.doc(db, 'orders', discountOrderId);
      await firestore.updateDoc(orderRef, {
        discount_type: firestore.deleteField(),
        discount_value: firestore.deleteField(),
        discount_amount: firestore.deleteField(),
        discount_reason: firestore.deleteField(),
        total_before_discount: firestore.deleteField(),
        total: originalTotal,
      });
      // Update table totals by querying table number
      try {
        const tableNumber = tableContext?.number;
        if (tableNumber) {
          const tablesRef = firestore.collection(db, 'restaurant_tables');
          const q = firestore.query(tablesRef, firestore.where('number', '==', tableNumber), firestore.limit(1));
          const snap = await firestore.getDocs(q);
          if (!snap.empty) {
            const tableTotal = tableActiveOrders.reduce((sum: number, o: any) => {
              if (o.id === discountOrderId) return sum + originalTotal;
              return sum + (o.total || 0);
            }, 0);
            await firestore.updateDoc(snap.docs[0].ref, {
              tab_total: Math.round(tableTotal * 100) / 100,
              total_amount: Math.round(tableTotal * 100) / 100,
            });
          }
        }
      } catch (tableErr) {
        console.warn('🏷️ [DISCOUNT] Table update failed (non-critical):', tableErr);
      }
      setDiscountDialogOpen(false);
      setDiscountValue('');
      setDiscountReason('');
      setDiscountOrderId('');
      setTableOrdersRefreshCount(c => c + 1);
    } catch (err) {
      console.error('🏷️ [DISCOUNT] ❌ Failed to remove discount:', err);
      alert('Failed to remove discount. Please try again.');
    }
  };

  const handleCheckout = async (checkoutData: CheckoutData) => {
    try {
      console.log(`%c[CHECKOUT START] Build: ${POS_BUILD_VERSION} | Print: ${POS_BUILD_PRINT} | Payment: ${checkoutData.payment_method}`, 'color: #ff9900; font-size: 14px; font-weight: bold; background: #222; padding: 4px 8px;');
      // Use calculated totals with proper tax rates. Add the zone-based delivery fee
      // (if any) — it was quoted in the dialog and passed through checkoutData.
      const deliveryFee = checkoutData.order_type === 'delivery' ? (checkoutData.delivery_fee || 0) : 0;
      const grossTotal = cartTotals.total + deliveryFee;
      // Apply checkout-level discount (works for dine_in, takeaway, delivery)
      const discountAmount = Math.max(0, Math.min(checkoutData.discount_amount || 0, grossTotal));
      const totalAmount = Math.max(0, grossTotal - discountAmount);
      const taxAmount = cartTotals.tax;
      const subtotal = cartTotals.subtotal;

      // Sequential order number (00001, 00002, ...) from Firestore counter
      let orderNumber = await FirebaseOrderService.getInstance().getNextOrderNumber();

      const orderItems = cart.map((item, index) => {
        // CRITICAL: Ensure price and quantity are numbers
        const itemPrice = parseFloat(item.price) || 0;
        const itemQuantity = parseInt(item.quantity) || 1;
        const itemTotal = itemPrice * itemQuantity;

        // Log item data for debugging
        console.log(`📦 [ORDER ITEM ${index + 1}] Creating order item:`, {
          id: item.id,
          name: item.name,
          quantity: itemQuantity,
          price: itemPrice,
          priceType: typeof item.price,
          total: itemTotal,
          category: item.category_name,
        });

        // CRITICAL: Check if item is a drink (always 19% VAT in Germany - TSE rule)
        // German TSE rules: ALL drinks = 19% VAT regardless of order type (dine-in, takeaway, delivery)
        // and regardless of payment method (cash, card, PayPal)
        const categoryName = (item.category_name || '').toLowerCase();
        const itemNameLower = (item.name || '').toLowerCase();
        const isDrink = categoryName.includes('drink') ||
          categoryName.includes('getränk') ||
          categoryName.includes('beverage') ||
          categoryName.includes('getränke') ||
          itemNameLower.includes('cola') ||
          itemNameLower.includes('coca') ||
          itemNameLower.includes('pepsi') ||
          itemNameLower.includes('sprite') ||
          itemNameLower.includes('fanta') ||
          itemNameLower.includes('wasser') ||
          itemNameLower.includes('water') ||
          itemNameLower.includes('saft') ||
          itemNameLower.includes('juice') ||
          itemNameLower.includes('limo') ||
          itemNameLower.includes('softdrink') ||
          itemNameLower.includes('bier') ||
          itemNameLower.includes('beer') ||
          itemNameLower.includes('wein') ||
          itemNameLower.includes('wine') ||
          itemNameLower.includes('kaffee') ||
          itemNameLower.includes('coffee') ||
          itemNameLower.includes('tee') ||
          itemNameLower.includes('tea');

        // Determine tax rate based on order type and item category
        // CRITICAL GERMAN TSE RULES:
        // - Drinks: ALWAYS 19% VAT (regardless of order type: dine-in, takeaway, delivery)
        // - Drinks: ALWAYS 19% VAT (regardless of payment method: cash, card, PayPal)
        // - Food (Takeaway/Delivery): 7% VAT
        // - Food (Dine-in): 19% VAT
        let taxRate: number;
        if (isDrink) {
          taxRate = 19; // CRITICAL: Drinks ALWAYS 19% VAT in Germany (TSE rule)
        } else {
          const orderType = checkoutData.order_type || 'dine_in';
          taxRate = (orderType === 'takeaway' || orderType === 'delivery') ? 7 : 19;
        }

        // Calculate base price (without tax) from price_with_tax
        // VAT is included in price, so: basePrice = total / (1 + VAT%)
        const basePrice = itemTotal / (1 + taxRate / 100);
        const itemTax = itemTotal - basePrice;

        // Calculate base price (item price without add-ons)
        const addonsTotal = (item.addons || []).reduce((sum: number, addon: any) => {
          const qty = addon.quantity || 1;
          return sum + ((addon.price || 0) * qty);
        }, 0);
        const itemBasePrice = itemPrice - addonsTotal; // Base price without add-ons
        
        const orderItem = {
          menu_item_id: item.id,
          menu_item_name: item.name, // CRITICAL: Firebase expects menu_item_name
          name: item.name, // Also include name for compatibility
          quantity: itemQuantity,
          unit_price: itemPrice, // CRITICAL: Firebase expects unit_price (price_with_tax per unit)
          price: itemPrice, // Also include price for compatibility
          base_price: (itemBasePrice / itemQuantity) / (1 + taxRate / 100), // base price without tax per unit (without add-ons)
          tax_rate: taxRate,
          tax_amount: itemTax,
          subtotal: basePrice,
          total: itemTotal,
          category_name: item.category_name || 'Unknown',
          addons: item.addons || [], // Include add-ons if any
          notes: item.notes || '' // Include notes if any
        };

        console.log(`✅ [ORDER ITEM ${index + 1}] Order item created:`, {
          menu_item_name: orderItem.menu_item_name,
          quantity: orderItem.quantity,
          unit_price: orderItem.unit_price,
          total: orderItem.total,
          validation: {
            nameMatches: orderItem.menu_item_name === item.name,
            priceMatches: orderItem.unit_price === itemPrice,
            quantityMatches: orderItem.quantity === itemQuantity,
            totalMatches: orderItem.total === itemTotal,
          },
        });

        return orderItem;
      });

      // Get table area from tableContext if available
      const tableArea = tableContext?.area || checkoutData.table_area || null;

      // CRITICAL FIX: Set payment_status based on payment_method
      // Cash and Card payments are completed immediately (card via swipe machine at POS)
      // Only PayPal payments are pending until payment is captured via QR code
      const paymentMethod = checkoutData.payment_method || 'cash';
      const paymentStatus = checkoutData.payment_status || ((paymentMethod === 'cash' || paymentMethod === 'card') ? 'completed' : 'pending');

      console.log('🔒 [PAYMENT STATUS] Setting payment status:', {
        paymentMethod,
        paymentStatus,
        fromCheckoutData: checkoutData.payment_status,
        calculated: paymentMethod === 'cash' ? 'completed' : 'pending',
      });

      const orderData = {
        customer_name: checkoutData.customer_name || (
          checkoutData.order_type === 'dine_in'
            ? (checkoutData.table_number ? `Dine-In Tisch ${checkoutData.table_number || tableContext?.number || tableContext?.display_number}` : 'Dine-In')
            : 'Laufkunde'
        ),
        customer_phone: checkoutData.customer_phone || '',
        customer_first_name: checkoutData.customer_first_name || '',
        customer_last_name: checkoutData.customer_last_name || '',
        customer_address: checkoutData.customer_address || '',
        customer_postcode: checkoutData.customer_postcode || '',
        customer_city: checkoutData.customer_city || '',
        delivery_address: checkoutData.customer_address
          ? `${checkoutData.customer_address}, ${checkoutData.customer_postcode || ''} ${checkoutData.customer_city || ''}`.trim()
          : '',
        delivery_latitude: checkoutData.delivery_latitude || null,
        delivery_longitude: checkoutData.delivery_longitude || null,
        table_number: checkoutData.table_number || null,
        table_area: tableArea, // CRITICAL: Store area with order to ensure correct table matching
        order_type: checkoutData.order_type || 'dine_in',
        payment_method: paymentMethod,
        payment_status: paymentStatus, // CRITICAL: 'pending' for PayPal/Card, 'completed' for cash
        items: orderItems,
        subtotal: subtotal,
        tax: taxAmount,
        delivery_fee: deliveryFee,
        total: totalAmount,
        // Discount metadata (only present when a discount was applied at checkout)
        ...(discountAmount > 0 ? {
          discount_type: checkoutData.discount_type,
          discount_value: checkoutData.discount_value,
          discount_amount: discountAmount,
          discount_reason: checkoutData.discount_reason || '',
          total_before_discount: grossTotal,
        } : {}),
      };

      // DINE-IN: Check if there's an existing active order for this table
      // If yes, add items to existing order instead of creating a new one
      let newOrder: any;
      let isAddingToExistingOrder = false;

      // START table lookup in PARALLEL with order creation — don't wait for it yet
      const _tableLookupPromise = (orderData.order_type === 'dine_in' && orderData.table_number)
        ? firebaseTableService.findTable(orderData.table_number, tableArea as 'indoor' | 'outdoor' | undefined).catch(() => null)
        : Promise.resolve(null);

      // Pickup/takeaway "+ Add Items" flow: Orders page stashes the target
      // order in localStorage when staff click "Add Items", then navigates here.
      // We pick that up and route this checkout through addItemsToOrder.
      const editOrderId = typeof window !== 'undefined' ? localStorage.getItem('saheeh_edit_order_id') : null;
      const editOrderNumber = typeof window !== 'undefined' ? localStorage.getItem('saheeh_edit_order_number') : null;
      const editOrderCustomer = typeof window !== 'undefined' ? localStorage.getItem('saheeh_edit_order_customer') : null;
      if (editOrderId && orderData.order_type !== 'dine_in') {
        try {
          const { doc: dRef, getDoc: gd } = await import('../../services/firestoreCompat');
          const { db: dbRef } = await import('../../services/firebaseConfig');
          const snap = await gd(dRef(dbRef, 'orders', editOrderId));
          if (snap.exists()) {
            const s = (snap.data().status || '').toLowerCase();
            if (!['completed', 'cancelled'].includes(s)) {
              isAddingToExistingOrder = true;
              try {
                const realName = editOrderCustomer || snap.data().customer_name || orderData.customer_name;
                newOrder = await firebaseOrderService.addItemsToOrder(
                  editOrderId, orderItems, subtotal, taxAmount, totalAmount,
                  realName || undefined,
                );
                orderNumber = editOrderNumber || newOrder.order_number || orderNumber;
                orderData.customer_name = realName;
                console.log(`✅ [CHECKOUT] Added items to pickup/takeaway order #${orderNumber}`);
              } catch (e) {
                console.error('❌ [CHECKOUT] Failed to add items to pickup order:', e);
                isAddingToExistingOrder = false;
              }
            } else {
              console.warn(`[CHECKOUT] Edit target order is ${s} — ignoring edit flag`);
            }
          }
        } catch (e) {
          console.warn('[CHECKOUT] Could not load edit order target:', e);
        }
        // Clear the flag — one-shot use
        try {
          localStorage.removeItem('saheeh_edit_order_id');
          localStorage.removeItem('saheeh_edit_order_number');
          localStorage.removeItem('saheeh_edit_order_customer');
          localStorage.removeItem('saheeh_edit_order_type');
        } catch {}
        // Banner stays visible until the user leaves dashboard — this gives them
        // feedback that the addition succeeded. Next navigation resets it.
        setEditOrderContext(null);
      }

      if (!isAddingToExistingOrder && orderData.order_type === 'dine_in') {
        let existingOrderId: string | null = null;
        let existingOrderNumber: string | null = null;
        let existingCustomerName: string | null = null;

        // Method 1: From tableActiveOrders (loaded from Firestore query)
        // Positive allow-list — prevents reusing an order stuck in a non-
        // standard terminal state ('delivered', 'paid', '', etc.) which would
        // otherwise cause the next customer's items to append to a stale doc.
        if (tableActiveOrders.length > 0) {
          const CHECKOUT_ACTIVE_STATUSES = ['pending', 'confirmed', 'preparing', 'ready', 'assigned'];
          const existingOrder = tableActiveOrders.find((o: any) => {
            const status = (o.status || '').toLowerCase();
            return CHECKOUT_ACTIVE_STATUSES.includes(status);
          });
          if (existingOrder?.id) {
            existingOrderId = existingOrder.id;
            existingOrderNumber = existingOrder.order_number;
            existingCustomerName = existingOrder.customer_name;
            console.log(`🪑 [CHECKOUT] Method 1: Found existing order from tableActiveOrders: ${existingOrderNumber}`);
          }
        }

        // Method 2: From tableContext (passed from restaurant page) - fallback if query was slow
        // CRITICAL: Must verify the order is NOT completed/cancelled before reusing
        if (!existingOrderId && tableContext?.active_order_id) {
          try {
            const { doc: firestoreDocRef, getDoc: firestoreGetDoc } = await import('../../services/firestoreCompat');
            const { db: firestoreDb } = await import('../../services/firebaseConfig');
            const orderSnap = await firestoreGetDoc(firestoreDocRef(firestoreDb, 'orders', tableContext.active_order_id));
            if (orderSnap.exists()) {
              const orderStatus = (orderSnap.data().status || '').toLowerCase();
              if (orderStatus !== 'completed' && orderStatus !== 'cancelled') {
                existingOrderId = tableContext.active_order_id;
                existingOrderNumber = tableContext.active_order_number || null;
                existingCustomerName = tableContext.customer_name || tableCustomerName || null;
                console.log(`🪑 [CHECKOUT] Method 2: Found ACTIVE order from tableContext: ${existingOrderNumber} (status: ${orderStatus})`);
              } else {
                console.log(`🪑 [CHECKOUT] Method 2: Order ${tableContext.active_order_number} is ${orderStatus} — skipping (will create new order)`);
                // Clear stale context so it doesn't keep matching
                setTableContext((prev: any) => prev ? { ...prev, active_order_id: undefined, active_order_number: undefined } : prev);
              }
            }
          } catch (e) {
            console.warn('⚠️ [CHECKOUT] Method 2 verification failed:', e);
          }
        }

        // Method 3: Direct Firestore lookup — run all format variants in PARALLEL
        if (!existingOrderId && orderData.table_number) {
          try {
            const { collection: col, query: q, where: w, getDocs: gd, limit: lim } = await import('../../services/firestoreCompat');
            const { db } = await import('../../services/firebaseConfig');
            const ordersRef = col(db, 'orders');
            const tableNum = orderData.table_number;
            const eightHoursAgo = new Date(Date.now() - 8 * 60 * 60 * 1000);
            // Fire all 3 format queries simultaneously instead of sequentially
            const snaps = await Promise.all(
              [tableNum, String(tableNum), Number(tableNum)].map(tryNum =>
                gd(q(ordersRef, w('table_number', '==', tryNum), w('order_type', '==', 'dine_in'), lim(10))).catch(() => null)
              )
            );
            for (const snap of snaps) {
              if (!snap || snap.empty) continue;
              for (const docSnap of snap.docs) {
                const data = docSnap.data();
                const status = (data.status || '').toLowerCase();
                if (status === 'cancelled' || status === 'completed') continue;
                const createdAt = data.created_at?.toDate?.() || (data.created_at?.seconds ? new Date(data.created_at.seconds * 1000) : null);
                if (createdAt && createdAt > eightHoursAgo) {
                  existingOrderId = docSnap.id;
                  existingOrderNumber = data.order_number;
                  existingCustomerName = data.customer_name;
                  break;
                }
              }
              if (existingOrderId) break;
            }
          } catch (lookupError) {
            console.warn('⚠️ [CHECKOUT] Method 3 lookup failed:', lookupError);
          }
        }

        // Add items to existing order
        if (existingOrderId) {
          try {
            isAddingToExistingOrder = true;
            // Determine real customer name to persist on the order
            // Helper: check if a name is a system default (not a real person's name)
            const isSystemDefault = (n: string | null | undefined) =>
              !n || n === 'Laufkunde' || n === 'Walk-in Customer' || n === 'Bestellung' || n.startsWith('Dine-In');
            const realCustomerName =
              (!isSystemDefault(existingCustomerName) ? existingCustomerName : null) ||
              (!isSystemDefault(tableCustomerName) ? tableCustomerName : null) ||
              (!isSystemDefault(tableContext?.customer_name) ? tableContext?.customer_name : null) ||
              (!isSystemDefault(orderData.customer_name) ? orderData.customer_name : null) ||
              existingCustomerName || orderData.customer_name;
            newOrder = await firebaseOrderService.addItemsToOrder(
              existingOrderId,
              orderItems,
              subtotal,
              taxAmount,
              totalAmount,
              realCustomerName || undefined
            );
            orderNumber = existingOrderNumber || orderNumber;
            // Update orderData with the real customer name (only if it's a real person name)
            if (realCustomerName && !isSystemDefault(realCustomerName)) {
              orderData.customer_name = realCustomerName;
            }
            console.log('✅ [CHECKOUT] Items added to existing order:', orderNumber, 'customer:', orderData.customer_name);
          } catch (addError) {
            console.error('❌ [CHECKOUT] Failed to add items to existing order, creating new:', addError);
            isAddingToExistingOrder = false;
          }
        }
      }

      // Helper for new-order path (isSystemDefault may not be in scope if we didn't enter the adding block)
      const isDefaultCustName = (n: string | null | undefined) =>
        !n || n === 'Laufkunde' || n === 'Walk-in Customer' || n === 'Bestellung' || n.startsWith('Dine-In');
      if (!isAddingToExistingOrder) {
        // For dine-in: use existing real customer name if the current one is a system default
        if (orderData.order_type === 'dine_in' && isDefaultCustName(orderData.customer_name)) {
          if (!isDefaultCustName(tableCustomerName)) {
            orderData.customer_name = tableCustomerName!;
          } else if (!isDefaultCustName(tableContext?.customer_name)) {
            orderData.customer_name = tableContext!.customer_name!;
          }
        }
        console.log('🛒 [CHECKOUT] Creating new order with Firebase...', orderData);
        // CRITICAL: Pass pre-allocated order number to avoid double-incrementing the counter
        newOrder = await firebaseOrderService.createOrder({ ...orderData, _preAllocatedOrderNumber: orderNumber });
        // Sync local orderNumber with the one stored in Firestore
        orderNumber = newOrder.order_number || orderNumber;
        console.log('✅ [CHECKOUT] Order created successfully:', newOrder);
      }

      // Create payment record in background — does NOT block the checkout response
      if (!isAddingToExistingOrder) {
        const _newOrder = newOrder;
        const _orderId = _newOrder.id;
        const _paymentData = {
          order_id: _orderId || _newOrder.order_number,
          order_number: _newOrder.order_number,
          customer_name: orderData.customer_name || 'Laufkunde',
          payment_method: paymentMethod,
          amount: totalAmount,
          status: paymentStatus,
          table_number: orderData.table_number || null,
          processed_by: (() => { try { const u = JSON.parse(localStorage.getItem('saheeh_pos_user') || '{}'); return u.email || u.name || 'POS System'; } catch { return 'POS System'; } })(),
          is_split_payment: checkoutData.use_split_payment || false,
          split_session_id: (checkoutData as any).split_session_id || null,
          split_person_name: (checkoutData as any).split_person_name || null,
          split_total_people: (checkoutData as any).split_total_people || null,
          split_person_index: (checkoutData as any).split_person_index || null,
        };
        (async () => {
          try {
            const firebasePaymentService = (await import('../../services/firebasePaymentService')).default;
            await firebasePaymentService.getInstance().createPayment(_paymentData);
          } catch (e) {
            console.error('❌ [CHECKOUT] Background payment record failed:', e);
          }
        })();
      }

      // Resolve the table we started looking up in parallel — should already be done
      // Fall back to a fresh lookup if parallel lookup returned null (race condition on first order)
      let _cachedTable = await _tableLookupPromise;
      if (!_cachedTable && orderData.order_type === 'dine_in' && orderData.table_number) {
        _cachedTable = await firebaseTableService.findTable(orderData.table_number, tableArea as 'indoor' | 'outdoor' | undefined).catch(() => null);
      }

      // Update table tab in Firebase if this is a dine-in order with table number
      // Uses tab system: accumulates orders instead of overwriting
      // When adding to existing order, update tab totals but don't add new order ID
      if (orderData.order_type === 'dine_in' && orderData.table_number && !isAddingToExistingOrder) {
        try {
          // Use the cached table — no extra Firestore round-trip needed
          const table = _cachedTable;

          if (table && table.id) {
            // CRITICAL: Check if the table has stale data from a previous session (>12h old)
            // If so, reset the tab before adding the new order to prevent accumulation
            let tableStartTime: Date | null = null;
            const rawSt = table.start_time;
            if (rawSt) {
              if (typeof rawSt === 'string') tableStartTime = new Date(rawSt);
              else if ((rawSt as any)?.toDate) tableStartTime = (rawSt as any).toDate();
              else if ((rawSt as any)?.seconds) tableStartTime = new Date((rawSt as any).seconds * 1000);
            }
            const isStaleTab = tableStartTime && (Date.now() - tableStartTime.getTime()) > 12 * 60 * 60 * 1000;

            if (isStaleTab && table.status === 'occupied') {
              console.log(`🧹 [TABLE TAB] Table ${orderData.table_number} has stale tab (start: ${tableStartTime?.toISOString()}) — resetting before new order`);
              // Reset the tab first, then add the new order
              await firebaseTableService.openTab(table.id as string, orderData.customer_name);
            }

            // Add this order to the table's running tab (accumulate, don't overwrite)
            await firebaseTableService.addOrderToTab(
              table.id as string,
              newOrder.id || '',
              totalAmount,
              cart.length,
              orderNumber
            );

            // CRITICAL: Set table status to 'occupied' + update customer/order info
            {
              const { getAuth } = await import('firebase/auth');
              const currentAuth = getAuth();
              const tableUpdateData: any = {
                status: 'occupied',
                tab_open: true,
                active_order_id: newOrder.id || '', // CRITICAL: Dashboard needs this for direct order lookup
                active_order_number: orderNumber,
                start_time: isStaleTab ? new Date().toISOString() : (table.start_time || new Date().toISOString()),
                occupied_by: currentAuth.currentUser?.email || (() => { try { return JSON.parse(localStorage.getItem('saheeh_pos_user') || '{}').email || 'unknown'; } catch { return 'unknown'; } })(),
                updated_at: new Date().toISOString(),
              };
              if (orderData.customer_name) {
                tableUpdateData.customer_name = orderData.customer_name;
              }
              const { doc: firestoreDoc, updateDoc } = await import('../../services/firestoreCompat');
              const { db } = await import('../../services/firebaseConfig');
              await updateDoc(firestoreDoc(db, 'restaurant_tables', table.id), tableUpdateData);
            }

            console.log(`✅ [TABLE TAB] Order ${newOrder.id} added to table ${orderData.table_number} tab. Round: ${(table.tab_rounds || 0) + 1}`);
          } else {
            // Fallback: table not found, use legacy update
            console.warn(`⚠️ [TABLE TAB] Table ${orderData.table_number} not found, using legacy update`);
            await firebaseTableService.updateTableByNumber(
              orderData.table_number,
              'occupied',
              {
                customer_name: orderData.customer_name || undefined,
                total_amount: totalAmount,
                item_count: cart.length,
                start_time: new Date().toISOString(),
                active_order_number: orderNumber,
              },
              tableArea as 'indoor' | 'outdoor' | undefined
            );
          }

          // Dispatch event to notify restaurant page
          window.dispatchEvent(new CustomEvent('tableStatusUpdated', {
            detail: {
              tableNumber: orderData.table_number,
              status: 'occupied',
              updateData: {
                total_amount: totalAmount,
                item_count: cart.length,
              }
            }
          }));
        } catch (tableError) {
          console.error('❌ [TABLE TAB] Error updating table tab:', tableError);
          // Don't block checkout if table update fails
        }
      }

      // When adding to existing order, update the table's tab totals AND customer info
      if (isAddingToExistingOrder && orderData.order_type === 'dine_in' && orderData.table_number) {
        try {
          // Reuse cached table — already fetched in parallel, no second lookup needed
          const table = _cachedTable;
          if (table && table.id) {
            const { doc: firestoreDoc, updateDoc, serverTimestamp: sTs } = await import('../../services/firestoreCompat');
            const { db } = await import('../../services/firebaseConfig');
            // Use ABSOLUTE values from the updated order — no increments (avoids drift)
            const orderTotal = newOrder?.total || 0;
            const orderItemCount = newOrder?.items?.length || 0;
            const tableUpdatePayload: any = {
              tab_total: orderTotal,
              tab_item_count: orderItemCount,
              total_amount: orderTotal,
              item_count: orderItemCount,
              active_order_id: newOrder?.id || '', // CRITICAL: ensure active_order_id is always set
              active_order_number: orderNumber,
              updated_at: new Date().toISOString(),
              updatedAt: sTs(),
            };
            // Ensure customer name is correct on table doc (only set real person names)
            const bestName = orderData.customer_name || newOrder?.customer_name || '';
            if (bestName && !isDefaultCustName(bestName)) {
              tableUpdatePayload.customer_name = bestName;
            }
            await updateDoc(firestoreDoc(db, 'restaurant_tables', table.id), tableUpdatePayload);
          }
        } catch (tabError) {
          console.error('❌ [TABLE TAB] Error updating tab totals for existing order:', tabError);
        }
      }

      // Prepare receipt data
      // CRITICAL: Log order_type to verify it's correct
      const orderTypeForReceipt = checkoutData.order_type || 'dine_in';
      console.log('🧾 [RECEIPT] Order type for receipt:', {
        fromCheckoutData: checkoutData.order_type,
        finalValue: orderTypeForReceipt,
        orderDataOrderType: orderData.order_type
      });

      // Determine customer name: if no name and no address, show order type label
      // Helper: Check if customer name is empty or default value
      const isEmptyCustomerName = (name: string | undefined | null): boolean => {
        if (!name) return true;
        const trimmed = name.trim();
        return trimmed === '' || trimmed === 'Laufkunde' || trimmed === 'Walk-in Customer';
      };
      
      let customerName = orderData.customer_name;
      // For dine-in adding to existing order, always use the real customer name
      if (isAddingToExistingOrder && isEmptyCustomerName(customerName)) {
        customerName = tableCustomerName || tableContext?.customer_name || customerName;
      }
      const customerAddress = checkoutData.customer_address || '';
      if (isEmptyCustomerName(customerName) && !customerAddress) {
        // No name and no address - show order type label:
        // - Takeaway → "Abholung"
        // - Delivery → "Lieferung"
        // - Dine In → "Dine-In Tisch X"
        if (orderTypeForReceipt === 'takeaway') {
          customerName = 'Abholung';
        } else if (orderTypeForReceipt === 'delivery') {
          customerName = 'Lieferung';
        } else {
          // Dine-in: show table number instead of generic label
          const tblNum = orderData.table_number || tableContext?.number || tableContext?.display_number;
          customerName = tblNum ? `Dine-In Tisch ${tblNum}` : 'Dine-In';
        }
      } else if (isEmptyCustomerName(customerName)) {
        if (orderTypeForReceipt === 'dine_in') {
          const tblNum = orderData.table_number || tableContext?.number || tableContext?.display_number;
          customerName = tblNum ? `Dine-In Tisch ${tblNum}` : 'Dine-In';
        } else {
          customerName = 'Bestellung';
        }
      }

      const receiptData = {
        orderNumber: orderNumber,
        customerName: customerName,
        customerNumber: '56447',
        orderDate: new Date().toLocaleDateString('en-US'),
        orderTime: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
        items: cart.map((item, idx) => {
          // Calculate base price (without add-ons)
          const addonsTotal = (item.addons || []).reduce((sum: number, addon: any) => {
            const qty = addon.quantity || 1;
            return sum + ((addon.price || 0) * qty);
          }, 0);
          const itemBasePrice = (item.price || 0) - addonsTotal; // Base price per unit without add-ons
          
          return {
            name: item.name,
            quantity: item.quantity,
            price: item.price, // Total price per unit (base + add-ons)
            base_price: itemBasePrice, // Base price per unit (without add-ons)
            total: item.price * item.quantity, // Total for this item (base + add-ons) * quantity
            category_name: item.category_name || item.category || '', // CRITICAL: Must have category_name for VAT calculation
            addons: item.addons || [], // Include add-ons with quantities
            notes: item.notes || '', // Include notes
            itemNumber: idx + 1, // Item number for receipt
          };
        }),
        subtotal: subtotal,
        tax: taxAmount,
        total: totalAmount,
        // CRITICAL: Use payment_method directly from orderData (set from checkoutData.payment_method on line 664)
        // Log to verify the value is correct
        paymentMethod: (() => {
          const pm = orderData.payment_method || 'cash';
          console.log('💳 [RECEIPT] Payment method for receipt:', {
            fromOrderData: orderData.payment_method,
            finalValue: pm,
            checkoutPaymentMethod: checkoutData.payment_method,
          });
          return pm;
        })(),
        paymentStatus: orderData.payment_status || 'completed',
        order_type: orderTypeForReceipt, // CRITICAL: Must have order_type for correct VAT calculation
        customerAddress: customerAddress || undefined, // Include customer address for receipt generation
        paypalApprovalUrl: (orderData as any).paypal_approval_url || undefined, // Include PayPal approval URL for QR code
        isCopy: false, // CRITICAL: Initial receipt is NOT a copy
        // Dine-in additional info
        // Prefer the real physical table number stored on the order over any
        // tableContext field — guards against stale UI indices from old caches.
        tableNumber: orderData.order_type === 'dine_in' ? (orderData.table_number || tableContext?.number || tableContext?.display_number || null) : null,
        isAdditionalRound: isAddingToExistingOrder, // Indicates this is an additional round for an existing table order
        // Discount fields — render on cashier receipt / invoice if applied at checkout
        ...(discountAmount > 0 ? {
          discount_type: checkoutData.discount_type,
          discount_value: checkoutData.discount_value,
          discount_amount: discountAmount,
          discount_reason: checkoutData.discount_reason || '',
          total_before_discount: grossTotal,
        } : {}),
      };

      console.log('🧾 [RECEIPT] Receipt data prepared:', {
        orderNumber: receiptData.orderNumber,
        order_type: receiptData.order_type,
        items_count: receiptData.items.length,
        items_with_category: receiptData.items.map((i: any) => ({
          name: i.name,
          category_name: i.category_name,
          price: i.price
        }))
      });

      // Prepare kitchen order data
      // Determine customer name: if no name and no address, show order type label
      // (Reusing isEmptyCustomerName helper from above)
      let kitchenCustomerName = orderData.customer_name;
      // For dine-in adding to existing order, always use the real customer name
      if (isAddingToExistingOrder && isEmptyCustomerName(kitchenCustomerName)) {
        kitchenCustomerName = tableCustomerName || tableContext?.customer_name || kitchenCustomerName;
      }
      const kitchenCustomerAddress = checkoutData.customer_address
        ? `${checkoutData.customer_address}, ${checkoutData.customer_postcode || ''} ${checkoutData.customer_city || ''}`.trim()
        : undefined;
      if (isEmptyCustomerName(kitchenCustomerName) && !kitchenCustomerAddress) {
        // No name and no address - show order type label:
        // - Takeaway → "Abholung"
        // - Delivery → "Lieferung"
        // - Dine In → "Bestellung"
        if (orderTypeForReceipt === 'takeaway') {
          kitchenCustomerName = 'Abholung';
        } else if (orderTypeForReceipt === 'delivery') {
          kitchenCustomerName = 'Lieferung';
        } else {
          kitchenCustomerName = 'Bestellung'; // Dine In order without name/address
        }
      } else if (isEmptyCustomerName(kitchenCustomerName)) {
        kitchenCustomerName = 'Bestellung'; // Has address but no name (for dine_in)
      }

      // Compute delivery tour number for delivery orders
      // Client request: use last 2 digits of order number as tour number (e.g., order 11205 → Tour 05)
      // Only for delivery (out-of-house) orders, max 2 digits
      let tripNumber: string | undefined;
      if (orderTypeForReceipt === 'delivery') {
        const numStr = String(orderNumber || '');
        if (numStr.length >= 2) {
          tripNumber = numStr.slice(-2); // Last 2 digits (e.g., "05", "45")
        } else if (numStr.length === 1) {
          tripNumber = '0' + numStr; // Pad single digit (e.g., "03")
        }
      }

      // Prepare receipt data with customer address
      const receiptDataWithAddress = {
        ...receiptData,
        customerAddress: checkoutData.customer_address
          ? `${checkoutData.customer_address}, ${checkoutData.customer_postcode || ''} ${checkoutData.customer_city || ''}`.trim()
          : undefined,
        customerPhone: checkoutData.customer_phone || undefined,
        portal: orderTypeForReceipt === 'takeaway' ? 'takeaway.com' : undefined,
        // Delivery trip number (T1, T2, T3...) — shown on receipts + kitchen tickets
        ...(tripNumber ? { trip_number: tripNumber } : {}),
      };

      // CRITICAL: For PayPal orders, skip automatic print here - it will be done after PayPal order is created (with QR code)
      // For cash/card orders, do automatic print immediately
      const isPayPalOrder = paymentMethod === 'paypal';

      if (isPayPalOrder) {
        console.log('💳 [PAYPAL] PayPal order detected - skipping automatic print here');
        console.log('💳 [PAYPAL] Automatic print with QR code will happen after PayPal order is created');
      } else {
        console.log(`%c[CASH/CARD] Creating Firestore print jobs (${POS_BUILD_VERSION})`, 'color: #00ff00; font-size: 14px; font-weight: bold; background: #333; padding: 4px 8px;');
        console.log('[CASH/CARD] Order:', orderNumber, '| Items:', receiptDataWithAddress.items?.length);

        // Fire-and-forget print jobs — do NOT await so order confirmation returns immediately
        // DINE-IN (table orders): kitchen print ONLY on every round.
        //   The cashier/invoice receipt is printed when the tab is closed ("Close Tab" flow).
        // TAKEAWAY / DELIVERY: both kitchen + cashier receipt on order creation.
        const isDineIn = orderData.order_type === 'dine_in';
        if (isAddingToExistingOrder) {
          // Additional round: print ONLY the new items with a visible ZUSATZ header.
          // Without a unique round id the dedup key would collide with the first round
          // and the kitchen would never see the addition.
          const additionRoundId = `r${Date.now()}`;
          const newItemsOnly = receiptDataWithAddress.items?.slice(-orderItems.length) || orderItems;
          const additionData = {
            ...receiptDataWithAddress,
            items: newItemsOnly,
            _isAddition: true,
          };
          console.log(`[PRINT] Addition round ${additionRoundId} — ${newItemsOnly.length} new items to kitchen`);
          firestorePrintService.printKitchenAddition(
            newOrder.id || orderNumber,
            orderNumber,
            additionData,
            newItemsOnly,
            additionRoundId,
          ).then(results => results.forEach(r => console.log('[PRINT] Addition kitchen job:', r.jobId, r.success ? 'ok' : r.message)))
            .catch(e => console.error('[PRINT] Addition print error:', e));

          // Also reprint the running Zwischenrechnung so the cashier has the latest total
          if (isDineIn) {
            const zwrData = {
              ...receiptDataWithAddress,
              is_prebill: true,
              payment_method: '',
              paymentStatus: 'pending',
              special_instructions: `*** ZWISCHENRECHNUNG — AKTUALISIERT ***\nTisch ${receiptDataWithAddress.tableNumber || ''} | ${receiptDataWithAddress.items?.length || 0} Artikel`,
            };
            const zwrId = `ZWR-${newOrder.id || orderNumber}-${additionRoundId}`;
            const zwrNumber = `ZWR-T${receiptDataWithAddress.tableNumber || orderNumber}`;
            firestorePrintService.printCashierReceipt(zwrId, zwrNumber, zwrData)
              .then(results => results.forEach(r => console.log('[PRINT] Updated Zwischenrechnung:', r.jobId, r.success ? 'ok' : r.message)))
              .catch(e => console.error('[PRINT] Zwischenrechnung error:', e));
          }
        } else if (isDineIn) {
          console.log('[PRINT] Dine-in order — kitchen print + auto Zwischenrechnung');
          firestorePrintService.printKitchenOrder(
            newOrder.id || orderNumber,
            orderNumber,
            receiptDataWithAddress
          ).then(results => results.forEach(r => console.log('[PRINT] Kitchen job:', r.jobId, r.success ? 'ok' : r.message)))
            .catch(e => console.error('[PRINT] Kitchen print error:', e));

          // Auto-print Zwischenrechnung on Epson for every dine-in order
          // Use stable ID (no timestamp) so idempotency check prevents duplicates
          const zwrData = {
            ...receiptDataWithAddress,
            is_prebill: true,
            payment_method: '',
            paymentStatus: 'pending',
            special_instructions: `*** ZWISCHENRECHNUNG — OFFEN ***\nTisch ${receiptDataWithAddress.tableNumber || ''} | ${receiptDataWithAddress.items?.length || 0} Artikel`,
          };
          const zwrId = `ZWR-${newOrder.id || orderNumber}`;
          const zwrNumber = `ZWR-T${receiptDataWithAddress.tableNumber || orderNumber}`;
          firestorePrintService.printCashierReceipt(zwrId, zwrNumber, zwrData)
            .then(results => results.forEach(r => console.log('[PRINT] Auto-Zwischenrechnung job:', r.jobId, r.success ? 'ok' : r.message)))
            .catch(e => console.error('[PRINT] Zwischenrechnung error:', e));
        } else {
          console.log('[PRINT] Takeaway/delivery order — receipt + kitchen (fire-and-forget)');
          firestorePrintService.printOrder(
            newOrder.id || orderNumber,
            orderNumber,
            receiptDataWithAddress
          ).then(async ({ receipt, kitchen }) => {
            receipt.forEach(r => console.log('[PRINT] Receipt job:', r.jobId, r.success ? 'ok' : r.message));
            kitchen.forEach(r => console.log('[PRINT] Kitchen job:', r.jobId, r.success ? 'ok' : r.message));
            // Mark order as auto-printed so the external-order listener doesn't re-fire it.
            try {
              const { updateDoc, doc: fsDoc } = await import('../../services/firestoreCompat');
              if (newOrder?.id) {
                await updateDoc(fsDoc(db, 'orders', newOrder.id), {
                  printed_auto: true,
                  printed_auto_at: new Date(),
                });
              }
            } catch (e) {
              console.warn('[PRINT] Could not set printed_auto flag:', e);
            }
          }).catch(e => console.error('[PRINT] Print error:', e));
        }
      }

      // For dine-in: update table context with customer name BEFORE closing the dialog
      // so that the next checkout opens with the customer name pre-filled
      if (checkoutData.order_type === 'dine_in' && tableContext) {
        const customerNameForTable = (checkoutData.customer_name && checkoutData.customer_name !== 'Laufkunde')
          ? checkoutData.customer_name
          : tableCustomerName || '';
        if (customerNameForTable) {
          // Update local state first (synchronous - available for next dialog open)
          const activeOrderId = newOrder.id || '';
          const activeOrderNum = orderNumber;
          setTableContext((prev: any) => prev ? { ...prev, customer_name: customerNameForTable, active_order_id: activeOrderId, active_order_number: activeOrderNum } : prev);
          setTableCustomerName(customerNameForTable);
          console.log('✅ [CHECKOUT] Updated table context with customer name:', customerNameForTable, 'order:', activeOrderNum);
          // Then update Firestore (async - for persistence when navigating away)
          try {
            const { doc: firestoreDoc, setDoc } = await import('../../services/firestoreCompat');
            const { db: firestoreDb } = await import('../../services/firebaseConfig');
            const tableContextRef = firestoreDoc(firestoreDb, 'table_context', 'current');
            await setDoc(tableContextRef, {
              selectedTable: { ...tableContext, customer_name: customerNameForTable, active_order_id: activeOrderId, active_order_number: activeOrderNum },
              updated_at: new Date().toISOString()
            }, { merge: true });
          } catch (ctxError) {
            console.warn('⚠️ [CHECKOUT] Failed to persist table context:', ctxError);
          }
        }
      }

      setCart([]);
      setCheckoutDialogOpen(false);

      // CRITICAL: Refresh table active orders so the "ACTIVE ORDER" section updates immediately
      if (orderData.order_type === 'dine_in' && orderData.table_number) {
        console.log('🔄 [CHECKOUT] Triggering table orders refresh after successful checkout');
        // Small delay to let Firestore write propagate before re-fetching
        setTimeout(() => setTableOrdersRefreshCount(c => c + 1), 800);
      }
    } catch (error) {
      console.error("❌ [CHECKOUT] Failed to create order:", error);
      alert(t('dashboard.create_order_failed'));
      throw error;
    }
  };

  const getEmojiIcon = (categoryName: string) => {
    const iconMap: { [key: string]: string } = {
      'Sushi Rolls': '🍽️',
      'Nigiri': '🍙',
      'Sashimi': '🐟',
      'Beverages': '🍵',
      'Desserts': '🍰',
      'Salads': '🥗',
      'Appetizers': '🥢'
    };
    return iconMap[categoryName] || '🍱';
  };

  // Generate HTML preview for cashier receipt matching the design
  const generateCashierReceiptHTML = (receiptData: any) => {
    const logoUrl = '/saheeh_logo_w.svg';
    console.log('🖼️ [LOGO DEBUG] Cashier Receipt - Logo URL:', logoUrl);
    console.log('🖼️ [LOGO DEBUG] Cashier Receipt - Receipt Data:', receiptData);

    // Calculate VAT correctly based on order type and items
    // Rules: Takeaway/Delivery = 7%, Dine-in = 19%, Drinks always = 19%
    // CRITICAL: Log order_type to debug VAT calculation issues
    const orderType = receiptData.order_type || 'dine_in';
    console.log('🧾 [VAT DEBUG] generateCashierReceiptHTML - Order Type:', {
      receiptData_order_type: receiptData.order_type,
      final_orderType: orderType,
      receiptData_keys: Object.keys(receiptData),
      full_receiptData: JSON.stringify(receiptData, null, 2)
    });

    let basePrice7 = 0;
    let basePrice19 = 0;
    let tax7 = 0;
    let tax19 = 0;

    (receiptData.items || []).forEach((item: any) => {
      const itemTotal = item.price * item.quantity;
      const categoryName = (item.category_name || '').toLowerCase();
      const itemNameLower = (item.name || '').toLowerCase();

      // CRITICAL: Check if item is a drink (always 19% VAT in Germany - TSE rule)
      // German TSE rules: ALL drinks = 19% VAT regardless of order type (dine-in, takeaway, delivery)
      // and regardless of payment method (cash, card, PayPal)
      const isDrink = categoryName.includes('drink') ||
        categoryName.includes('getränk') ||
        categoryName.includes('beverage') ||
        categoryName.includes('getränke') ||
        itemNameLower.includes('cola') ||
        itemNameLower.includes('coca') ||
        itemNameLower.includes('pepsi') ||
        itemNameLower.includes('sprite') ||
        itemNameLower.includes('fanta') ||
        itemNameLower.includes('wasser') ||
        itemNameLower.includes('water') ||
        itemNameLower.includes('saft') ||
        itemNameLower.includes('juice') ||
        itemNameLower.includes('limo') ||
        itemNameLower.includes('softdrink') ||
        itemNameLower.includes('bier') ||
        itemNameLower.includes('beer') ||
        itemNameLower.includes('wein') ||
        itemNameLower.includes('wine') ||
        itemNameLower.includes('kaffee') ||
        itemNameLower.includes('coffee') ||
        itemNameLower.includes('tee') ||
        itemNameLower.includes('tea');

      // CRITICAL: ALWAYS recalculate VAT based on order_type and drink detection
      // Do NOT use stored tax_rate - it may be incorrect if order_type changed or was stored wrong
      // CRITICAL GERMAN TSE RULES:
      // - Drinks: ALWAYS 19% VAT (regardless of order type: dine-in, takeaway, delivery)
      // - Drinks: ALWAYS 19% VAT (regardless of payment method: cash, card, PayPal)
      // - Food (Takeaway/Delivery): 7% VAT
      // - Food (Dine-in): 19% VAT
      let taxRate: number;
      if (isDrink) {
        taxRate = 19; // CRITICAL: Drinks ALWAYS 19% VAT in Germany (TSE rule)
        console.log(`💰 [QR RECEIPT] ${item.name}: DRINK → 19% VAT (German TSE rule)`);
      } else {
        // Food items: based on order type
        taxRate = (orderType === 'takeaway' || orderType === 'delivery') ? 7 : 19;
        console.log(`💰 [QR RECEIPT] ${item.name}: FOOD (${orderType}) → ${taxRate}% VAT`);
      }

      // Verify stored tax_rate matches calculated rate (for debugging)
      if (item.tax_rate !== undefined && item.tax_rate !== null) {
        const storedRate = parseFloat(item.tax_rate);
        if (storedRate !== taxRate) {
          console.warn(`⚠️ [VAT] Item "${item.name}" has stored tax_rate ${storedRate}% but calculated ${taxRate}% based on order_type ${orderType} - using calculated rate`);
        }
      }

      // Calculate base price from price_with_tax
      const basePrice = itemTotal / (1 + taxRate / 100);
      const itemTax = itemTotal - basePrice;

      if (taxRate === 7) {
        basePrice7 += basePrice;
        tax7 += itemTax;
      } else {
        basePrice19 += basePrice;
        tax19 += itemTax;
      }
    });

    const netto = basePrice7 + basePrice19;
    const mwst = tax7 + tax19;

    // Determine VAT label - Match example receipt format: "MwSt 19.0%"
    let mwstLabel: string;
    if (tax7 > 0 && tax19 > 0) {
      mwstLabel = 'MwSt (7.0%/19.0%)';
    } else if (tax7 > 0) {
      mwstLabel = 'MwSt 7.0%';
    } else {
      mwstLabel = 'MwSt 19.0%';
    }

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Kassenbeleg - ${receiptData.orderNumber}</title>
        <style>
          @media print {
            body { margin: 0; padding: 0; }
            .no-print { display: none !important; }
            .receipt { box-shadow: none; }
          }
          body {
            font-family: 'Courier New', monospace;
            background: #f5f5f5;
            padding: 20px;
            margin: 0;
          }
          .receipt {
            max-width: 300px;
            margin: 0 auto;
            background: white;
            padding: 20px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          }
          .logo-section {
            text-align: center;
            border-bottom: 2px solid #000;
            padding-bottom: 15px;
            margin-bottom: 15px;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            width: 100%;
          }
          .logo-section img {
            height: 60px;
            max-width: 200px;
            width: auto;
            margin-bottom: 8px;
            display: block;
            margin-left: auto;
            margin-right: auto;
            object-fit: contain;
            align-self: center;
          }
          .logo-section img[src=""],
          .logo-section img:not([src]) {
            display: none;
          }
          .company-info {
            text-align: center !important;
            font-size: 12px;
            line-height: 1.4;
            margin-bottom: 10px;
          }
          .company-info > div:first-child {
            text-align: center !important;
            width: 100%;
          }
          .divider {
            border-top: 1px dashed #000;
            margin: 10px 0;
          }
          .customer-info {
            font-size: 11pt;
            line-height: 1.5;
            margin-bottom: 12px;
            text-align: left !important;
            padding-left: 0;
          }
          .customer-info > div {
            font-size: 11pt;
            font-weight: bold;
            margin-bottom: 4px;
            text-align: left !important;
          }
          .delivery-header {
            text-align: center;
            font-weight: bold;
            margin: 10px 0;
            font-size: 12px;
          }
          .invoice-title {
            text-align: center;
            font-weight: bold;
            font-size: 16px;
            margin: 10px 0;
          }
          .order-details {
            font-size: 12px;
            line-height: 1.4;
            margin-bottom: 10px;
            text-align: left;
          }
          .order-details strong {
            font-weight: bold;
          }
          .items-section {
            margin: 15px 0;
          }
          .item-row {
            font-size: 14pt;
            line-height: 1.5;
            margin-bottom: 4px;
            font-family: 'Courier New', monospace;
            font-weight: bold;
            white-space: pre;
          }
          .summary {
            font-size: 12px;
            line-height: 1.4;
            margin-top: 15px;
          }
          .summary-row {
            font-family: 'Courier New', monospace;
            white-space: pre;
            margin-bottom: 3px;
            font-size: 10pt;
            line-height: 1.5;
          }
          .summary-row.total {
            font-weight: bold;
            font-size: 11pt;
          }
          .payment-info {
            font-size: 12px;
            margin-top: 10px;
            line-height: 1.4;
            font-family: 'Courier New', monospace;
            white-space: pre;
          }
          .payment-status {
            text-align: center;
            font-weight: bold;
            font-size: 12px;
            margin-top: 10px;
          }
          .preview-controls {
            background: #1976D2;
            color: white;
            padding: 15px;
            text-align: center;
            margin-bottom: 20px;
            border-radius: 4px;
          }
          .preview-controls button {
            background: white;
            color: #1976D2;
            border: none;
            padding: 10px 20px;
            margin: 0 5px;
            border-radius: 4px;
            cursor: pointer;
            font-weight: bold;
          }
          .preview-controls button:hover {
            background: #f0f0f0;
          }
        </style>
      </head>
      <body>
        <div class="receipt">
          <div class="preview-controls no-print">
            <h3 style="margin: 0 0 10px 0; color: white;">Kassenbeleg - ${receiptData.orderNumber}</h3>
            <button onclick="window.print()">🖨️ Drucken</button>
            <button onclick="window.close()">❌ Schließen</button>
          </div>
          
          <div class="logo-section">
            <img 
              src="${logoUrl}" 
              alt="SAHEEH Logo" 
              id="cashier-logo"
              onerror="
                console.error('❌ [LOGO DEBUG] Cashier Receipt - Logo failed to load!');
                console.error('❌ [LOGO DEBUG] Image src:', this.src);
                console.error('❌ [LOGO DEBUG] Image naturalWidth:', this.naturalWidth);
                console.error('❌ [LOGO DEBUG] Image naturalHeight:', this.naturalHeight);
                console.error('❌ [LOGO DEBUG] Image complete:', this.complete);
                console.error('❌ [LOGO DEBUG] Image display style:', window.getComputedStyle(this).display);
                this.style.display='none';
                document.getElementById('logo-debug-cashier').innerHTML = '❌ Logo failed to load - Check console for details';
                document.getElementById('logo-debug-cashier').style.display = 'block';
                document.getElementById('logo-debug-cashier').style.color = 'red';
              " 
              onload="
                console.log('✅ [LOGO DEBUG] Cashier Receipt - Logo loaded successfully!');
                console.log('✅ [LOGO DEBUG] Image src:', this.src);
                console.log('✅ [LOGO DEBUG] Image naturalWidth:', this.naturalWidth);
                console.log('✅ [LOGO DEBUG] Image naturalHeight:', this.naturalHeight);
                console.log('✅ [LOGO DEBUG] Image complete:', this.complete);
                console.log('✅ [LOGO DEBUG] Image display style:', window.getComputedStyle(this).display);
                console.log('✅ [LOGO DEBUG] Image computed width:', window.getComputedStyle(this).width);
                console.log('✅ [LOGO DEBUG] Image computed height:', window.getComputedStyle(this).height);
                document.getElementById('logo-debug-cashier').innerHTML = '✅ Logo loaded successfully!';
                document.getElementById('logo-debug-cashier').style.display = 'block';
                document.getElementById('logo-debug-cashier').style.color = 'green';
              "
              style="
                height: auto;
                max-width: 150px;
                width: auto;
                display: block;
                margin: 0 auto 8px auto;
                object-fit: contain;
              "
            >
          </div>
          
          <!-- SPACE (2x) after logo -->
          <div style="margin-bottom: 20px;"></div>
          
          <!-- Business Information - ALL LINES CENTERED (matching ESC/POS output) -->
          <div class="company-info" style="margin-bottom: 30px; text-align: center;">
            <div style="text-align: center; width: 100%;">Saheeh UG (haftungsbeschränkt)</div>
            <div style="text-align: center; width: 100%;">Saheeh Hürth</div>
            <div style="text-align: center; width: 100%;">Sudetenstraße 60</div>
            <div style="text-align: center; width: 100%;">50354 Hürth</div>
          </div>
          
          <!-- SPACE (3x) after business info -->
          <div style="margin-bottom: 30px;"></div>
          
          <!-- SPACE before divider -->
          <div style="margin-bottom: 10px;"></div>
          
          <!-- DIVIDER -->
          <div class="divider"></div>
          
          <!-- Customer Name, Address and Phone - LEFT ALIGNED, LARGER FONT (matching physical printer) -->
          <div class="customer-info" style="text-align: left !important; margin-top: 10px; margin-bottom: 12px; font-size: 11pt; font-weight: bold;">
            ${(() => {
              // If no name or address, show order type label:
              // - Takeaway → "Abholung"
              // - Delivery → "Lieferung"
              // - Dine In → "Bestellung"
              let customerName = receiptData.customerName;
              if (!customerName && !receiptData.customerAddress) {
                if (receiptData.order_type === 'takeaway') {
                  customerName = 'Abholung';
                } else if (receiptData.order_type === 'delivery') {
                  customerName = 'Lieferung';
                } else {
                  customerName = 'Bestellung'; // Dine In order without name/address
                }
              } else if (!customerName) {
                customerName = 'Bestellung'; // Has address but no name (for dine_in)
              }
              return customerName ? `<div style="text-align: left !important; width: 100%; margin-bottom: 6px; font-weight: bold;">${customerName}</div>` : '';
            })()}
            ${receiptData.customerAddress ? `<div style="text-align: left !important; width: 100%; margin-bottom: 6px; font-weight: bold;">${receiptData.customerAddress}</div>` : ''}
            ${receiptData.customerPhone ? `<div style="text-align: left !important; width: 100%; margin-bottom: 6px; font-weight: bold;">Tel. : ${receiptData.customerPhone}</div>` : ''}
          </div>
          
          <!-- SPACE after customer info -->
          <div style="margin-bottom: 10px;"></div>
          
          <!-- DIVIDER after customer info -->
          <div class="divider"></div>
          
          <!-- SPACE after divider -->
          <div style="margin-bottom: 10px;"></div>
          
          <!-- Portal - LEFT ALIGNED, LARGER FONT -->
          <div class="order-details" style="text-align: left !important; margin-bottom: 12px; font-size: 10pt;">
            <div style="text-align: left !important;">Portal: ${receiptData.portal || 'takeaway.com'}</div>
          </div>
          
          <!-- SPACE after portal -->
          <div style="margin-bottom: 10px;"></div>
          
          <!-- Items Section - SMALLER FONT TO FIT PROPERLY -->
          <div class="items-section" style="margin-bottom: 12px;">
            ${receiptData.items.map((item: any, idx: number) => {
      // Calculate base price and add-ons
      const basePrice = item.base_price || item.price || 0;
      const baseTotal = basePrice * item.quantity;
      const addonsTotal = (item.addons || []).reduce((sum: number, addon: any) => {
        const qty = addon.quantity || 1;
        return sum + ((addon.price || 0) * qty);
      }, 0);
      const itemTotal = item.total || (baseTotal + addonsTotal);
      const itemNumber = item.itemNumber || idx + 1;
      
      const itemName = `${item.quantity} x ${item.name.replace('?', ' ')} #${itemNumber}`;
      const basePriceStr = baseTotal.toFixed(2).replace('.', ',') + ' €';
      const itemTotalStr = itemTotal.toFixed(2).replace('.', ',') + ' €';
      
      let html = `<div style="margin-bottom: 4px;">
        <div class="item-row" style="font-size: 11pt; font-weight: bold; display: flex; justify-content: space-between; align-items: baseline;">
          <span style="text-align: left; flex: 1; padding-right: 5px;">${itemName}</span>
          <span style="text-align: right; font-weight: bold; min-width: 60px; flex-shrink: 0;">${basePriceStr}</span>
        </div>`;
      
      // Show add-ons
      if (item.addons && item.addons.length > 0) {
        item.addons.forEach((addon: any) => {
          const qty = addon.quantity || 1;
          const addonName = addon.name_de || addon.name;
          const addonTotal = (addon.price || 0) * qty;
          const addonTotalStr = `+${addonTotal.toFixed(2).replace('.', ',')} €`;
          html += `
        <div class="addon-row" style="font-size: 10pt; color: #666; display: flex; justify-content: space-between; align-items: baseline; padding-left: 15px;">
          <span style="text-align: left; flex: 1; padding-right: 5px;">+ ${qty > 1 ? `${qty}x ` : ''}${addonName}</span>
          <span style="text-align: right; font-weight: 600; min-width: 60px; flex-shrink: 0;">${addonTotalStr}</span>
        </div>`;
        });
        
        // Show item total if there are add-ons
        html += `
        <div class="item-total-row" style="font-size: 11pt; font-weight: bold; display: flex; justify-content: space-between; align-items: baseline; padding-left: 15px; margin-top: 2px;">
          <span style="text-align: left; flex: 1; padding-right: 5px;">= Gesamt:</span>
          <span style="text-align: right; font-weight: bold; min-width: 60px; flex-shrink: 0;">${itemTotalStr}</span>
        </div>`;
      }
      
      // Show notes if present
      if (item.notes && item.notes.trim() !== '') {
        html += `
        <div style="font-size: 10pt; color: #666; margin-top: 4px; margin-left: 15px; font-style: italic; font-weight: normal;">
          <strong>HINWEIS:</strong> ${item.notes}
        </div>`;
      }
      
      html += `</div>`;
      return html;
    }).join('')}
          </div>
          
          <!-- SPACE after items -->
          <div style="margin-bottom: 10px;"></div>
          
          <!-- Financial Summary - EXACT ORDER AS USER REQUESTED (AFTER Items, BEFORE Payment) -->
          <div class="summary" style="margin-bottom: 10px;">
            ${(() => {
        // CRITICAL: Use calculated totals instead of receiptData values to ensure accuracy
        const calculatedTotal = netto + mwst;
        // Remove Euro symbols to match physical receipt format
        const nettoStr = netto.toFixed(2).replace('.', ',');
        const mwstStr = mwst.toFixed(2).replace('.', ',');
        const totalStr = calculatedTotal.toFixed(2).replace('.', ',');
        const nettoLabel = 'Netto:';
        const nettoValueWithEuro = nettoStr + ' €';
        // Use mwstLabel calculated above (based on order type and items)
        const mwstValueWithEuro = mwstStr + ' €';
        const totalLabel = 'Gesamt brutto:';
        const totalValueWithEuro = totalStr + ' €';
        // CRITICAL: All values must align to the same right column (column 48)
        return `
                <div class="summary-row" style="font-family: 'Courier New', monospace; white-space: pre; font-size: 10pt; display: flex; justify-content: space-between; align-items: baseline;">
                  <span style="text-align: left;">${nettoLabel}</span>
                  <span style="text-align: right; font-weight: bold; min-width: 60px;">${nettoValueWithEuro}</span>
                </div>
                <div class="summary-row" style="font-family: 'Courier New', monospace; white-space: pre; font-size: 10pt; display: flex; justify-content: space-between; align-items: baseline;">
                  <span style="text-align: left;">${mwstLabel}</span>
                  <span style="text-align: right; font-weight: bold; min-width: 60px;">${mwstValueWithEuro}</span>
                </div>
                <div class="summary-row total" style="font-family: 'Courier New', monospace; white-space: pre; font-weight: bold; font-size: 11pt; display: flex; justify-content: space-between; align-items: baseline;">
                  <span style="text-align: left;">${totalLabel}</span>
                  <span style="text-align: right; font-weight: bold; min-width: 60px;">${totalValueWithEuro}</span>
                </div>
              `;
      })()}
          </div>
          
          <!-- SPACE after financial summary -->
          <div style="margin-bottom: 10px;"></div>
          
          <!-- Payment Information - EXACT ORDER AS USER REQUESTED (AFTER Financial, BEFORE Delivery Header) -->
          <div class="payment-info" style="margin-bottom: 10px;">
            ${(() => {
        // CRITICAL: Use calculated total for payment amount
        const calculatedTotal = netto + mwst;
        // Remove Euro symbol and add colon to match physical receipt format
        const paymentAmount = calculatedTotal.toFixed(2).replace('.', ',');
        // CRITICAL: Match physical receipt format - "cash: 10,90 €"
        let paymentLabel: string;
        if (receiptData.paymentMethod === 'card') {
          paymentLabel = 'Kartenzahlung';
        } else if (receiptData.paymentMethod === 'paypal') {
          paymentLabel = 'Onlinezahlung takeaway.com';
        } else {
          paymentLabel = 'Bargeld';
        }
        // Add colon after label to match physical receipt: "cash: 10,90 €"
        const paymentLabelWithColon = paymentLabel + ':';
        const paymentValueWithEuro = paymentAmount + ' €';
        // CRITICAL: Value must align to the same right column as financial summary
        return `<div style="font-family: 'Courier New', monospace; white-space: pre; font-size: 10pt; font-weight: bold; display: flex; justify-content: space-between; align-items: baseline;">
                <span style="text-align: left;">${paymentLabelWithColon}</span>
                <span style="text-align: right; font-weight: bold; min-width: 60px;">${paymentValueWithEuro}</span>
              </div>`;
      })()}
          </div>
          
          <!-- SPACE after payment -->
          <div style="margin-bottom: 10px;"></div>
          
          <!-- DIVIDER before delivery header -->
          <div class="divider"></div>
          
          <!-- Delivery Header - EXACT ORDER AS USER REQUESTED (AFTER Payment, BEFORE RECHNUNG) -->
          <div class="delivery-header" style="margin-top: 10px; margin-bottom: 10px;">---==${(() => {
        const orderType = receiptData.order_type || 'dine_in';
        if (orderType === 'delivery') return 'Lieferung';
        if (orderType === 'takeaway') return 'Abholung';
        return 'Im Restaurant';
      })()}==---</div>
          
          <!-- Invoice Title - EXACT ORDER AS USER REQUESTED (AFTER Delivery Header) -->
          <div class="invoice-title" style="margin-top: 20px; margin-bottom: 15px;">RECHNUNG</div>
          
          <!-- Order Details - EXACT ORDER AS USER REQUESTED (AFTER RECHNUNG) -->
          <div class="order-details" style="margin-top: 20px; margin-bottom: 20px;">
            <div>${receiptData.orderDate} | ${receiptData.orderTime} Uhr</div>
            <div><strong>SOFORT</strong></div>
            ${(() => {
        const orderNum = receiptData.orderNumber || '';
        const rechnungLabel = 'Rechnungsnummer:';
        // CRITICAL: Only show "(Kopie)" if isCopy flag is true
        const rechnungValue = orderNum + (receiptData.isCopy ? ' (Kopie)' : '');
        const kundeLabel = receiptData.customerNumber ? 'Kundennummer:' : '';
        const kasseLabel = 'Kasse';
        const kasseValue = '1';
        // CRITICAL: All values must align to the same right column (matching financial summary)
        return `
                <div style="font-family: 'Courier New', monospace; white-space: pre; font-size: 9pt; display: flex; justify-content: space-between; align-items: baseline;">
                  <span style="text-align: left; flex: 1;">${rechnungLabel}</span>
                  <span style="text-align: right; font-weight: bold; min-width: 80px; flex-shrink: 0;">${rechnungValue}</span>
                </div>
                ${receiptData.customerNumber ? `<div style="font-family: 'Courier New', monospace; white-space: pre; font-size: 9pt; display: flex; justify-content: space-between; align-items: baseline;">
                  <span style="text-align: left; flex: 1;">${kundeLabel}</span>
                  <span style="text-align: right; font-weight: bold; min-width: 80px; flex-shrink: 0;">${receiptData.customerNumber}</span>
                </div>` : ''}
                <div style="font-family: 'Courier New', monospace; white-space: pre; font-size: 9pt; display: flex; justify-content: space-between; align-items: baseline;">
                  <span style="text-align: left; flex: 1;">${kasseLabel}</span>
                  <span style="text-align: right; font-weight: bold; min-width: 80px; flex-shrink: 0;">${kasseValue}</span>
                </div>
              `;
      })()}
          </div>
          
          <!-- DIVIDER after order details -->
          <div class="divider"></div>
          
          <!-- Payment Status - EXACT ORDER AS USER REQUESTED (AFTER Order Details, at the very end) -->
          <div class="payment-status" style="margin-top: 10px; margin-bottom: 10px;">${receiptData.paymentStatus === 'completed' ? 'BEREITS GEZAHLT' : 'ZAHLUNG AUSSTEHEND'}</div>
          
          ${(() => {
        // Add PayPal QR Code only if payment is PayPal and approval URL is available
        // CRITICAL: Card payments don't use QR code (processed via card swipe machine at POS)
        // NOTE: User's example doesn't show QR code, so we'll add it after BEREITS GEZAHLT if needed
        if (receiptData.paymentMethod === 'paypal' && receiptData.paypalApprovalUrl) {
          const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(receiptData.paypalApprovalUrl)}`;
          return `
                <div class="divider"></div>
                <div style="text-align: center; margin: 15px 0;">
                  <div style="font-weight: bold; margin-bottom: 10px; font-size: 12px;">PayPal QR-Code:</div>
                  <img src="${qrCodeUrl}" alt="PayPal QR Code" style="max-width: 200px; height: auto; border: 2px solid #000; padding: 10px; background: white;">
                  <div style="font-size: 10px; margin-top: 8px; color: #666;">Scannen zum Bezahlen</div>
                </div>
                <div class="divider"></div>
              `;
        }
        return '';
      })()}
        </div>
      </body>
      </html>
    `;
  };

  // @ts-ignore - kept for potential future use
  const __generateKitchenOrderHTML = (kitchenData: any, kitchenName: string) => { void kitchenData; void kitchenName;
    const logoUrl = '/saheeh_logo_w.svg';

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Küchenbon - ${kitchenData.orderNumber}</title>
        <style>
          @media print {
            body { margin: 0; padding: 0; }
            .no-print { display: none !important; }
            .receipt { box-shadow: none; }
          }
          body {
            font-family: Arial, sans-serif;
            background: #f5f5f5;
            padding: 20px;
            margin: 0;
          }
          .receipt {
            max-width: 300px;
            margin: 0 auto;
            background: white;
            padding: 20px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          }
          .logo-section {
            text-align: center;
            border-bottom: 2px solid #000;
            padding-bottom: 15px;
            margin-bottom: 15px;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            width: 100%;
          }
          .logo-section img {
            height: 60px;
            max-width: 200px;
            width: auto;
            margin-bottom: 8px;
            display: block;
            margin-left: auto;
            margin-right: auto;
            object-fit: contain;
            align-self: center;
          }
          .logo-section img[src=""],
          .logo-section img:not([src]) {
            display: none;
          }
          .company-info {
            text-align: center;
            font-size: 12px;
            line-height: 1.4;
            margin-bottom: 10px;
          }
          .divider {
            border-top: 1px dashed #000;
            margin: 10px 0;
          }
          .kitchen-title {
            text-align: center;
            font-weight: bold;
            font-size: 18px;
            margin: 15px 0;
            text-transform: uppercase;
          }
          .order-header {
            text-align: center;
            font-size: 12px;
            margin-bottom: 15px;
          }
          .order-header strong {
            font-weight: bold;
            font-size: 14px;
          }
          .customer-info {
            font-size: 11px;
            line-height: 1.5;
            margin-bottom: 10px;
          }
          .items-section {
            margin: 15px 0;
          }
          .item-row {
            font-size: 12px;
            line-height: 1.6;
            margin-bottom: 8px;
            padding: 5px 0;
            border-bottom: 1px dotted #ccc;
          }
          .item-quantity {
            font-weight: bold;
            font-size: 14px;
          }
          .preview-controls {
            background: #d32f2f;
            color: white;
            padding: 15px;
            text-align: center;
            margin-bottom: 20px;
            border-radius: 4px;
          }
          .preview-controls button {
            background: white;
            color: #d32f2f;
            border: none;
            padding: 10px 20px;
            margin: 0 5px;
            border-radius: 4px;
            cursor: pointer;
            font-weight: bold;
          }
          .preview-controls button:hover {
            background: #f0f0f0;
          }
        </style>
      </head>
      <body>
        <div class="receipt">
          <div class="preview-controls no-print">
            <h3 style="margin: 0 0 10px 0; color: white;">Küchenbon - ${kitchenName}</h3>
            <button onclick="window.print()">🖨️ Drucken</button>
            <button onclick="window.close()">❌ Schließen</button>
          </div>
          
          <!-- LOGO -->
          <div class="logo-section">
            <img 
              src="${logoUrl}" 
              alt="SAHEEH Logo" 
              id="kitchen-logo"
              onerror="
                console.error('❌ [LOGO DEBUG] Kitchen Order - Logo failed to load!');
                console.error('❌ [LOGO DEBUG] Image src:', this.src);
                console.error('❌ [LOGO DEBUG] Image naturalWidth:', this.naturalWidth);
                console.error('❌ [LOGO DEBUG] Image naturalHeight:', this.naturalHeight);
                console.error('❌ [LOGO DEBUG] Image complete:', this.complete);
                console.error('❌ [LOGO DEBUG] Image display style:', window.getComputedStyle(this).display);
                this.style.display='none';
                document.getElementById('logo-debug-kitchen').innerHTML = '❌ Logo failed to load - Check console for details';
                document.getElementById('logo-debug-kitchen').style.display = 'block';
                document.getElementById('logo-debug-kitchen').style.color = 'red';
              " 
              onload="
                console.log('✅ [LOGO DEBUG] Kitchen Order - Logo loaded successfully!');
                console.log('✅ [LOGO DEBUG] Image src:', this.src);
                console.log('✅ [LOGO DEBUG] Image naturalWidth:', this.naturalWidth);
                console.log('✅ [LOGO DEBUG] Image naturalHeight:', this.naturalHeight);
                console.log('✅ [LOGO DEBUG] Image complete:', this.complete);
                console.log('✅ [LOGO DEBUG] Image display style:', window.getComputedStyle(this).display);
                console.log('✅ [LOGO DEBUG] Image computed width:', window.getComputedStyle(this).width);
                console.log('✅ [LOGO DEBUG] Image computed height:', window.getComputedStyle(this).height);
                document.getElementById('logo-debug-kitchen').innerHTML = '✅ Logo loaded successfully!';
                document.getElementById('logo-debug-kitchen').style.display = 'block';
                document.getElementById('logo-debug-kitchen').style.color = 'green';
              "
              style="
                height: auto;
                max-width: 150px;
                width: auto;
                display: block;
                margin: 0 auto 8px auto;
                object-fit: contain;
              "
            >
          </div>
          
          <!-- SPACE (3x) -->
          <div style="margin-bottom: 30px;"></div>
          
          <!-- DIVIDER -->
          <div class="divider"></div>
          
          <!-- ITEMS SECTION - 10pt font size (reduced from 24px) -->
          <div class="items-section" style="margin: 20px 0;">
            ${kitchenData.items.map((item: any, idx: number) => {
              const itemNumber = item.itemNumber || idx + 1;
              let html = `
              <div class="item-row" style="font-size: 12pt; font-weight: bold; line-height: 1.6; margin-bottom: 12px; padding-bottom: 8px; border-bottom: 1px dotted #ccc;">
                <div style="font-size: 14pt; font-weight: bold; margin-bottom: 4px;">
                  ${item.quantity} x ${item.name.replace('?', ' ')} #${itemNumber}
                </div>`;
              
              // Show add-ons clearly for chef
              if (item.addons && item.addons.length > 0) {
                html += `
                <div style="margin-left: 15px; margin-top: 6px; margin-bottom: 4px;">
                  <div style="font-size: 10pt; font-weight: bold; color: #d32f2f; margin-bottom: 4px;">ZUSÄTZE:</div>`;
                item.addons.forEach((addon: any) => {
                  const qty = addon.quantity || 1;
                  const addonName = language === 'ar' ? (addon.name_de || addon.name) : addon.name;
                  html += `
                  <div style="font-size: 10pt; color: #333; margin-left: 10px; margin-bottom: 2px;">
                    + ${qty > 1 ? `${qty}x ` : ''}${addonName}
                  </div>`;
                });
                html += `</div>`;
              }
              
              // Show notes
              if (item.notes) {
                html += `
                <div style="font-size: 10pt; color: #666; margin-top: 4px; margin-left: 15px; font-style: italic; font-weight: normal;">
                  <strong>HINWEIS:</strong> ${item.notes}
                </div>`;
              }
              
              html += `</div>`;
              return html;
            }).join('')}
          </div>
          
          <!-- NO DIVIDERS AFTER ITEMS (removed as requested) -->
          
          <!-- KÜCHENBON -->
          <div class="kitchen-title">KÜCHENBON</div>
          
          <!-- DIVIDER -->
          <div class="divider"></div>
          
          <!-- Lieferung Section -->
          <div class="order-header">
            <div>Lieferung</div>
            <div>${kitchenData.orderDate} | ${kitchenData.orderTime}</div>
            <div><strong>SOFORT</strong></div>
            <div><strong>Rechnungsnummer: ${kitchenData.orderNumber}</strong></div>
          </div>
          
          <!-- DIVIDER -->
          <div class="divider"></div>
          
          <!-- Portal and Additional Info -->
          <div class="order-details" style="font-size: 11px; margin-bottom: 10px;">
            <div>Portal takeaway.com</div>
            <div>Es existieren weitere Positionen für diese</div>
            <div>Bestellung</div>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  // Save PDF for testing (development/testing mode)
  const savePDFForTesting = (htmlContent: string, title: string) => {
    const printWindow = window.open('', '_blank', 'width=600,height=800');
    if (printWindow) {
      printWindow.document.title = title;
      printWindow.document.write(htmlContent);
      printWindow.document.close();

      // Wait for content to load, then trigger print dialog (user can save as PDF)
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
        console.log(`📄 [TEST MODE] PDF save dialog opened for: ${title}`);
        console.log(`📄 [TEST MODE] Select "Save as PDF" to save the receipt`);
      }, 500);
    }
  };

  // Drag handlers for draft banner
  const handleBannerMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.button !== 0) return; // Only handle left mouse button
    // Don't start dragging if clicking on buttons
    if ((e.target as HTMLElement).closest('button')) return;

    const bannerBox = e.currentTarget.closest('[data-draft-banner]') as HTMLElement;
    if (!bannerBox) return;

    const rect = bannerBox.getBoundingClientRect();
    setBannerDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });

    // Initialize position if not set
    if (bannerPosition.x === 0 && bannerPosition.y === 80) {
      const centerX = (window.innerWidth - rect.width) / 2;
      setBannerPosition({ x: centerX, y: 80 });
    }

    setIsDraggingBanner(true);
  };

  useEffect(() => {
    if (!isDraggingBanner) return;

    const handleMouseMove = (e: MouseEvent) => {
      const bannerBox = document.querySelector('[data-draft-banner]') as HTMLElement;
      if (!bannerBox) return;

      const rect = bannerBox.getBoundingClientRect();
      const newX = e.clientX - bannerDragOffset.x;
      const newY = e.clientY - bannerDragOffset.y;

      // Keep banner within viewport bounds
      const bannerWidth = rect.width || 600;
      const bannerHeight = rect.height || 100;
      const maxX = window.innerWidth - bannerWidth;
      const maxY = window.innerHeight - bannerHeight;

      setBannerPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY))
      });
    };

    const handleMouseUp = () => {
      setIsDraggingBanner(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingBanner, bannerDragOffset]);

  return (
    <>
      {/* Opening-cash prompt — shows once per login if the active shift's
          cash_opening is still 0. Silent no-op otherwise. */}
      <OpenShiftDialog />
      {/* Add-Items mode banner — shown when staff clicked "+ Add Items" on the Orders page.
          Compact single-row header stays pinned to top. The "Show items" list is
          a floating overlay (absolute-positioned) so it does NOT push the dashboard
          content down — previously the expanded list ate vertical space and cut off
          the menu grid / cart total. */}
      {editOrderContext && (
        <Box sx={{
          position: 'sticky', top: 0, zIndex: 1100,
          backgroundColor: 'rgba(0, 200, 150,0.10)',
          borderBottom: '2px solid #00C896',
          display: 'flex', flexDirection: 'column',
        }}>
          <Box sx={{ px: 1.5, py: 1, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography sx={{ fontWeight: 700, color: '#00C896', fontSize: '0.9rem', whiteSpace: 'nowrap' }}>
                {t('add_items.banner_title', { number: editOrderContext.number })}
              </Typography>
              <Box sx={{
                backgroundColor: '#00C896', color: '#1a1d2e', fontWeight: 800,
                fontSize: '0.62rem', px: 0.8, py: 0.15, borderRadius: 0.8, letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}>
                {editOrderContext.type}
              </Box>
              <Typography sx={{ fontSize: '0.72rem', color: '#c8e6c9', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {t('add_items.banner_subtitle', {
                  customer: editOrderContext.customer || t('checkout.walk_in_customer') || 'Customer',
                  count: String(editOrderExistingItems.length),
                  total: editOrderExistingTotal.toFixed(2),
                })}
              </Typography>
            </Box>
            {editOrderExistingItems.length > 0 && (
              <Chip
                label={editOrderDetailsOpen ? t('add_items.hide_items') : t('add_items.show_items')}
                size="small"
                onClick={() => setEditOrderDetailsOpen(v => !v)}
                sx={{ backgroundColor: 'rgba(0, 200, 150,0.25)', color: '#00C896', fontWeight: 700, cursor: 'pointer', height: 26 }}
              />
            )}
            <Chip label={t('add_items.cancel')} size="small"
              onClick={() => {
                try {
                  localStorage.removeItem('saheeh_edit_order_id');
                  localStorage.removeItem('saheeh_edit_order_number');
                  localStorage.removeItem('saheeh_edit_order_customer');
                  localStorage.removeItem('saheeh_edit_order_type');
                } catch {}
                setEditOrderContext(null);
                setEditOrderExistingItems([]);
                setEditOrderExistingTotal(0);
                setEditOrderDetailsOpen(false);
                setCart([]);
              }}
              sx={{ backgroundColor: 'rgba(244,67,54,0.15)', color: '#F44336', fontWeight: 700, cursor: 'pointer', height: 26 }}
            />
          </Box>
          {editOrderDetailsOpen && editOrderExistingItems.length > 0 && (
            <Box sx={{
              position: 'absolute',
              top: '100%',
              left: 0, right: 0,
              zIndex: 1200,
              px: 2, pt: 1, pb: 1.5,
              backgroundColor: '#141726',
              borderBottom: '2px solid #00C896',
              boxShadow: '0 8px 18px rgba(0,0,0,0.45)',
              maxHeight: 280, overflowY: 'auto',
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#00C896', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {t('add_items.already_in_order', { number: editOrderContext.number })}
                </Typography>
                <Chip label={t('add_items.close')} size="small" onClick={() => setEditOrderDetailsOpen(false)}
                  sx={{ backgroundColor: 'rgba(255,255,255,0.06)', color: '#9ca3af', cursor: 'pointer', height: 20, fontSize: '0.62rem' }} />
              </Box>
              {editOrderExistingItems.map((it: any, idx: number) => (
                <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: '#cfd8dc', py: 0.25 }}>
                  <span>{it.quantity || 1}× {it.name || it.menu_item_name || 'Item'}</span>
                  <span>€{((it.price || 0) * (it.quantity || 1)).toFixed(2)}</span>
                </Box>
              ))}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.8, pt: 0.6, borderTop: '1px solid rgba(0, 200, 150,0.25)', fontWeight: 700, color: '#00C896', fontSize: '0.82rem' }}>
                <span>{t('add_items.running_total')}</span>
                <span>€{editOrderExistingTotal.toFixed(2)}</span>
              </Box>
            </Box>
          )}
        </Box>
      )}
      {/* Checkout Drafts Notification Banner */}
      {checkoutDrafts.length > 0 && (
        <Box
          data-draft-banner="true"
          onMouseDown={handleBannerMouseDown}
          sx={{
            position: 'fixed',
            left: bannerPosition.x !== 0 ? `${bannerPosition.x}px` : '50%',
            top: `${bannerPosition.y}px`,
            transform: bannerPosition.x !== 0 ? 'none' : 'translateX(-50%)',
            zIndex: 1200,
            maxWidth: '600px',
            width: '90%',
            backgroundColor: 'rgba(255, 193, 7, 0.15)',
            border: '2px solid #ffc107',
            borderRadius: 2,
            p: 2,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            cursor: isDraggingBanner ? 'grabbing' : 'grab',
            userSelect: 'none',
            transition: isDraggingBanner ? 'none' : 'all 0.1s ease-out',
            '&:active': {
              cursor: 'grabbing'
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, pointerEvents: 'none' }}>
            <DraftIcon sx={{ color: '#856404', fontSize: 28 }} />
            <Box>
              <Typography sx={{ fontWeight: 600, color: '#856404', fontSize: '0.95rem' }}>
                {language === 'ar'
                  ? `${checkoutDrafts.length} Entwurf${checkoutDrafts.length > 1 ? 'e' : ''} verfügbar`
                  : `${checkoutDrafts.length} Draft${checkoutDrafts.length > 1 ? 's' : ''} Available`}
              </Typography>
              <Typography sx={{ fontSize: '0.85rem', color: '#856404', mt: 0.25 }}>
                {language === 'ar'
                  ? 'Klicken Sie, um fortzufahren'
                  : 'Click to resume'}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, pointerEvents: 'auto', alignItems: 'center' }}>
            {checkoutDrafts.slice(0, 3).map((draft, index) => (
              <Box key={draft.id || index} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<ResumeIcon />}
                  onClick={() => {
                    setSelectedCheckoutDraft(draft);
                    setCart(draft.cart_items.map(item => ({
                      ...item,
                      cartId: item.cartId || Date.now(),
                    })));
                    setCheckoutDialogOpen(true);
                  }}
                  sx={{
                    backgroundColor: '#856404',
                    color: '#fff',
                    '&:hover': {
                      backgroundColor: '#6c5200',
                    },
                    textTransform: 'none',
                    fontSize: '0.8rem',
                    px: 1.5,
                    py: 0.5,
                  }}
                >
                  {draft.table_number
                    ? `${t('drafts.table')} ${draft.table_number}`
                    : t('drafts.status_draft')}
                </Button>
                <IconButton
                  size="small"
                  onClick={async (e) => {
                    e.stopPropagation();
                    const draftId = draft.id || draft.draft_id;
                    if (!draftId) {
                      console.error('❌ [DRAFT] Cannot delete draft: missing ID');
                      return;
                    }

                    const confirmMessage = draft.table_number
                      ? t('dashboard.confirm_delete_draft_table', { table: draft.table_number })
                      : t('dashboard.confirm_delete_draft');

                    if (window.confirm(confirmMessage)) {
                      try {
                        await checkoutDraftService.deleteDraft(draftId);
                        console.log('✅ [DRAFT] Draft deleted:', draftId);
                        // Remove from local state
                        setCheckoutDrafts(prev => prev.filter(d => (d.id || d.draft_id) !== draftId));
                      } catch (error) {
                        console.error('❌ [DRAFT] Error deleting draft:', error);
                        alert(t('dashboard.delete_draft_error'));
                      }
                    }
                  }}
                  sx={{
                    color: '#856404',
                    padding: '4px',
                    '&:hover': {
                      backgroundColor: 'rgba(133, 100, 4, 0.1)',
                      color: '#d32f2f',
                    },
                  }}
                >
                  <CloseIcon sx={{ fontSize: '18px' }} />
                </IconButton>
              </Box>
            ))}
            {checkoutDrafts.length > 3 && (
              <Typography sx={{ fontSize: '0.85rem', color: '#856404', alignSelf: 'center', px: 1 }}>
                +{checkoutDrafts.length - 3}
              </Typography>
            )}
          </Box>
        </Box>
      )}

      <Box
        component="main"
        sx={{
          width: '100%',
          backgroundColor: appTheme.theme.background_color || '#1a1d2e',
          display: 'flex',
          flexDirection: 'column',
          overflow: { xs: 'auto', sm: 'hidden' },
          height: { xs: 'auto', sm: '100vh' },
          minHeight: { xs: 'calc(100vh - 56px)' },
          minWidth: 0,
          margin: 0,
          padding: 0,
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {/* Header — compact on tablets */}
        <Box sx={{
          p: { xs: 1, sm: 1, md: 1.5 },
          pl: { xs: 1.5, sm: 2, md: 3 },
          pb: { xs: 0.5, md: 1 },
          borderBottom: '1px solid #3a3d52',
          backgroundColor: appTheme.theme.background_color || appTheme.theme.background_color || '#1a1d2e',
          flexShrink: 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                fontFamily: '"Poppins", sans-serif',
                color: '#e0e0e0',
                marginBottom: { xs: 0, md: 1 },
                fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.75rem' }
              }}
            >
              {t('dashboard.new_order')}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#9ca3af',
                fontFamily: '"Poppins", sans-serif',
                fontSize: { xs: '0.75rem', md: '0.95rem' },
                display: { xs: 'none', md: 'block' }
              }}
            >
              {t('dashboard.select_items')}
            </Typography>
          </Box>
          <LoggedInUserDisplay />
        </Box>

        {/* Revenue bar removed — not needed on Dashboard */}

        {/* Search Bar - Compact Style (Narrow) */}
        <Box sx={{ p: { xs: 0.5, md: 1 }, pl: { xs: 1, md: 2.5 }, pr: { xs: 1, md: 3 }, pb: 0.5, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
          <TextField
            placeholder={t('dashboard.search_placeholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: '#9ca3af', fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: '100%', md: '400px' },
              maxWidth: '100%',
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                backgroundColor: appTheme.theme.background_color || '#1a1d2e',
                height: '40px',
                fontSize: '0.875rem',
                '&:hover fieldset': {
                  borderColor: '#00C896',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#00C896',
                  borderWidth: '1.5px',
                },
              },
            }}
          />
        </Box>

        {/* Content Area with Fixed Layout — responsive for tablets.
            The "+ Adding items" banner mounted above this row takes ~56px
            (one-line header + py padding). Plus the outer container's own
            padding. Measured empirically: 160 leaves a comfortable bottom
            gap so the cart panel's rounded corner + Warenkorb leeren breathe. */}
        <Box sx={{ flexGrow: 1, display: 'flex', overflow: { xs: 'visible', sm: 'hidden' }, gap: { xs: 1, md: 2 }, p: { xs: 1, md: 2 }, pt: { xs: 0.5, md: 1 }, pb: { xs: 4, md: 2 }, minHeight: 0, maxHeight: { xs: 'none', sm: editOrderContext ? 'calc(100vh - 160px)' : 'calc(100vh - 80px)' } }}>
          {/* Menu Section - Fixed Height with Scroll */}
          <Box sx={{
            flexGrow: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            height: '100%'
          }}>
            {/* Category Filters - Redesigned Compact */}
            <Box sx={{
              mb: { xs: 1, md: 2 },
              display: 'flex',
              flexWrap: { xs: 'nowrap', lg: 'wrap' },
              overflowX: { xs: 'auto', lg: 'visible' },
              WebkitOverflowScrolling: 'touch',
              gap: { xs: 0.75, md: 1 },
              flexShrink: 0,
              p: { xs: 0.5, md: 1 },
              pb: { xs: 1, md: 1 },
              // Hide scrollbar but keep scrollable
              '&::-webkit-scrollbar': { height: { xs: '3px', md: '6px' } },
              '&::-webkit-scrollbar-thumb': { background: '#ccc', borderRadius: 3 },
            }}>
              {availableCategories.length > 0 ? availableCategories.map(category => {
                const isSelected = selectedCategory === category.id;
                const categoryColor = appTheme.theme.category_button_color || appTheme.theme.primary_color || '#FF9800';
                // Helper to convert hex to rgba for boxShadow
                const hexToRgba = (hex: string, alpha: number) => {
                  const r = parseInt(hex.slice(1, 3), 16);
                  const g = parseInt(hex.slice(3, 5), 16);
                  const b = parseInt(hex.slice(5, 7), 16);
                  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
                };
                return (
                  <Box
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: { xs: 62, sm: 65, md: 75 },
                      maxWidth: { xs: 70, sm: 72, md: 85 },
                      height: { xs: 56, sm: 58, md: 70 },
                      padding: { xs: '6px 8px', md: '8px 10px' },
                      flexShrink: 0,
                      backgroundColor: appTheme.theme.background_color || '#1a1d2e',
                      border: isSelected
                        ? `2.5px solid ${categoryColor}`
                        : '2px solid #3a3d52',
                      borderRadius: 1.5,
                      cursor: 'pointer',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxShadow: isSelected
                        ? `0 4px 12px ${hexToRgba(categoryColor, 0.25)}`
                        : '0 2px 6px rgba(0, 0, 0, 0.08)',
                      position: 'relative',
                      overflow: 'hidden',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: isSelected
                          ? `0 6px 16px ${hexToRgba(categoryColor, 0.3)}`
                          : `0 4px 12px ${hexToRgba(categoryColor, 0.15)}`,
                        borderColor: categoryColor,
                      },
                      '&:active': {
                        transform: 'translateY(-1px)',
                      },
                      // Selected indicator bar at bottom
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: isSelected ? 3 : 0,
                        backgroundColor: categoryColor,
                        transition: 'height 0.3s ease',
                      }
                    }}
                  >
                    {/* Category Image/Icon */}
                    <Box
                      sx={{
                        width: { xs: 28, sm: 30, md: 36 },
                        height: { xs: 28, sm: 30, md: 36 },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 0.25,
                        borderRadius: 0.75,
                        overflow: 'hidden',
                        backgroundColor: isSelected ? 'rgba(0, 200, 150, 0.15)' : '#1e2139',
                        border: isSelected ? '1.5px solid #00C896' : '1px solid #3a3d52',
                      }}
                    >
                      {category.image_url ? (
                        <Box
                          component="img"
                          src={category.image_url}
                          alt={category.name}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                          onError={(e: any) => {
                            // Fallback to icon if image fails
                            e.target.style.display = 'none';
                            const parent = e.target.parentElement;
                            if (parent && category.icon) {
                              const iconSpan = document.createElement('span');
                              iconSpan.textContent = category.icon;
                              iconSpan.style.fontSize = '22px';
                              parent.appendChild(iconSpan);
                            }
                          }}
                        />
                      ) : (
                        <Typography sx={{ fontSize: '22px', lineHeight: 1 }}>
                          {category.icon || '🍱'}
                        </Typography>
                      )}
                    </Box>

                    {/* Category Name */}
                    <Typography
                      sx={{
                        fontFamily: '"Poppins", sans-serif',
                        fontSize: '0.65rem',
                        fontWeight: isSelected ? 700 : 600,
                        color: isSelected ? '#00C896' : '#9ca3af',
                        textAlign: 'center',
                        lineHeight: 1.1,
                        mt: 0.25,
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {category.name}
                    </Typography>
                  </Box>
                );
              }) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 75,
                    maxWidth: 85,
                    height: 70,
                    padding: '8px 10px',
                    backgroundColor: appTheme.theme.background_color || '#1a1d2e',
                    border: '1px solid #3a3d52',
                    borderRadius: 1.5,
                    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.08)',
                  }}
                >
                  <Typography sx={{ fontSize: '22px', mb: 0.25 }}>🍱</Typography>
                  <Typography
                    sx={{
                      fontFamily: '"Poppins", sans-serif',
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      color: '#d0d0d0',
                    }}
                  >
                    All
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Menu Items Grid - Scrollable with touch support */}
            <Box
              sx={{
                flexGrow: 1,
                overflowY: 'auto',
                overflowX: 'hidden',
                WebkitOverflowScrolling: 'touch',
                minHeight: 0,
                p: { xs: 1, md: 2 },
                backgroundColor: appTheme.theme.background_color || '#1e2139',
                borderRadius: 2,
                '&::-webkit-scrollbar': {
                  width: { xs: '4px', md: '10px' },
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f0f0f0',
                  borderRadius: '5px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#c0c0c0',
                  borderRadius: '5px',
                  '&:hover': {
                    background: '#a0a0a0',
                  },
                },
              }}
            >
              {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                  <Typography sx={{ color: '#9ca3af', fontFamily: '"Poppins", sans-serif' }}>
                    Loading menu items...
                  </Typography>
                </Box>
              ) : filteredProducts.length > 0 ? (
                <>
                <Box sx={{
                  display: { xs: 'flex', lg: 'none' },
                  flexWrap: 'wrap',
                  gap: 0.75,
                  width: '100%',
                }}>
                  {filteredProducts.map((product) => {
                    const price = product.price_with_tax || parseFloat(product.base_price) || 0;
                    const isSoldOut = product.sold_out === true;
                    return (
                      <Box
                        key={`tablet-${product.id}`}
                        onClick={() => handleItemClick(product)}
                        sx={{
                          width: { xs: 'calc(50% - 4px)', sm: 'calc(33.33% - 5px)', md: 'calc(25% - 5px)', lg: 'calc(20% - 5px)' },
                          minWidth: '90px',
                          cursor: isSoldOut ? 'not-allowed' : 'pointer',
                          borderRadius: 2,
                          border: '1.5px solid #3a3d52',
                          overflow: 'hidden',
                          backgroundColor: '#252838',
                          transition: 'all 0.15s ease',
                          touchAction: 'manipulation',
                          opacity: isSoldOut ? 0.6 : 1,
                          position: 'relative',
                          '&:active': isSoldOut ? {} : { transform: 'scale(0.97)', borderColor: '#4caf50' },
                        }}
                      >
                        {/* Sold-out overlay: trendy gradient pill with glow + frosted backdrop */}
                        {isSoldOut && (
                          <Box sx={{
                            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            zIndex: 3, pointerEvents: 'none',
                            background: 'linear-gradient(135deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.35) 100%)',
                            backdropFilter: 'blur(2px)',
                            WebkitBackdropFilter: 'blur(2px)',
                          }}>
                            <Box sx={{
                              transform: 'rotate(-6deg)',
                              background: 'linear-gradient(135deg, #ff4757 0%, #c0392b 100%)',
                              color: '#fff',
                              fontWeight: 900,
                              fontSize: '0.58rem',
                              letterSpacing: '1.5px',
                              px: 1, py: 0.3,
                              borderRadius: '999px',
                              border: '1.5px solid rgba(255,255,255,0.95)',
                              boxShadow: '0 4px 14px rgba(239, 68, 68, 0.55)',
                              fontFamily: '"Poppins", sans-serif',
                              textTransform: 'uppercase',
                              whiteSpace: 'nowrap',
                            }}>
                              {t('menu.sold_out_badge') || 'AUSVERKAUFT'}
                            </Box>
                          </Box>
                        )}
                        {/* Tiny image */}
                        <Box sx={{
                          height: 60,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: '#1e2139',
                          borderBottom: '1px solid #3a3d52',
                          overflow: 'hidden',
                        }}>
                          <MenuImage
                            src={product.image_url}
                            alt={product.name}
                            category={product.category_name}
                            iconSize={24}
                            fallback={<Typography sx={{ fontSize: '24px' }}>{getEmojiIcon(product.category_name || '')}</Typography>}
                          />
                        </Box>
                        {/* Name + Price compact */}
                        <Box sx={{ p: 0.75, textAlign: 'center' }}>
                          <Typography sx={{
                            fontSize: '0.68rem', fontWeight: 600, color: '#e0e0e0', lineHeight: 1.2,
                            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                            overflow: 'hidden', minHeight: '2em',
                          }}>
                            {product.name}
                          </Typography>
                          <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#d32f2f', mt: 0.25 }}>
                            €{price.toFixed(2)}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
                {/* Desktop grid — hidden on tablets */}
                <Grid container spacing={2} sx={{ width: '100%', margin: 0, display: { xs: 'none', lg: 'flex' } }}>
                  {filteredProducts.map((product) => {
                    const isSoldOut = product.sold_out === true;
                    return (
                      <Grid item xs={6} sm={4} md={4} lg={4} key={product.id}>
                        <Card
                          onClick={() => handleItemClick(product)}
                          sx={{
                            border: '1px solid #3a3d52',
                            borderRadius: 2.5,
                            display: 'flex',
                            flexDirection: 'column',
                            backgroundColor: appTheme.theme.background_color || '#1a1d2e',
                            boxShadow: '0 1px 4px rgba(0, 0, 0, 0.06)',
                            transition: 'all 0.2s ease',
                            overflow: 'hidden',
                            height: '100%',
                            cursor: isSoldOut ? 'not-allowed' : 'pointer',
                            position: 'relative',
                            opacity: isSoldOut ? 0.6 : 1,
                            '&:hover': isSoldOut ? {} : {
                              borderColor: '#d32f2f',
                              boxShadow: '0 3px 12px rgba(211, 47, 47, 0.15)',
                              transform: 'translateY(-2px)',
                              // Keep the dark card background on hover. Previous fallback was
                              // '#fffafa' (near-white) which flashed white behind semi-transparent
                              // product images that had their own white studio backgrounds.
                              backgroundColor: appTheme.theme.background_color || '#1a1d2e',
                              '& .card-hover-indicator': {
                                opacity: 1,
                              },
                            },
                            '&:active': isSoldOut ? {} : {
                              transform: 'translateY(0)',
                              boxShadow: '0 1px 4px rgba(211, 47, 47, 0.2)',
                            },
                          }}
                        >
                          {/* Sold-out overlay: frosted image + trendy gradient pill with glow */}
                          {isSoldOut && (
                            <Box sx={{
                              position: 'absolute',
                              top: 0, left: 0, right: 0, bottom: 0,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              zIndex: 3, pointerEvents: 'none',
                              background: 'linear-gradient(135deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.35) 100%)',
                              backdropFilter: 'blur(3px)',
                              WebkitBackdropFilter: 'blur(3px)',
                            }}>
                              <Box sx={{
                                transform: 'rotate(-6deg)',
                                background: 'linear-gradient(135deg, #ff4757 0%, #c0392b 100%)',
                                color: '#fff',
                                fontWeight: 900,
                                fontSize: { xs: '0.75rem', md: '1rem' },
                                letterSpacing: { xs: '2px', md: '3px' },
                                px: { xs: 1.5, md: 2.5 },
                                py: { xs: 0.6, md: 0.85 },
                                borderRadius: '999px',
                                border: '2px solid rgba(255,255,255,0.95)',
                                boxShadow: '0 8px 28px rgba(239, 68, 68, 0.55), 0 0 0 4px rgba(239, 68, 68, 0.15)',
                                fontFamily: '"Poppins", sans-serif',
                                textTransform: 'uppercase',
                                whiteSpace: 'nowrap',
                              }}>
                                {t('menu.sold_out_badge') || 'AUSVERKAUFT'}
                              </Box>
                            </Box>
                          )}
                          {/* Product Image or Emoji Icon */}
                          <Box
                            sx={{
                              height: { xs: 80, sm: 90, md: 120 },
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: '#1e2139',
                              borderBottom: '1px solid #3a3d52',
                              flexShrink: 0,
                              overflow: 'hidden',
                              position: 'relative',
                            }}
                          >
                            <MenuImage
                              src={product.image_url}
                              alt={product.name}
                              category={product.category_name}
                              iconSize={40}
                              fallback={<Typography sx={{ fontSize: '40px' }}>{getEmojiIcon(product.category_name)}</Typography>}
                            />
                          </Box>

                          <CardContent sx={{
                            p: { xs: 1, sm: 1.25, md: 2 },
                            display: 'flex',
                            flexDirection: 'column',
                            flexGrow: 1,
                            overflow: 'hidden',
                            paddingBottom: { xs: '8px !important', md: '16px' },
                          }}>
                            {/* Item Name */}
                            <Typography
                              variant="h6"
                              sx={{
                                color: '#e0e0e0',
                                fontWeight: 700,
                                mb: { xs: 0.5, md: 1 },
                                fontSize: { xs: '0.78rem', sm: '0.85rem', md: '1rem' },
                                fontFamily: '"Poppins", sans-serif',
                                lineHeight: 1.2,
                                flexShrink: 0,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                minHeight: { xs: '1.9em', md: '2.4em' },
                              }}
                            >
                              {product.name}
                            </Typography>

                            {/* Description - Hidden on small tablets, compact on larger */}
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#9ca3af',
                                mb: { xs: 0.5, md: 1.5 },
                                fontSize: { xs: '0.68rem', md: '0.8rem' },
                                fontFamily: '"Poppins", sans-serif',
                                lineHeight: 1.3,
                                maxHeight: { xs: '1.3em', md: '2.8em' },
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: { xs: 'none', md: '-webkit-box' },
                                WebkitLineClamp: { xs: 1, md: 2 },
                                WebkitBoxOrient: 'vertical',
                                flexShrink: 0,
                              }}
                            >
                              {product.description || 'Fresh and delicious'}
                            </Typography>

                            {/* Price and Category - Bottom Section */}
                            <Box sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              mt: 'auto',
                              pt: 1.5,
                              borderTop: '1px solid #f0f0f0',
                              flexShrink: 0,
                            }}>
                              <Chip
                                label={product.category_name}
                                size="small"
                                sx={{
                                  backgroundColor: '#e3f2fd',
                                  color: '#1976d2',
                                  fontWeight: 500,
                                  fontSize: '0.7rem',
                                  height: 22,
                                  '& .MuiChip-label': {
                                    padding: '0 8px',
                                  },
                                }}
                              />
                              <Typography
                                variant="h6"
                                sx={{
                                  color: '#d32f2f',
                                  fontWeight: 700,
                                  fontSize: { xs: '0.9rem', md: '1.15rem' },
                                  fontFamily: '"Poppins", sans-serif',
                                }}
                              >
                                €{(product.price_with_tax || parseFloat(product.base_price) || 0).toFixed(2)}
                              </Typography>
                            </Box>

                            {/* Click indicator - red bar at bottom on hover */}
                            <Box
                              sx={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                height: 3,
                                backgroundColor: '#d32f2f',
                                opacity: 0,
                                transition: 'opacity 0.2s ease',
                              }}
                              className="card-hover-indicator"
                            />
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
                </>
              ) : (
                <Box sx={{
                  textAlign: 'center',
                  py: 8,
                  px: 4,
                  width: '100%'
                }}>
                  <Typography
                    variant="h5"
                    sx={{
                      mb: 2,
                      color: '#9ca3af',
                      fontFamily: '"Poppins", sans-serif',
                      fontWeight: 600
                    }}
                  >
                    {products.length === 0
                      ? t('dashboard.no_menu_items')
                      : `${t('dashboard.no_items_in')} "${availableCategories.find(c => c.id === selectedCategory)?.name || selectedCategory}"`}
                  </Typography>
                  {products.length === 0 ? (
                    <Typography
                      variant="body1"
                      sx={{
                        color: '#999999',
                        fontFamily: '"Poppins", sans-serif'
                      }}
                    >
                      Please upload menu items to Firebase Firestore
                    </Typography>
                  ) : (
                    <Button
                      variant="outlined"
                      onClick={() => setSelectedCategory('all')}
                      sx={{
                        mt: 2,
                        borderColor: '#1976d2',
                        color: '#1976d2',
                        '&:hover': {
                          borderColor: '#1565c0',
                          backgroundColor: '#e3f2fd'
                        }
                      }}
                    >
                      {t('dashboard.show_all_items')}
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          </Box>

          {/* Order Summary - Hidden on mobile, shown in Drawer */}
          <Box sx={{
            flex: { xs: '0 0 260px', sm: '0 0 260px', md: '0 0 320px', lg: '0 0 420px' },
            minWidth: { xs: 260, sm: 260, md: 320, lg: 420 },
            maxWidth: { xs: 260, sm: 260, md: 320, lg: 420 },
            display: { xs: 'none', sm: 'flex' },
            backgroundColor: appTheme.theme.background_color || '#1a1d2e',
            flexDirection: 'column',
            overflow: 'hidden',
            maxHeight: {
              xs: editOrderContext ? 'calc(100vh - 210px)' : 'calc(100vh - 136px)',
              sm: editOrderContext ? 'calc(100vh - 160px)' : 'calc(100vh - 80px)',
            },
            borderRadius: 2,
            border: '1px solid #3a3d52',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
            fontFamily: '"Poppins", sans-serif'
          }}>
            {/* Header with Logo */}
            <Box sx={{
              p: { xs: 1, md: 1.5 },
              borderBottom: '1px solid #3a3d52',
              backgroundColor: appTheme.theme.background_color || '#1a1d2e',
              borderRadius: '8px 8px 0 0',
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              flexShrink: 0
            }}>
              <Box
                component="img"
                src="/saheeh_logo_w.svg"
                alt="Saheeh"
                sx={{
                  width: { xs: 24, md: 32 },
                  height: { xs: 24, md: 32 },
                  objectFit: 'contain',
                  flexShrink: 0
                }}
                onError={(e: any) => {
                  // Fallback to emoji if logo fails to load
                  e.target.style.display = 'none';
                  const parent = e.target.parentElement;
                  if (parent) {
                    const emoji = document.createElement('span');
                    emoji.textContent = '🍱';
                    emoji.style.fontSize = '1.5rem';
                    parent.insertBefore(emoji, e.target);
                  }
                }}
              />
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: '#e0e0e0',
                  fontFamily: '"Poppins", sans-serif',
                  fontSize: { xs: '0.9rem', md: '1.2rem' }
                }}
              >
                {t('dashboard.current_order')}
              </Typography>
            </Box>

            {/* Active Table Banner - Shows when staff is working on a dine-in table */}
            {tableContext && (() => {
              // Live table data from real-time Firestore listener — most reliable source
              const liveTableData = occupiedTables.find(t => t.number === tableContext.number);

              const pendingOrders = tableActiveOrders.filter(o => {
                const s = (o.status || '').toLowerCase();
                return s !== 'completed' && s !== 'cancelled';
              });

              // Use live table data as primary source; fall back to queried orders
              const liveOrderNumber = (liveTableData as any)?.active_order_number || '';
              const liveTotal: number = (liveTableData as any)?.tab_total || (liveTableData as any)?.total_amount || 0;
              const liveItemCount: number = (liveTableData as any)?.tab_item_count || (liveTableData as any)?.item_count || 0;

              const activeOrderNumber = liveOrderNumber || (pendingOrders.length > 0 ? pendingOrders[0].order_number : null);
              const totalItems = liveItemCount || pendingOrders.reduce((sum: number, o: any) => sum + (o.items?.length || 0), 0);
              const totalAmount = liveTotal || pendingOrders.reduce((sum: number, o: any) => sum + (o.total || 0), 0);

              // Table is "live-occupied" if occupiedTables still lists it as occupied
              const isLiveOccupied = liveTableData?.status === 'occupied';
              const hasOrderData = totalAmount > 0 || totalItems > 0 || !!activeOrderNumber;

              const isSplitPaid = Boolean((liveTableData as any)?.split_payment_complete);
              const splitPeople: { name: string; total: number; payment_method: string }[] = (liveTableData as any)?.split_people || [];
              const splitTotal: number = (liveTableData as any)?.split_total || totalAmount;

              return (
              <Box sx={{
                px: { xs: 1, md: 1.5 },
                py: { xs: 0.5, md: 1 },
                backgroundColor: isSplitPaid ? 'rgba(147, 51, 234, 0.12)' : 'rgba(76, 175, 80, 0.15)',
                borderBottom: `1px solid ${isSplitPaid ? '#d6bcfa' : '#a5d6a7'}`,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.25,
                cursor: (tableActiveOrders.length > 0 || isLiveOccupied) ? 'pointer' : 'default',
              }}
              onClick={() => { if ((tableActiveOrders.length > 0 || isLiveOccupied) && !isSplitPaid) setTableDetailsExpanded(!tableDetailsExpanded); }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#2e7d32', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      🪑 {t('dashboard.table')} {tableContext.display_number || tableContext.number}
                    </Typography>
                    {isSplitPaid && (
                      <Box component="span" sx={{
                        px: 1, py: 0.25, borderRadius: '20px',
                        backgroundColor: '#2e7d32', color: '#ffffff !important',
                        fontSize: '0.65rem', fontWeight: 800, letterSpacing: '0.05em',
                      }}>
                        ✅ SPLIT PAID
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {(tableActiveOrders.length > 0 || isLiveOccupied) && !isSplitPaid && (
                      <Typography variant="caption" sx={{ color: '#2e7d32', fontSize: '0.75rem', userSelect: 'none' }}>
                        {tableDetailsExpanded ? '▼' : '▲'}
                      </Typography>
                    )}
                    <Typography
                      variant="caption"
                      sx={{ cursor: 'pointer', color: '#d32f2f', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
                      onClick={async (e) => {
                        e.stopPropagation();
                        setTableContext(null); setTableActiveOrders([]); setTableCustomerName('');
                        try {
                          const { doc, deleteDoc } = await import('../../services/firestoreCompat');
                          const { db } = await import('../../services/firebaseConfig');
                          await deleteDoc(doc(db, 'table_context', 'current'));
                        } catch (e) { console.warn('Could not clear table context from Firestore:', e); }
                      }}
                    >
                      ✕ {t('dashboard.clear_table')}
                    </Typography>
                  </Box>
                </Box>

                {/* Split-paid: show per-person breakdown */}
                {isSplitPaid && splitPeople.length > 0 && (
                  <Box sx={{ mt: 0.5 }}>
                    {splitPeople.map((p, i) => (
                      <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.25 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#553c9a', fontSize: '0.72rem' }}>
                          {p.name}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Box component="span" sx={{
                            px: 0.75, py: 0.1, borderRadius: '10px',
                            backgroundColor: p.payment_method === 'cash' ? '#c6f6d5' : p.payment_method === 'card' ? '#bee3f8' : '#fefcbf',
                            color: p.payment_method === 'cash' ? '#276749' : p.payment_method === 'card' ? '#2b6cb0' : '#975a16',
                            fontSize: '0.6rem', fontWeight: 700,
                          }}>
                            {p.payment_method}
                          </Box>
                          <Typography variant="caption" sx={{ fontWeight: 800, color: '#553c9a', fontSize: '0.72rem', minWidth: 48, textAlign: 'right' }}>
                            €{p.total.toFixed(2)}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5, pt: 0.5, borderTop: '1px solid #d6bcfa' }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#6b46c1', fontSize: '0.72rem' }}>
                        Total Paid
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: '#6b46c1', fontSize: '0.72rem' }}>
                        €{splitTotal.toFixed(2)}
                      </Typography>
                    </Box>
                    <Typography variant="caption" sx={{ display: 'block', color: '#9f7aea', fontSize: '0.65rem', mt: 0.5, fontStyle: 'italic' }}>
                      Go to Restaurant → Release Table to free this table
                    </Typography>
                  </Box>
                )}

                {/* Normal: show items count + Close Tab button
                    Show whenever: there are pending orders from query, OR live table is occupied with data */}
                {!isSplitPaid && (pendingOrders.length > 0 || (isLiveOccupied && hasOrderData)) && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" sx={{ color: '#555' }}>
                      {totalItems > 0 && <>{totalItems} {t('dashboard.items_ordered')} · </>}
                      €{totalAmount.toFixed(2)}
                      {activeOrderNumber && <span style={{ color: '#2e7d32', fontFamily: 'monospace', fontWeight: 700, marginLeft: 4 }}>#{activeOrderNumber}</span>}
                    </Typography>
                    <Box
                      component="button"
                      onClick={(e: any) => {
                        e.stopPropagation();
                        navigate('/restaurant', { state: { autoCloseTab: tableContext.number } });
                      }}
                      sx={{
                        background: 'linear-gradient(135deg, #e65100 0%, #bf360c 100%)',
                        color: '#fff', border: 'none', borderRadius: '6px',
                        px: 1, py: 0.4, fontSize: '0.68rem', fontWeight: 700,
                        cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
                        '&:hover': { opacity: 0.9 }
                      }}
                    >
                      🧾 Close Tab
                    </Box>
                  </Box>
                )}

                {/* Stale state: all orders completed AND table is no longer live-occupied — show Release Table */}
                {/* Guard with !isLiveOccupied so this never appears alongside the "Close Tab" row */}
                {!isSplitPaid && !isLiveOccupied && pendingOrders.length === 0 && tableActiveOrders.some((o: any) => o._isCompleted) && (() => {
                  const lastOrder = tableActiveOrders.find((o: any) => o._isCompleted);
                  const lastTotal = lastOrder?.total || 0;
                  const lastOrderNum = lastOrder?.order_number || '';
                  return (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" sx={{ color: '#888', fontStyle: 'italic' }}>
                        Paid · €{lastTotal.toFixed(2)}
                        {lastOrderNum && <span style={{ fontFamily: 'monospace', fontWeight: 700, marginLeft: 4, color: '#555' }}>#{lastOrderNum}</span>}
                      </Typography>
                      <Box
                        component="button"
                        onClick={async (e: any) => {
                          e.stopPropagation();
                          try {
                            const liveTable = occupiedTables.find((t: any) => t.number === tableContext.number);
                            if (liveTable && liveTable.id) {
                              await firebaseTableService.updateTableStatus(liveTable.id, 'available');
                            }
                            setTableContext(null);
                            setTableActiveOrders([]);
                            setTableCustomerName('');
                            const { doc: fsDoc, deleteDoc: fsDelete } = await import('../../services/firestoreCompat');
                            const { db: fsDb } = await import('../../services/firebaseConfig');
                            await fsDelete(fsDoc(fsDb, 'table_context', 'current'));
                          } catch (err) {
                            console.error('[DASHBOARD] Release table failed:', err);
                          }
                        }}
                        sx={{
                          background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                          color: '#fff', border: 'none', borderRadius: '6px',
                          px: 1, py: 0.4, fontSize: '0.68rem', fontWeight: 700,
                          cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
                          '&:hover': { opacity: 0.9 }
                        }}
                      >
                        🔓 Release Table
                      </Box>
                    </Box>
                  );
                })()}
              </Box>
              );
            })()}

            {/* Occupied Tables Quick Select - Shows when no table is selected AND
                we're not in "+ Add Items" edit mode for a pickup/takeaway order
                (those tables are irrelevant when editing a pickup — they just clutter the cart). */}
            {!tableContext && !editOrderContext && occupiedTables.length > 0 && (
              <Box sx={{
                px: 1.5, py: 1,
                backgroundColor: 'rgba(255, 193, 7, 0.12)',
                borderBottom: '1px solid #ffe082',
              }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#f57f17', textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.5px', mb: 0.5, display: 'block' }}>
                  {t('dashboard.active_tables') || 'Active Tables'}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {occupiedTables.map(table => (
                    <Chip
                      key={`${table.area}-${table.number}`}
                      label={`T${table.number}${table.customer_name && table.customer_name !== 'Laufkunde' && table.customer_name !== 'Walk-in Customer' && !table.customer_name.startsWith('Dine-In') ? ` · ${table.customer_name}` : ''}`}
                      size="small"
                      onClick={async () => {
                        const ctx: any = {
                          number: table.number,
                          display_number: table.number,
                          type: 'dine_in',
                          area: table.area || 'indoor',
                          start_time: table.start_time || new Date().toISOString(),
                        };
                        if (table.id) ctx.id = String(table.id);
                        if (table.customer_name) ctx.customer_name = table.customer_name;
                        // Only copy active_order fields if the order is NOT completed/cancelled
                        // This prevents reusing a paid order when adding new items to the same table
                        if (table.active_order_id) {
                          try {
                            const { doc: checkDoc, getDoc: checkGetDoc } = await import('../../services/firestoreCompat');
                            const { db: checkDb } = await import('../../services/firebaseConfig');
                            const checkSnap = await checkGetDoc(checkDoc(checkDb, 'orders', table.active_order_id));
                            if (checkSnap.exists()) {
                              const checkStatus = (checkSnap.data().status || '').toLowerCase();
                              if (checkStatus !== 'completed' && checkStatus !== 'cancelled') {
                                ctx.active_order_id = table.active_order_id;
                                ctx.active_order_number = table.active_order_number;
                              }
                            }
                          } catch { /* ignore — will create new order */ }
                        }
                        if ((table as any).split_payment_complete) ctx.split_payment_complete = true;
                        setTableContext(ctx);
                        // Save to Firestore so page refresh remembers
                        try {
                          const { doc: fsDoc, setDoc: fsSetDoc } = await import('../../services/firestoreCompat');
                          const { db: fsDb } = await import('../../services/firebaseConfig');
                          await fsSetDoc(fsDoc(fsDb, 'table_context', 'current'), {
                            selectedTable: ctx,
                            updated_at: new Date().toISOString()
                          }, { merge: true });
                        } catch (e) { /* ignore */ }
                      }}
                      sx={{
                        fontWeight: 600, fontSize: '0.75rem', height: 28,
                        backgroundColor: 'rgba(255, 152, 0, 0.12)', color: '#e65100',
                        border: '1px solid #ffcc80',
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: '#ffe0b2' }
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Items List - Scrollable, shows at least 4-5 items */}
            <Box sx={{
              flex: '1 1 auto',
              overflowY: 'auto',
              overflowX: 'hidden',
              WebkitOverflowScrolling: 'touch',
              p: { xs: 1, md: 1.5 },
              backgroundColor: appTheme.theme.background_color || '#1a1d2e',
              minHeight: 0,
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f0f0f0',
                borderRadius: '5px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#c0c0c0',
                borderRadius: '5px',
                '&:hover': {
                  background: '#a0a0a0',
                },
              },
            }}>
              {/* Active Table Orders - Items currently on this table */}
              {tableDetailsExpanded && tableContext && (() => {
                // Use tableActiveOrders directly — already filtered during loading
                const activeOrders = tableActiveOrders;
                if (activeOrders.length === 0) {
                  // Fallback: if live table data shows items but query returned empty, trigger a retry
                  const liveData = occupiedTables.find(t => t.number === tableContext.number);
                  if (liveData && ((liveData as any).tab_item_count > 0 || (liveData as any).active_order_id)) {
                    // Show a retry button instead of nothing
                    return (
                      <Box sx={{ mb: 2, p: 2, borderRadius: 2, border: '1px dashed #a5d6a7', textAlign: 'center', backgroundColor: 'rgba(76, 175, 80, 0.08)' }}>
                        <Typography variant="caption" sx={{ color: '#2e7d32', fontWeight: 600 }}>
                          {t('dashboard.loading_order_items') || 'Bestellartikel werden geladen...'}
                        </Typography>
                        <Box
                          component="button"
                          onClick={() => setTableOrdersRefreshCount(c => c + 1)}
                          sx={{
                            display: 'block', mx: 'auto', mt: 1, px: 2, py: 0.5,
                            border: '1px solid #4caf50', borderRadius: 1, backgroundColor: '#252838',
                            color: '#2e7d32', fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer',
                            '&:hover': { backgroundColor: 'rgba(76, 175, 80, 0.15)' },
                          }}
                        >
                          🔄 {t('dashboard.retry') || 'Aktualisieren'}
                        </Box>
                      </Box>
                    );
                  }
                  return null;
                }
                return (
                <Box sx={{ mb: { xs: 1, md: 2 }, borderRadius: 1.5, border: '1px solid #a5d6a7', overflow: 'hidden' }}>
                  <Box sx={{ px: { xs: 1, md: 1.5 }, py: { xs: 0.5, md: 1 }, backgroundColor: 'rgba(76, 175, 80, 0.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#2e7d32', textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: { xs: '0.6rem', md: '0.7rem' } }}>
                      {t('dashboard.active_order_items')}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#2e7d32', fontFamily: 'monospace', fontWeight: 600, fontSize: { xs: '0.6rem', md: '0.65rem' } }}>
                      {activeOrders[0]?.order_number || ''}
                    </Typography>
                  </Box>
                  <Box sx={{ px: { xs: 0.5, md: 1 }, py: 0.25, backgroundColor: 'rgba(76, 175, 80, 0.08)' }}>
                    {activeOrders.map((order, oIdx) => (
                      <Box key={order.id || oIdx}>
                        {(order.items || []).map((item: any, iIdx: number) => {
                          const itemName = item.menu_item_name || item.name || 'Item';
                          const itemQty = item.quantity || 1;
                          const itemPrice = item.price || item.unit_price || 0;
                          const lineTotal = itemPrice * itemQty;
                          const itemLabel = `${itemQty}× ${itemName}`;
                          return (
                            <Box key={iIdx} sx={{
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                              py: { xs: 0.25, md: 0.5 }, px: 0.5, my: 0.25,
                              backgroundColor: '#252838', borderRadius: 1, border: '1px solid rgba(76, 175, 80, 0.3)',
                            }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, flex: 1 }}>
                                <Typography sx={{ fontSize: { xs: '0.6rem', md: '0.7rem' }, color: '#43a047' }}>✓</Typography>
                                <Typography variant="body2" noWrap sx={{ fontWeight: 600, color: '#1b5e20', fontSize: { xs: '0.7rem', md: '0.8rem' }, lineHeight: 1.2 }}>
                                  {itemLabel}
                                </Typography>
                              </Box>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#1b5e20', fontSize: { xs: '0.7rem', md: '0.8rem' }, flexShrink: 0, ml: 0.5 }}>
                                €{lineTotal.toFixed(2)}
                              </Typography>
                              {/* Remove (void) button — opens audit-logged confirmation dialog */}
                              <Box
                                role="button"
                                aria-label={`Remove ${itemLabel}`}
                                title={`Remove ${itemLabel}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setVoidItemTarget({
                                    orderId: order.id,
                                    orderNumber: order.order_number,
                                    itemIndex: iIdx,
                                    itemLabel,
                                    itemPrice: lineTotal,
                                  });
                                  setVoidItemReason('');
                                }}
                                sx={{
                                  ml: 0.75,
                                  width: 22,
                                  height: 22,
                                  borderRadius: '50%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: 'pointer',
                                  flexShrink: 0,
                                  color: '#d32f2f',
                                  border: '1px solid rgba(211, 47, 47, 0.35)',
                                  backgroundColor: 'rgba(211, 47, 47, 0.08)',
                                  fontWeight: 800,
                                  fontSize: '0.78rem',
                                  lineHeight: 1,
                                  transition: 'all 0.12s ease',
                                  '&:hover': {
                                    backgroundColor: '#d32f2f',
                                    color: '#fff',
                                    borderColor: '#d32f2f',
                                  },
                                }}
                              >
                                ×
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    ))}
                  </Box>
                  {/* Show discount info if applied */}
                  {activeOrders.some((o: any) => o.discount_amount > 0) && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', px: { xs: 0.75, md: 1.5 }, py: { xs: 0.3, md: 0.5 }, backgroundColor: 'rgba(255, 152, 0, 0.12)', borderTop: '1px solid #ffe0b2' }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#e65100', fontSize: { xs: '0.62rem', md: '0.7rem' } }}>
                        {t('dashboard.discount_amount')} {activeOrders[0]?.discount_reason ? `(${activeOrders[0].discount_reason})` : ''}
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#e65100', fontSize: { xs: '0.62rem', md: '0.7rem' } }}>
                        -€{activeOrders.reduce((sum: number, o: any) => sum + (o.discount_amount || 0), 0).toFixed(2)}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: { xs: 0.75, md: 1.5 }, py: { xs: 0.4, md: 0.75 }, backgroundColor: 'rgba(0, 200, 150, 0.12)', borderTop: '1px solid rgba(0, 200, 150, 0.25)' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#00C896 !important', fontSize: { xs: '0.68rem', md: '0.75rem' } }}>{t('dashboard.total')}</Typography>
                      <Box
                        component="span"
                        onClick={() => {
                          const orderId = activeOrders[0]?.id;
                          if (orderId) {
                            setDiscountOrderId(orderId);
                            const existing = activeOrders[0];
                            if (existing?.discount_amount > 0) {
                              setDiscountType(existing.discount_type || 'percentage');
                              setDiscountValue(String(existing.discount_value || ''));
                              setDiscountReason(existing.discount_reason || '');
                            }
                            setDiscountDialogOpen(true);
                          }
                        }}
                        sx={{
                          fontSize: { xs: '0.55rem', md: '0.65rem' }, fontWeight: 600,
                          color: '#FF9800 !important', cursor: 'pointer', backgroundColor: 'rgba(255, 152, 0, 0.12)',
                          px: 0.8, py: 0.2, borderRadius: 0.8, border: '1px solid rgba(255, 152, 0, 0.3)',
                          '&:hover': { backgroundColor: 'rgba(255, 152, 0, 0.2)' },
                        }}
                      >
                        🏷️ {activeOrders.some((o: any) => o.discount_amount > 0) ? t('dashboard.change_discount') : t('dashboard.apply_discount')}
                      </Box>
                    </Box>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#00C896 !important', fontSize: { xs: '0.68rem', md: '0.75rem' } }}>€{activeOrders.reduce((sum: number, o: any) => sum + (o.total || 0), 0).toFixed(2)}</Typography>
                  </Box>
                </Box>
                );
              })()}

              {/* Previously Served section removed — client only wants active order details */}

              {/* Draft Orders Section - ONLY VISIBLE when there are pending/draft orders */}
              {draftOrders.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontFamily: '"Poppins", sans-serif',
                      fontWeight: 600,
                      color: '#ff9800',
                      mb: 1,
                      fontSize: '0.85rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}
                  >
                    {'📝 '}{t('dashboard.drafts_pending_split')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {draftOrders.map((draft) => (
                      <Box
                        key={draft.id}
                        sx={{
                          border: '2px solid #ff9800',
                          borderRadius: 1.5,
                          backgroundColor: 'rgba(255, 152, 0, 0.12)',
                          p: 1.25,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: '#ffe0b2',
                            borderColor: '#f57c00',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 2px 8px rgba(255, 152, 0, 0.3)'
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box
                            onClick={() => {
                              console.log('🔄 [DASHBOARD] Resuming draft:', draft.id, 'Table:', draft.table_number);
                              setSelectedDraft(draft);
                              setSplitDialogOpen(true);
                            }}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              flex: 1,
                              cursor: 'pointer'
                            }}
                          >
                            <DraftIcon sx={{ color: '#ff9800', fontSize: 20 }} />
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: '"Poppins", sans-serif',
                                fontWeight: 600,
                                color: '#e0e0e0'
                              }}
                            >
                              {t('drafts.table')} {draft.table_number}
                            </Typography>
                            <Chip
                              label={draft.status === 'draft'
                                ? t('drafts.status_draft')
                                : t('drafts.status_pending')}
                              size="small"
                              sx={{
                                height: '18px',
                                fontSize: '0.7rem',
                                backgroundColor: draft.status === 'draft' ? '#3a3d52' : '#ff9800',
                                color: draft.status === 'draft' ? '#9ca3af' : '#fff'
                              }}
                            />
                            <ResumeIcon sx={{ color: '#ff9800', fontSize: 18, ml: 'auto' }} />
                          </Box>
                          <IconButton
                            onClick={async (e) => {
                              e.stopPropagation();
                              const confirmDelete = window.confirm(
                                t('dashboard.confirm_delete_draft_table', { table: draft.table_number })
                              );
                              if (confirmDelete && draft.id) {
                                try {
                                  console.log('🗑️ [DASHBOARD] Deleting draft:', draft.id);
                                  await draftService.cancelDraft(draft.id);
                                  console.log('✅ [DASHBOARD] Draft deleted successfully');
                                } catch (error) {
                                  console.error('❌ [DASHBOARD] Error deleting draft:', error);
                                  alert(t('dashboard.delete_draft_error'));
                                }
                              }
                            }}
                            size="small"
                            sx={{
                              color: '#d32f2f',
                              '&:hover': {
                                backgroundColor: 'rgba(244, 67, 54, 0.12)',
                                color: '#c62828'
                              },
                              ml: 0.5
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <Box
                          onClick={() => {
                            console.log('🔄 [DASHBOARD] Resuming draft:', draft.id, 'Table:', draft.table_number);
                            setSelectedDraft(draft);
                            setSplitDialogOpen(true);
                          }}
                          sx={{ cursor: 'pointer' }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              fontFamily: '"Poppins", sans-serif',
                              color: '#9ca3af',
                              fontSize: '0.75rem'
                            }}
                          >
                            {t('dashboard.step')} {draft.active_step + 1}/4 • {draft.people.length} {t('dashboard.people')} • €{draft.original_order_total.toFixed(2)}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                  <Divider sx={{ my: 1.5, borderColor: '#3a3d52' }} />
                </Box>
              )}

              {cart.length === 0 && draftOrders.length === 0 && tableActiveOrders.length === 0 ? (
                <Box sx={{
                  textAlign: 'center',
                  py: { xs: 2, lg: 8 },
                  color: '#9ca3af',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: '"Poppins", sans-serif',
                      fontWeight: 600,
                      marginBottom: { xs: 0.5, lg: 2 },
                      fontSize: { xs: '0.85rem', lg: '1.2rem' }
                    }}
                  >
                    {t('dashboard.cart_empty')}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontFamily: '"Poppins", sans-serif',
                      fontSize: { xs: '0.75rem', lg: '1rem' },
                      display: { xs: 'none', lg: 'block' }
                    }}
                  >
                    {t('dashboard.add_items_start')}
                  </Typography>
                </Box>
              ) : cart.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {cart.map(item => (
                    <Box
                      key={item.cartId}
                      sx={{
                        border: '1px solid #00C896',
                        borderRadius: 1.5,
                        backgroundColor: appTheme.theme.background_color || '#1a1d2e',
                        p: 1.25,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.75,
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                      }}
                    >
                      {/* Top Row: Image, Name, Price */}
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        {item.image_url ? (
                          <Box
                            component="img"
                            src={item.image_url}
                            alt={item.name}
                            sx={{
                              width: 40,
                              height: 40,
                              objectFit: 'cover',
                              borderRadius: 1,
                              border: '1px solid #3a3d52',
                              flexShrink: 0
                            }}
                            onError={(e: any) => {
                              // Fallback to emoji if image fails to load
                              e.target.style.display = 'none';
                              const parent = e.target.parentElement;
                              if (parent) {
                                const emoji = document.createElement('span');
                                emoji.textContent = '🍱';
                                emoji.style.fontSize = '1.5rem';
                                emoji.style.lineHeight = '1';
                                parent.insertBefore(emoji, e.target);
                              }
                            }}
                          />
                        ) : (
                          <Typography sx={{ fontSize: '1.5rem', lineHeight: 1, flexShrink: 0 }}>
                            {getEmojiIcon(item.category_name || '')}
                          </Typography>
                        )}
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontFamily: '"Poppins", sans-serif',
                              fontWeight: 700,
                              color: '#e0e0e0',
                              fontSize: '0.9rem',
                              lineHeight: 1.2,
                              mb: 0.25
                            }}
                          >
                            {item.name}
                          </Typography>
                        </Box>
                        <Typography
                          sx={{
                            fontFamily: '"Poppins", sans-serif',
                            fontWeight: 700,
                            color: '#e0e0e0',
                            fontSize: '1rem',
                            flexShrink: 0
                          }}
                        >
                          €{(item.price * item.quantity).toFixed(2)}
                        </Typography>
                      </Box>

                      {/* Description - Compact */}
                      {item.description && (
                        <Typography
                          sx={{
                            fontFamily: '"Poppins", sans-serif',
                            color: '#9ca3af',
                            fontSize: '0.75rem',
                            lineHeight: 1.3,
                            pl: 3.5,
                            display: '-webkit-box',
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          {item.description}
                        </Typography>
                      )}

                      {/* Add-ons Display */}
                      {item.addons && item.addons.length > 0 && (
                        <Box sx={{ pl: 3.5, mt: 0.5 }}>
                          <Typography
                            sx={{
                              fontFamily: '"Poppins", sans-serif',
                              color: '#1976D2',
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              mb: 0.5
                            }}
                          >
                            {t('addons.addons_label')}:
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {item.addons.map((addon: any, idx: number) => {
                              const qty = addon.quantity || 1;
                              const displayName = language === 'ar' ? (addon.name_de || addon.name) : addon.name;
                              return (
                                <Chip
                                  key={idx}
                                  label={`${qty > 1 ? `${qty}x ` : ''}${displayName}`}
                                  size="small"
                                  sx={{
                                    height: 18,
                                    fontSize: '0.65rem',
                                    backgroundColor: '#E3F2FD',
                                    color: '#1976D2',
                                    fontWeight: 500,
                                    '& .MuiChip-label': {
                                      px: 0.75
                                    }
                                  }}
                                />
                              );
                            })}
                          </Box>
                        </Box>
                      )}

                      {/* Notes Display */}
                      {item.notes && (
                        <Box sx={{ pl: 3.5, mt: 0.5 }}>
                          <Typography
                            sx={{
                              fontFamily: '"Poppins", sans-serif',
                              color: '#9ca3af',
                              fontSize: '0.7rem',
                              fontStyle: 'italic'
                            }}
                          >
                            📝 {item.notes}
                          </Typography>
                        </Box>
                      )}

                      {/* Bottom Row: Category Tag, Edit Button, and Quantity */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pl: 3.5, mt: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {item.category_name && (
                            <Chip
                              label={item.category_name}
                              size="small"
                              sx={{
                                backgroundColor: '#ff6b35',
                                color: '#ffffff',
                                height: 20,
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                '& .MuiChip-label': {
                                  px: 1
                                }
                              }}
                            />
                          )}
                          
                          {/* Edit Button */}
                          <IconButton
                            onClick={() => {
                              setSelectedProductForAddOns(item);
                              setAddItemModalOpen(true);
                            }}
                            size="small"
                            sx={{
                              color: '#1976D2',
                              border: '1px solid #1976D2',
                              borderRadius: 1,
                              width: 28,
                              height: 28,
                              padding: 0,
                              '&:hover': {
                                backgroundColor: '#E3F2FD',
                                borderColor: '#1565C0',
                              },
                            }}
                            title={t('dashboard.edit_item')}
                          >
                            <Edit sx={{ fontSize: '14px' }} />
                          </IconButton>
                        </Box>

                        {/* Quantity Controls - Smaller */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
                          <IconButton
                            onClick={() => updateQuantity(item.cartId, item.quantity - 1)}
                            size="small"
                            sx={{
                              color: '#e0e0e0',
                              border: '1px solid #00C896',
                              borderRadius: '50%',
                              width: 28,
                              height: 28,
                              backgroundColor: appTheme.theme.background_color || '#1a1d2e',
                              padding: 0,
                              '&:hover': {
                                backgroundColor: appTheme.theme.secondary_color || '#2d3048',
                                borderColor: '#d32f2f',
                                color: '#d32f2f'
                              },
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <Remove sx={{ fontSize: '16px' }} />
                          </IconButton>
                          <Typography
                            sx={{
                              color: '#e0e0e0',
                              fontFamily: '"Poppins", sans-serif',
                              fontWeight: 700,
                              fontSize: '0.95rem',
                              minWidth: 24,
                              textAlign: 'center'
                            }}
                          >
                            {item.quantity}
                          </Typography>
                          <IconButton
                            onClick={() => updateQuantity(item.cartId, item.quantity + 1)}
                            size="small"
                            sx={{
                              color: '#e0e0e0',
                              border: '1px solid #00C896',
                              borderRadius: '50%',
                              width: 28,
                              height: 28,
                              backgroundColor: appTheme.theme.background_color || '#1a1d2e',
                              padding: 0,
                              '&:hover': {
                                backgroundColor: appTheme.theme.secondary_color || '#2d3048',
                                borderColor: '#d32f2f',
                                color: '#d32f2f'
                              },
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <Add sx={{ fontSize: '16px' }} />
                          </IconButton>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : null}
            </Box>

            {/* Order Summary Footer — compact on tablet (up to lg), full on desktop (lg+).
                Extra bottom padding so the "Clear Cart" button isn't cut off by the
                viewport — previously had py: 2 which crowded the bottom border. */}
            <Box sx={{
              borderTop: { xs: '1px solid #3a3d52', lg: '2px solid #3a3d52' },
              px: { xs: 0.5, lg: 2 },
              py: { xs: 0.25, lg: 2 },
              pb: { xs: 1, lg: 3 },
              backgroundColor: appTheme.theme.background_color || '#1a1d2e',
              flexShrink: 0
            }}>
              <Box sx={{ mb: { xs: 0, lg: 2 } }}>
                {/* Subtotal + Tax in single row on tablet, stacked on desktop */}
                <Box sx={{ display: { xs: 'flex', lg: 'none' }, justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ color: '#9ca3af', fontSize: '0.62rem', fontFamily: '"Poppins"' }}>
                    {t('dashboard.subtotal')}: €{cartTotals.subtotal.toFixed(2)}
                  </Typography>
                  <Typography sx={{ color: '#9ca3af', fontSize: '0.62rem', fontFamily: '"Poppins"' }}>
                    {t('dashboard.tax', { rate: cartTotals.tax19 > 0 ? 19 : cartTotals.tax7 > 0 ? 7 : '' })}: €{cartTotals.tax.toFixed(2)}
                  </Typography>
                  <Typography sx={{ color: '#e0e0e0', fontWeight: 700, fontSize: '0.72rem', fontFamily: '"Poppins"' }}>
                    {t('dashboard.total')}: €{cartTotals.total.toFixed(2)}
                  </Typography>
                </Box>
                {/* Desktop: full stacked layout */}
                <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography sx={{ color: '#9ca3af', fontWeight: 500, fontFamily: '"Poppins", sans-serif', fontSize: '1rem' }}>
                      {t('dashboard.subtotal')}
                    </Typography>
                    <Typography sx={{ color: '#e0e0e0', fontWeight: 600, fontFamily: '"Poppins", sans-serif', fontSize: '1rem' }}>
                      €{cartTotals.subtotal.toFixed(2)}
                    </Typography>
                  </Box>
                  {cartTotals.tax7 > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography sx={{ color: '#9ca3af', fontWeight: 500, fontFamily: '"Poppins", sans-serif', fontSize: '0.875rem' }}>
                        {t('dashboard.tax', { rate: 7 })}
                      </Typography>
                      <Typography sx={{ color: '#e0e0e0', fontWeight: 600, fontFamily: '"Poppins", sans-serif', fontSize: '0.875rem' }}>
                        €{cartTotals.tax7.toFixed(2)}
                      </Typography>
                    </Box>
                  )}
                  {cartTotals.tax19 > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography sx={{ color: '#9ca3af', fontWeight: 500, fontFamily: '"Poppins", sans-serif', fontSize: '0.875rem' }}>
                        {t('dashboard.tax', { rate: 19 })}
                      </Typography>
                      <Typography sx={{ color: '#e0e0e0', fontWeight: 600, fontFamily: '"Poppins", sans-serif', fontSize: '0.875rem' }}>
                        €{cartTotals.tax19.toFixed(2)}
                      </Typography>
                    </Box>
                  )}
                  {cartTotals.tax7 === 0 && cartTotals.tax19 === 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography sx={{ color: '#9ca3af', fontWeight: 500, fontFamily: '"Poppins", sans-serif' }}>
                        {t('dashboard.tax', { rate: '' })}
                      </Typography>
                      <Typography sx={{ color: '#e0e0e0', fontWeight: 600, fontFamily: '"Poppins", sans-serif' }}>
                        €{cartTotals.tax.toFixed(2)}
                      </Typography>
                    </Box>
                  )}
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ color: '#e0e0e0', fontWeight: 700, fontSize: '1.2rem', fontFamily: '"Poppins", sans-serif' }}>
                      {t('dashboard.total')}
                    </Typography>
                    <Typography sx={{ color: '#e0e0e0', fontWeight: 700, fontSize: '1.4rem', fontFamily: '"Poppins", sans-serif' }}>
                      €{cartTotals.total.toFixed(2)}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleCheckoutClick}
                disabled={cart.length === 0}
                sx={{
                  backgroundColor: '#d32f2f',
                  '&:hover': {
                    backgroundColor: '#c62828',
                  },
                  '&:disabled': {
                    backgroundColor: '#3a3d52',
                    color: '#9ca3af'
                  },
                  color: '#ffffff',
                  fontWeight: 600,
                  borderRadius: { xs: 1, lg: 2 },
                  py: { xs: 0.3, lg: 1.5 },
                  fontFamily: '"Poppins", sans-serif',
                  mb: { xs: 0.25, lg: 1.5 },
                  fontSize: { xs: '0.68rem', lg: '0.95rem' },
                  minHeight: { xs: 26, lg: 40 },
                  touchAction: 'manipulation',
                  boxShadow: '0 4px 12px rgba(211, 47, 47, 0.3)',
                  transition: 'all 0.3s ease',
                  '&:hover:not(:disabled)': {
                    boxShadow: '0 6px 16px rgba(211, 47, 47, 0.4)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                {t('dashboard.checkout', { count: cart.length })}
              </Button>

              {(() => {
                const _liveTable = occupiedTables.find(t => t.number === tableContext?.number);
                const _splitPaid = Boolean((_liveTable as any)?.split_payment_complete);
                return (
                  <Button
                    fullWidth
                    variant="outlined"
                    size="medium"
                    onClick={handleSplitTableClick}
                    disabled={cart.length === 0 || _splitPaid}
                    startIcon={<TableRestaurant />}
                    sx={{
                      borderColor: _splitPaid ? '#d6bcfa' : '#e0e0e0',
                      borderWidth: 2,
                      color: _splitPaid ? '#9f7aea' : '#333333',
                      '&:hover': {
                        borderColor: _splitPaid ? '#d6bcfa' : '#1976d2',
                        color: _splitPaid ? '#9f7aea' : '#1976d2',
                        backgroundColor: _splitPaid ? 'rgba(139, 92, 246, 0.1)' : '#2d3048',
                      },
                      '&:disabled': {
                        borderColor: _splitPaid ? '#d6bcfa' : '#cccccc',
                        color: _splitPaid ? '#b794f4' : '#cccccc',
                      },
                      borderRadius: { xs: 1, lg: 2 },
                      py: { xs: 0.2, lg: 1.25 },
                      fontFamily: '"Poppins", sans-serif',
                      mb: { xs: 0.25, lg: 1.5 },
                      fontSize: { xs: '0.62rem', lg: '0.9rem' },
                      minHeight: { xs: 24, lg: 36 },
                      touchAction: 'manipulation',
                      transition: 'all 0.3s ease',
                    }}
                  >
                    {_splitPaid
                      ? '✅ Split Already Paid'
                      : t('dashboard.split_table', { count: cart.length })
                    }
                  </Button>
                );
              })()}

              {/* Clear Cart — hidden on tablets when cart is empty to save space */}
              <Button
                fullWidth
                variant="outlined"
                size="medium"
                onClick={() => setCart([])}
                disabled={cart.length === 0}
                sx={{
                  borderColor: '#3a3d52',
                  borderWidth: 2,
                  color: '#9ca3af',
                  '&:hover': {
                    borderColor: '#d32f2f',
                    color: '#d32f2f',
                    backgroundColor: 'rgba(244, 67, 54, 0.12)',
                  },
                  '&:disabled': {
                    borderColor: '#cccccc',
                    color: '#cccccc'
                  },
                  borderRadius: { xs: 1, lg: 2 },
                  py: { xs: 0.2, lg: 1.25 },
                  fontFamily: '"Poppins", sans-serif',
                  fontSize: { xs: '0.6rem', lg: '0.9rem' },
                  minHeight: { xs: 22, lg: 36 },
                  transition: 'all 0.3s ease',
                  display: cart.length === 0 ? { xs: 'none', lg: 'flex' } : 'flex',
                }}
              >
                {t('dashboard.clear_cart')}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Mobile: Floating Cart Button */}
      {isMobile && (
        <Box
          onClick={() => setMobileCartOpen(true)}
          sx={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            zIndex: 1200,
            width: 60,
            height: 60,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #00C896 0%, #00A37A 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 20px rgba(0, 200, 150, 0.4)',
            cursor: 'pointer',
            touchAction: 'manipulation',
          }}
        >
          <Badge
            badgeContent={cart.reduce((sum: number, item: any) => sum + item.quantity, 0)}
            color="error"
            sx={{ '& .MuiBadge-badge': { fontSize: '0.75rem', fontWeight: 700 } }}
          >
            <ShoppingCartIcon sx={{ color: '#0A2540', fontSize: 28 }} />
          </Badge>
        </Box>
      )}

      {/* Mobile: Cart Drawer */}
      {isMobile && (
        <Drawer
          anchor="bottom"
          open={mobileCartOpen}
          onClose={() => setMobileCartOpen(false)}
          PaperProps={{
            sx: {
              backgroundColor: '#1a1d2e',
              borderTopLeftRadius: 20,
              borderTopRightRadius: 20,
              maxHeight: '85vh',
              overflow: 'auto',
            },
          }}
        >
          {/* Drag handle */}
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1.5, pb: 1 }}>
            <Box sx={{ width: 40, height: 4, borderRadius: 2, backgroundColor: '#3a3d52' }} />
          </Box>

          {/* Cart content - reuse the same cart UI */}
          <Box sx={{ px: 2, pb: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  component="img"
                  src="/saheeh_logo_w.svg"
                  alt="Saheeh"
                  sx={{ width: 28, height: 28, objectFit: 'contain' }}
                />
                <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#fff' }}>
                  {t('dashboard.current_order')}
                </Typography>
              </Box>
              <Typography sx={{ color: '#00C896', fontWeight: 700, fontSize: '0.85rem' }}>
                {cart.length} {cart.length === 1 ? 'Artikel' : 'Artikel'}
              </Typography>
            </Box>

            {/* Cart items */}
            {cart.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography sx={{ color: '#6b7280', fontSize: '0.9rem' }}>
                  {t('dashboard.empty_cart')}
                </Typography>
              </Box>
            ) : (
              <>
                {cart.map((item: any) => {
                  const hasCustomisations = !!(
                    (item.selections && Object.keys(item.selections).length > 0) ||
                    (Array.isArray(item.modifier_snapshot) && item.modifier_snapshot.length > 0) ||
                    (Array.isArray(item.addons) && item.addons.length > 0) ||
                    (item.notes && item.notes.trim().length > 0)
                  );
                  const modifierSummary: string[] = [];
                  if (item.modifier_snapshot && item.selections) {
                    item.modifier_snapshot.forEach((g: any) => {
                      const picked: string[] = item.selections[g.id] || [];
                      picked.forEach((optId: string) => {
                        const opt = g.options?.find((o: any) => o.id === optId);
                        if (opt) modifierSummary.push(language === 'ar' ? (opt.name_de || opt.name) : opt.name);
                      });
                    });
                  }
                  if (Array.isArray(item.addons)) {
                    item.addons.forEach((a: any) => {
                      const n = language === 'ar' ? (a.name_de || a.name) : a.name;
                      if (n) modifierSummary.push(`${n}${a.quantity > 1 ? ` ×${a.quantity}` : ''}`);
                    });
                  }
                  return (
                  <Box key={item.cartId} sx={{
                    p: 1.5, mb: 1, borderRadius: 2, border: '1px solid #2a2d3e', backgroundColor: '#252838',
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 600, color: '#fff', fontSize: '0.9rem' }} noWrap>
                          {item.name}
                        </Typography>
                        <Typography sx={{ color: '#00C896', fontSize: '0.8rem', fontWeight: 600 }}>
                          €{(item.price * item.quantity).toFixed(2)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedProductForAddOns(item);
                            setAddItemModalOpen(true);
                            setMobileCartOpen(false);
                          }}
                          sx={{ border: '1px solid #1976D2', width: 32, height: 32, color: '#1976D2' }}
                          title={t('dashboard.edit_item')}
                        >
                          <Edit sx={{ fontSize: 14 }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => {
                            if (item.quantity <= 1) {
                              setCart(prev => prev.filter(c => c.cartId !== item.cartId));
                            } else {
                              setCart(prev => prev.map(c => c.cartId === item.cartId ? { ...c, quantity: c.quantity - 1 } : c));
                            }
                          }}
                          sx={{ border: '1px solid #3a3d52', width: 32, height: 32, color: '#9ca3af' }}
                        >
                          <Remove sx={{ fontSize: 16 }} />
                        </IconButton>
                        <Typography sx={{ fontWeight: 700, color: '#fff', minWidth: 24, textAlign: 'center' }}>
                          {item.quantity}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => setCart(prev => prev.map(c => c.cartId === item.cartId ? { ...c, quantity: c.quantity + 1 } : c))}
                          sx={{ border: '1px solid #00C896', width: 32, height: 32, color: '#00C896', backgroundColor: 'rgba(0, 200, 150,0.1)' }}
                        >
                          <Add sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>
                    </Box>
                    {hasCustomisations && (
                      <Box sx={{ mt: 0.75, pl: 0.5 }}>
                        {modifierSummary.length > 0 && (
                          <Typography sx={{ fontSize: '0.72rem', color: '#9ca3af', lineHeight: 1.4 }}>
                            {modifierSummary.join(' · ')}
                          </Typography>
                        )}
                        {item.notes && (
                          <Typography sx={{ fontSize: '0.72rem', color: '#f59e0b', fontStyle: 'italic', mt: 0.25 }}>
                            {item.notes}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                  );
                })}

                {/* Total */}
                <Box sx={{ mt: 2, p: 2, borderRadius: 2, background: 'rgba(0, 200, 150, 0.08)', border: '1px solid rgba(0, 200, 150, 0.2)' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography sx={{ color: '#9ca3af', fontSize: '0.85rem' }}>{t('checkout.subtotal')}</Typography>
                    <Typography sx={{ color: '#e0e0e0', fontWeight: 600 }}>€{cartTotals.subtotal.toFixed(2)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.1rem' }}>{t('checkout.total')}</Typography>
                    <Typography sx={{ color: '#00C896', fontWeight: 800, fontSize: '1.2rem' }}>
                      €{cartTotals.total.toFixed(2)}
                    </Typography>
                  </Box>
                </Box>

                {/* Checkout button */}
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => { setMobileCartOpen(false); setCheckoutDialogOpen(true); }}
                  disabled={cart.length === 0}
                  sx={{
                    mt: 2,
                    py: 1.5,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #00C896 0%, #00A37A 100%)',
                    color: '#0A2540',
                    fontWeight: 700,
                    fontSize: '1rem',
                    textTransform: 'none',
                    boxShadow: '0 4px 16px rgba(0, 200, 150, 0.3)',
                    '&:hover': { background: 'linear-gradient(135deg, #1AD4A6 0%, #00B387 100%)' },
                  }}
                >
                  {t('dashboard.checkout', { count: cart.length })}
                </Button>

                {/* Clear cart */}
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => { setCart([]); setMobileCartOpen(false); }}
                  sx={{
                    mt: 1,
                    py: 1,
                    borderRadius: 2,
                    borderColor: '#3a3d52',
                    color: '#9ca3af',
                    textTransform: 'none',
                    '&:hover': { borderColor: '#ef4444', color: '#ef4444' },
                  }}
                >
                  {t('dashboard.clear_cart')}
                </Button>
              </>
            )}
          </Box>
        </Drawer>
      )}

      {/* Void-Item Confirmation Dialog (client request 2026-04-29) */}
      {voidItemTarget && (
        <Box sx={{
          position: 'fixed', top: { xs: 56, sm: 0 }, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
        onClick={() => { if (!voidItemBusy) { setVoidItemTarget(null); setVoidItemReason(''); } }}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              backgroundColor: '#252838', borderRadius: 3, p: 3, minWidth: 340, maxWidth: 440,
              boxShadow: '0 8px 32px rgba(0,0,0,0.4)', border: '1px solid #3a3d52',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#fff', fontFamily: '"Poppins", sans-serif' }}>
              Remove item from order?
            </Typography>
            <Typography sx={{ color: '#9ca3af', fontSize: '0.85rem', mb: 2 }}>
              Order <span style={{ color: '#00C896', fontWeight: 700 }}>#{voidItemTarget.orderNumber}</span> · this action is logged in the audit trail.
            </Typography>
            <Box sx={{
              backgroundColor: 'rgba(211, 47, 47, 0.10)', border: '1px solid rgba(211, 47, 47, 0.35)',
              borderRadius: 1.5, p: 1.5, mb: 2,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <Typography sx={{ color: '#fff', fontWeight: 600 }}>{voidItemTarget.itemLabel}</Typography>
              <Typography sx={{ color: '#ff8a80', fontWeight: 700 }}>−€{voidItemTarget.itemPrice.toFixed(2)}</Typography>
            </Box>
            <Typography sx={{ color: '#9ca3af', fontSize: '0.78rem', mb: 0.6 }}>
              Reason (required)
            </Typography>
            <Box
              component="textarea"
              value={voidItemReason}
              onChange={(e: any) => setVoidItemReason(e.target.value)}
              placeholder="e.g. wrong item, customer changed mind, kitchen unavailable…"
              rows={2}
              sx={{
                width: '100%', resize: 'vertical', minHeight: 56,
                backgroundColor: '#1a1d2e', color: '#e0e0e0',
                border: '1px solid #3a3d52', borderRadius: 1.5,
                p: 1, fontFamily: 'inherit', fontSize: '0.85rem',
                outline: 'none',
                '&:focus': { borderColor: '#00C896' },
              }}
            />
            <Box sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'flex-end' }}>
              <Box
                role="button"
                onClick={() => { if (!voidItemBusy) { setVoidItemTarget(null); setVoidItemReason(''); } }}
                sx={{
                  px: 2.2, py: 1, borderRadius: 1.5, cursor: 'pointer',
                  color: '#9ca3af', fontWeight: 600, fontSize: '0.85rem',
                  border: '1px solid #3a3d52',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.04)' },
                }}
              >
                Cancel
              </Box>
              <Box
                role="button"
                onClick={() => { if (!voidItemBusy) handleVoidItem(); }}
                sx={{
                  px: 2.2, py: 1, borderRadius: 1.5, cursor: voidItemBusy ? 'not-allowed' : 'pointer',
                  backgroundColor: voidItemBusy ? '#7a1f1f' : '#d32f2f', color: '#fff',
                  fontWeight: 700, fontSize: '0.85rem',
                  opacity: voidItemBusy ? 0.7 : 1,
                  '&:hover': { backgroundColor: voidItemBusy ? '#7a1f1f' : '#b71c1c' },
                }}
              >
                {voidItemBusy ? 'Removing…' : 'Remove item'}
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {/* Discount Dialog */}
      {discountDialogOpen && (
        <Box sx={{
          position: 'fixed', top: { xs: 56, sm: 0 }, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
        onClick={() => setDiscountDialogOpen(false)}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              backgroundColor: '#252838', borderRadius: 3, p: 3, minWidth: 340, maxWidth: 400,
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, fontFamily: '"Poppins", sans-serif' }}>
              {t('dashboard.discount_title')}
            </Typography>
            {/* Discount type toggle */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Box
                component="button"
                onClick={() => setDiscountType('percentage')}
                sx={{
                  flex: 1, py: 1, border: '2px solid', borderRadius: 1.5, cursor: 'pointer',
                  borderColor: discountType === 'percentage' ? '#4caf50' : '#e0e0e0',
                  backgroundColor: discountType === 'percentage' ? 'rgba(76, 175, 80, 0.15)' : '#252838',
                  fontWeight: 600, fontSize: '0.9rem', fontFamily: '"Poppins", sans-serif',
                  color: discountType === 'percentage' ? '#4caf50' : '#9ca3af',
                }}
              >
                % {t('dashboard.discount_percentage')}
              </Box>
              <Box
                component="button"
                onClick={() => setDiscountType('fixed')}
                sx={{
                  flex: 1, py: 1, border: '2px solid', borderRadius: 1.5, cursor: 'pointer',
                  borderColor: discountType === 'fixed' ? '#4caf50' : '#e0e0e0',
                  backgroundColor: discountType === 'fixed' ? 'rgba(76, 175, 80, 0.15)' : '#252838',
                  fontWeight: 600, fontSize: '0.9rem', fontFamily: '"Poppins", sans-serif',
                  color: discountType === 'fixed' ? '#4caf50' : '#9ca3af',
                }}
              >
                € {t('dashboard.discount_fixed')}
              </Box>
            </Box>
            {/* Discount value input */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#d0d0d0', mb: 0.5, display: 'block' }}>
                {discountType === 'percentage' ? t('dashboard.discount_value_pct') : t('dashboard.discount_value_eur')}
              </Typography>
              <input
                type="number"
                value={discountValue}
                onChange={(e) => setDiscountValue(e.target.value)}
                placeholder={discountType === 'percentage' ? 'z.B. 10' : 'z.B. 5.00'}
                min="0"
                max={discountType === 'percentage' ? '100' : undefined}
                step={discountType === 'percentage' ? '1' : '0.50'}
                style={{
                  width: '100%', padding: '10px 12px', border: '1px solid #3a3d52', borderRadius: 8,
                  fontSize: '1.1rem', fontWeight: 600, fontFamily: '"Poppins", sans-serif',
                  outline: 'none', boxSizing: 'border-box',
                }}
              />
            </Box>
            {/* Quick percentage buttons */}
            {discountType === 'percentage' && (
              <Box sx={{ display: 'flex', gap: 0.5, mb: 2, flexWrap: 'wrap' }}>
                {[5, 10, 15, 20, 25, 50].map(pct => (
                  <Box
                    key={pct}
                    component="button"
                    onClick={() => setDiscountValue(String(pct))}
                    sx={{
                      px: 1.5, py: 0.5, border: '1px solid rgba(76, 175, 80, 0.3)', borderRadius: 1,
                      backgroundColor: discountValue === String(pct) ? '#4caf50' : 'rgba(76, 175, 80, 0.08)',
                      color: discountValue === String(pct) ? '#fff' : '#4caf50',
                      fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer',
                      '&:hover': { backgroundColor: '#a5d6a7' },
                    }}
                  >
                    {pct}%
                  </Box>
                ))}
              </Box>
            )}
            {/* Reason */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#d0d0d0', mb: 0.5, display: 'block' }}>
                {t('dashboard.discount_reason')}
              </Typography>
              <input
                type="text"
                value={discountReason}
                onChange={(e) => setDiscountReason(e.target.value)}
                placeholder={t('dashboard.discount_reason_placeholder')}
                style={{
                  width: '100%', padding: '8px 12px', border: '1px solid #3a3d52', borderRadius: 8,
                  fontSize: '0.9rem', fontFamily: '"Poppins", sans-serif',
                  outline: 'none', boxSizing: 'border-box',
                }}
              />
            </Box>
            {/* Preview */}
            {discountValue && parseFloat(discountValue) > 0 && (() => {
              const order = tableActiveOrders.find((o: any) => o.id === discountOrderId);
              const origTotal = order?.total_before_discount || order?.total || 0;
              const val = parseFloat(discountValue);
              const disc = discountType === 'percentage' ? Math.round(origTotal * val / 100 * 100) / 100 : Math.min(val, origTotal);
              const newTotal = Math.round((origTotal - disc) * 100) / 100;
              return (
                <Box sx={{ mb: 2, p: 1.5, backgroundColor: 'rgba(255, 152, 0, 0.12)', borderRadius: 1.5, border: '1px solid #ffe0b2' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" sx={{ color: '#9ca3af' }}>{t('dashboard.discount_original')}:</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>€{origTotal.toFixed(2)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" sx={{ color: '#e65100' }}>{t('dashboard.discount_amount')}:</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#e65100' }}>-€{disc.toFixed(2)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #ffe0b2', pt: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>{t('dashboard.discount_new_price')}:</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#2e7d32' }}>€{newTotal.toFixed(2)}</Typography>
                  </Box>
                </Box>
              );
            })()}
            {/* Remove discount button — only when discount exists */}
            {(() => {
              const order = tableActiveOrders.find((o: any) => o.id === discountOrderId);
              return order?.discount_amount > 0;
            })() && (
              <Box
                component="button"
                onClick={handleRemoveDiscount}
                sx={{
                  width: '100%', py: 1, mb: 1.5, border: '2px solid #ef5350', borderRadius: 1.5,
                  backgroundColor: '#252838', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer',
                  fontFamily: '"Poppins", sans-serif', color: '#ef5350',
                  '&:hover': { backgroundColor: 'rgba(244, 67, 54, 0.12)' },
                }}
              >
                {t('dashboard.discount_remove')}
              </Box>
            )}
            {/* Buttons */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Box
                component="button"
                onClick={() => { setDiscountDialogOpen(false); setDiscountValue(''); setDiscountReason(''); }}
                sx={{
                  flex: 1, py: 1.2, border: '1px solid #3a3d52', borderRadius: 1.5,
                  backgroundColor: '#252838', fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer',
                  fontFamily: '"Poppins", sans-serif', color: '#9ca3af',
                  '&:hover': { backgroundColor: '#2d3048' },
                }}
              >
                {t('dashboard.discount_cancel')}
              </Box>
              <Box
                component="button"
                onClick={() => handleApplyDiscount()}
                sx={{
                  flex: 1, py: 1.2, border: 'none', borderRadius: 1.5,
                  backgroundColor: '#4caf50', fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer',
                  fontFamily: '"Poppins", sans-serif', color: '#fff',
                  '&:hover': { backgroundColor: '#388e3c' },
                  opacity: (!discountValue || parseFloat(discountValue) <= 0) ? 0.5 : 1,
                }}
              >
                {t('dashboard.discount_apply')}
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {/* F3 Add-on Dialog */}
      {f3DialogOpen && (
        <Box sx={{ position: 'fixed', top: { xs: 56, sm: 0 }, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setF3DialogOpen(false)}>
          <Box onClick={(e) => e.stopPropagation()} sx={{ backgroundColor: '#252838', borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.3)', border: '1px solid #3a3d52', width: 420, p: 0, overflow: 'hidden' }}>
            {/* Header */}
            <Box sx={{ bgcolor: '#ff9800', p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#fff', fontFamily: '"Poppins"' }}>
                F3 — Zusatz zu: {cart.find(i => i.cartId === f3TargetCartId)?.name || ''}
              </Typography>
              <Box onClick={() => setF3DialogOpen(false)} sx={{ cursor: 'pointer', color: '#fff', fontWeight: 700, fontSize: '1.2rem' }}>✕</Box>
            </Box>
            {/* Select target item */}
            {cart.length > 1 && (
              <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
                <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af', mb: 0.5, fontFamily: '"Poppins"' }}>Artikel auswählen:</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {cart.map(item => (
                    <Box key={item.cartId} onClick={() => setF3TargetCartId(item.cartId)}
                      sx={{ px: 1.5, py: 0.5, borderRadius: 2, fontSize: '0.8rem', cursor: 'pointer', fontFamily: '"Poppins"',
                        border: f3TargetCartId === item.cartId ? '2px solid #ff9800' : '1px solid #ddd',
                        backgroundColor: f3TargetCartId === item.cartId ? 'rgba(255, 152, 0, 0.15)' : '#2d3048',
                        fontWeight: f3TargetCartId === item.cartId ? 600 : 400,
                      }}>
                      {item.name}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
            {/* Form */}
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, mb: 0.5, fontFamily: '"Poppins"' }}>Bezeichnung</Typography>
                <input ref={customNameInputRef} value={customItemName} onChange={e => setCustomItemName(e.target.value)}
                  placeholder="z.B. Extra Käse"
                  onKeyDown={e => { if (e.key === 'Enter') handleF3Submit(); if (e.key === 'Escape') setF3DialogOpen(false); }}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #3a3d52', borderRadius: 8, fontSize: '1rem', fontFamily: '"Poppins"', outline: 'none', boxSizing: 'border-box' }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, mb: 0.5, fontFamily: '"Poppins"' }}>Preis (EUR)</Typography>
                <input value={customItemPrice} onChange={e => setCustomItemPrice(e.target.value)} type="number" step="0.01" min="0"
                  placeholder="0.00"
                  onKeyDown={e => { if (e.key === 'Enter') handleF3Submit(); if (e.key === 'Escape') setF3DialogOpen(false); }}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #3a3d52', borderRadius: 8, fontSize: '1rem', fontFamily: '"Poppins"', outline: 'none', boxSizing: 'border-box' }} />
              </Box>
              <Box onClick={handleF3Submit}
                sx={{ mt: 0.5, py: 1.2, textAlign: 'center', borderRadius: 2, backgroundColor: '#ff9800', color: '#fff', fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer', fontFamily: '"Poppins"', '&:hover': { backgroundColor: '#f57c00' } }}>
                Zusatz hinzufügen
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {/* F4 Standalone Custom Item Dialog */}
      {f4DialogOpen && (
        <Box sx={{ position: 'fixed', top: { xs: 56, sm: 0 }, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setF4DialogOpen(false)}>
          <Box onClick={(e) => e.stopPropagation()} sx={{ backgroundColor: '#252838', borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.3)', border: '1px solid #3a3d52', width: 420, p: 0, overflow: 'hidden' }}>
            {/* Header */}
            <Box sx={{ bgcolor: '#2196f3', p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#fff', fontFamily: '"Poppins"' }}>
                F4 — Benutzerdefinierter Artikel
              </Typography>
              <Box onClick={() => setF4DialogOpen(false)} sx={{ cursor: 'pointer', color: '#fff', fontWeight: 700, fontSize: '1.2rem' }}>✕</Box>
            </Box>
            {/* Form */}
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, mb: 0.5, fontFamily: '"Poppins"' }}>Bezeichnung</Typography>
                <input ref={customNameInputRef} value={customItemName} onChange={e => setCustomItemName(e.target.value)}
                  placeholder="z.B. Sonderwunsch Sushi Platte"
                  onKeyDown={e => { if (e.key === 'Enter') handleF4Submit(); if (e.key === 'Escape') setF4DialogOpen(false); }}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #3a3d52', borderRadius: 8, fontSize: '1rem', fontFamily: '"Poppins"', outline: 'none', boxSizing: 'border-box' }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, mb: 0.5, fontFamily: '"Poppins"' }}>Preis (EUR)</Typography>
                <input value={customItemPrice} onChange={e => setCustomItemPrice(e.target.value)} type="number" step="0.01" min="0"
                  placeholder="0.00"
                  onKeyDown={e => { if (e.key === 'Enter') handleF4Submit(); if (e.key === 'Escape') setF4DialogOpen(false); }}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #3a3d52', borderRadius: 8, fontSize: '1rem', fontFamily: '"Poppins"', outline: 'none', boxSizing: 'border-box' }} />
              </Box>
              <Box onClick={handleF4Submit}
                sx={{ mt: 0.5, py: 1.2, textAlign: 'center', borderRadius: 2, backgroundColor: '#2196f3', color: '#fff', fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer', fontFamily: '"Poppins"', '&:hover': { backgroundColor: '#1976d2' } }}>
                Artikel hinzufügen
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {/* Checkout Dialog */}
      <CheckoutDialog
        open={checkoutDialogOpen}
        onClose={() => {
          setCheckoutDialogOpen(false);
          // For dine-in: keep table context so staff can add more items
          // For other types: clear it
          if (!tableContext || tableContext.type !== 'dine_in') {
            setTableContext(null);
          }
          setSelectedCheckoutDraft(null);
        }}
        onCheckout={async (checkoutData) => {
          await handleCheckout(checkoutData);
          // For dine-in: keep table context active (table stays occupied until "Close Tab")
          // For other order types: clear table context
          if (checkoutData.order_type !== 'dine_in') {
            setTableContext(null);
          } else {
            // Reload table orders to show the newly placed order in the table banner
            setTimeout(() => setTableOrdersRefreshCount(c => c + 1), 1500); // Wait for Firestore write to propagate
          }
          setSelectedCheckoutDraft(null);
        }}
        cart={selectedCheckoutDraft ? selectedCheckoutDraft.cart_items.map(item => ({
          ...item,
          cartId: item.cartId || Date.now(),
        })) : cart}
        tableContext={tableContext}
        tableExistingCustomerName={tableCustomerName}
        draftData={selectedCheckoutDraft}
        forcedOrderType={editOrderContext ? (editOrderContext.type as any) : undefined}
        forcedCustomerName={editOrderContext?.customer || undefined}
        onPayPalOrderCreated={async (orderId, approvalUrl, amount, originalCartItems, orderTypeFromCheckout) => {
          console.log('📢📢📢 [DASHBOARD] ========== CALLBACK RECEIVED ==========');
          console.log('📢📢📢 [DASHBOARD] PayPal order created, showing QR dialog:', {
            orderId,
            amount,
            hasApprovalUrl: !!approvalUrl,
            approvalUrl: approvalUrl?.substring(0, 50) + '...',
            cartItemsCount: originalCartItems?.length || 0,
            orderTypeFromCheckout: orderTypeFromCheckout
          });
          console.log('📢📢📢 [DASHBOARD] Setting state values...');
          setPaypalOrderId(orderId);
          setPaypalApprovalUrl(approvalUrl);
          setPaypalAmount(amount);
          setPaypalOriginalCartItems(originalCartItems || []); // CRITICAL: Store original cart items
          setPaypalOrderType(orderTypeFromCheckout || null); // CRITICAL: Store order_type for correct VAT
          setPaypalQRDialogOpen(true);
          console.log('📢📢📢 [DASHBOARD] State set - QR dialog should open now!');

          // CRITICAL: Trigger automatic print with QR code (same logic as handleCheckout)
          try {
            console.log('🖨️ [PAYPAL] Triggering automatic print with QR code...');

            // Fetch order data from Firebase
            const { doc, getDoc } = await import('../../services/firestoreCompat');
            const { db } = await import('../../services/firebaseConfig');
            const orderDoc = await getDoc(doc(db, 'orders', orderId));

            if (orderDoc.exists()) {
              const orderData = orderDoc.data();
              const orderNumber = orderData.order_number || orderId;

              // CRITICAL: Log order_type from Firebase to debug VAT issues
              console.log('🔍 [QR RECEIPT DEBUG] Order data from Firebase:', {
                orderId: orderId,
                orderNumber: orderNumber,
                order_type_from_firebase: orderData.order_type,
                order_type_type: typeof orderData.order_type,
                all_orderData_keys: Object.keys(orderData),
                orderData_snapshot: JSON.stringify({
                  order_type: orderData.order_type,
                  customer_name: orderData.customer_name,
                  payment_method: orderData.payment_method
                }, null, 2)
              });

              // CRITICAL: Get order_type - prioritize callback parameter (most reliable), then stored state, then Firebase, then fallback
              // Use order_type from callback parameter directly (not from state, as state updates are async)
              let orderTypeFromFirebase = orderTypeFromCheckout || paypalOrderType || orderData.order_type;

              if (!orderTypeFromFirebase) {
                // Try alternative field names in Firebase
                orderTypeFromFirebase = (orderData as any).orderType ||
                  (orderData as any).type ||
                  'dine_in';
                console.warn('⚠️ [QR RECEIPT] order_type not found, trying alternatives:', {
                  fromCallback: orderTypeFromCheckout,
                  fromState: paypalOrderType,
                  fromFirebase: orderData.order_type,
                  orderType: (orderData as any).orderType,
                  type: (orderData as any).type,
                  defaulting_to: orderTypeFromFirebase,
                  all_keys: Object.keys(orderData)
                });
              } else {
                const source = orderTypeFromCheckout ? 'callback_parameter' : (paypalOrderType ? 'stored_state' : 'firebase');
                console.log(`✅ [QR RECEIPT] order_type found (${source}):`, orderTypeFromFirebase);
              }

              // Prepare receipt data with QR code (same as handleCheckout)
              // CRITICAL: Ensure items have category_name from Firebase for correct VAT calculation
              // Determine customer name: if no name and no address, show order type label
              let qrReceiptCustomerName = orderData.customer_name;
              const qrReceiptCustomerAddress = orderData.customer_address
                ? `${orderData.customer_address}, ${orderData.customer_postcode || ''} ${orderData.customer_city || ''}`.trim()
                : undefined;
              if (!qrReceiptCustomerName && !qrReceiptCustomerAddress) {
                // No name and no address - show order type label:
                // - Takeaway → "Abholung"
                // - Delivery → "Lieferung"
                // - Dine In → "Bestellung"
                if (orderTypeFromFirebase === 'takeaway') {
                  qrReceiptCustomerName = 'Abholung';
                } else if (orderTypeFromFirebase === 'delivery') {
                  qrReceiptCustomerName = 'Lieferung';
                } else {
                  qrReceiptCustomerName = 'Bestellung'; // Dine In order without name/address
                }
              } else if (!qrReceiptCustomerName) {
                qrReceiptCustomerName = 'Bestellung'; // Has address but no name (for dine_in)
              }

              const receiptData = {
                orderNumber: orderNumber,
                customerName: qrReceiptCustomerName,
                customerNumber: '56447',
                orderDate: new Date().toLocaleDateString('en-US'),
                orderTime: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
                items: (originalCartItems || orderData.items || []).map((item: any) => {
                  // CRITICAL: Get category_name from Firebase order item if using originalCartItems
                  let categoryName = item.category_name || item.category || '';
                  if (originalCartItems && originalCartItems.length > 0) {
                    // Find corresponding Firebase item to get category_name
                    const firebaseItem = (orderData.items || []).find((fi: any) =>
                      (fi.menu_item_name || fi.name) === (item.name || item.menu_item_name) ||
                      fi.menu_item_id === item.id
                    );
                    if (firebaseItem) {
                      categoryName = firebaseItem.category_name || firebaseItem.category || categoryName;
                    }
                  }

                  return {
                    name: item.name || item.menu_item_name,
                    quantity: item.quantity || 1,
                    price: item.price || item.unit_price || 0,
                    category_name: categoryName, // CRITICAL: Must have category_name for correct VAT calculation
                  };
                }),
                subtotal: orderData.subtotal || 0,
                tax: orderData.tax || 0,
                total: orderData.total || amount,
                paymentMethod: orderData.payment_method || 'paypal',
                paymentStatus: orderData.payment_status || 'pending',
                order_type: orderTypeFromFirebase, // CRITICAL: Use order_type from Firebase (with fallback)
                paypalApprovalUrl: approvalUrl, // CRITICAL: Include QR code URL
                isCopy: false,
                tableNumber: orderData.table_number || null,
              };

              console.log('🧾 [QR RECEIPT] Receipt data prepared:', {
                orderNumber: receiptData.orderNumber,
                order_type: receiptData.order_type,
                order_type_source: orderTypeFromCheckout ? 'callback_parameter' : (paypalOrderType ? 'stored_state' : (orderData.order_type ? 'firebase' : 'fallback')),
                orderTypeFromCheckout: orderTypeFromCheckout,
                paypalOrderType: paypalOrderType,
                orderData_order_type: orderData.order_type,
                items_count: receiptData.items.length,
                items_with_category: receiptData.items.map((i: any) => ({
                  name: i.name,
                  category_name: i.category_name,
                  price: i.price
                }))
              });

              // Add customer address if available
              const receiptDataWithAddress = {
                ...receiptData,
                customerAddress: qrReceiptCustomerAddress,
                customerPhone: orderData.customer_phone || undefined,
              };

              // Create Firestore print jobs (relay .exe picks them up and sends to printers)
              // DINE-IN: kitchen only — cashier receipt is printed when the tab is closed.
              // TAKEAWAY/DELIVERY: both cashier receipt + kitchen.
              console.log('[PRINT] Creating Firestore print jobs for PayPal order...');
              try {
                if (orderTypeFromFirebase === 'dine_in') {
                  const kitchenResults = await firestorePrintService.printKitchenOrder(
                    orderId,
                    orderNumber,
                    receiptDataWithAddress
                  );
                  kitchenResults.forEach(r => console.log('[PRINT] PayPal kitchen job:', r.jobId, r.success ? 'ok' : r.message));
                } else {
                  const printResults = await firestorePrintService.printOrder(
                    orderId,
                    orderNumber,
                    receiptDataWithAddress
                  );
                  printResults.receipt.forEach(r => console.log('[PRINT] PayPal cashier receipt job:', r.jobId, r.success ? 'ok' : r.message));
                  printResults.kitchen.forEach(r => console.log('[PRINT] PayPal kitchen job:', r.jobId, r.success ? 'ok' : r.message));
                }
              } catch (printError) {
                console.error('[PRINT] Error creating PayPal print jobs:', printError);
              }
            } else {
              console.error('❌ [PAYPAL] Order not found for automatic print:', orderId);
            }
          } catch (printError) {
            console.error('❌ [PAYPAL] Error triggering automatic print:', printError);
          }
        }}
      />

      {/* PayPal QR Payment Dialog - Rendered here to stay visible even when CheckoutDialog closes */}
      <PayPalQRPayment
        key={`paypal-qr-${paypalOrderId || 'none'}`}
        open={paypalQRDialogOpen && !!paypalApprovalUrl && !!paypalOrderId}
        onClose={() => {
          console.log('🔒 [DASHBOARD] Closing PayPal QR dialog');
          setPaypalQRDialogOpen(false);
          setPaypalOrderId(null);
          setPaypalApprovalUrl(null);
          setPaypalAmount(0);
          setPaypalOriginalCartItems([]); // Clear cart items
          setPaypalOrderType(null); // Clear order_type
        }}
        approvalUrl={paypalApprovalUrl || ''}
        orderId={paypalOrderId || ''}
        amount={paypalAmount}
        onPaymentComplete={async () => {
          console.log('✅ [DASHBOARD] PayPal payment completed!');

          // Generate receipt with "BEREITS GEZAHLT" status
          if (paypalOrderId) {
            try {
              console.log('🧾 [DASHBOARD] Generating payment confirmation receipt...');

              // Fetch updated order from Firebase
              const { doc, getDoc } = await import('../../services/firestoreCompat');
              const { db } = await import('../../services/firebaseConfig');
              const orderDoc = await getDoc(doc(db, 'orders', paypalOrderId));

              if (orderDoc.exists()) {
                const orderData = orderDoc.data();

                // CRITICAL: Log the raw Firebase data to see what we're working with
                console.log('🔍 [PAYMENT CONFIRMATION] Raw Firebase order data:', {
                  orderId: paypalOrderId,
                  itemsCount: orderData.items?.length || 0,
                  firstItem: orderData.items?.[0] || null,
                  allItems: orderData.items || [],
                  hasOriginalCart: paypalOriginalCartItems && paypalOriginalCartItems.length > 0,
                  originalCartCount: paypalOriginalCartItems?.length || 0,
                });

                // CRITICAL FIX: Use ORIGINAL cart items for calculations if available
                // This ensures totals match the items displayed on the receipt
                const itemsForCalculation = (paypalOriginalCartItems && paypalOriginalCartItems.length > 0)
                  ? paypalOriginalCartItems
                  : (orderData.items || []);

                console.log('🧮 [PAYMENT CONFIRMATION] Calculating totals from:', {
                  source: (paypalOriginalCartItems && paypalOriginalCartItems.length > 0) ? 'ORIGINAL CART' : 'FIREBASE',
                  itemsCount: itemsForCalculation.length,
                });

                // CRITICAL: Determine tax rate based on order type and item category (same logic as order creation)
                const orderType = orderData.order_type || 'dine_in';

                // Calculate totals from items to validate - USE ORDER TYPE AND CATEGORY FOR TAX CALCULATION
                const calculatedSubtotal = itemsForCalculation.reduce((sum: number, item: any) => {
                  // Use original cart price if available, otherwise use Firebase data
                  const itemPrice = (paypalOriginalCartItems && paypalOriginalCartItems.length > 0)
                    ? parseFloat(item.price || 0)
                    : parseFloat(item.unit_price || item.price || 0);
                  const itemQuantity = parseInt(item.quantity || 1);
                  const itemTotal = itemPrice * itemQuantity;

                  // CRITICAL: Use stored tax_rate from Firebase if available (it was calculated correctly at order creation)
                  // Otherwise, calculate based on order type and item category
                  let taxRate: number;
                  if (item.tax_rate !== undefined && item.tax_rate !== null) {
                    // Use stored tax_rate from Firebase (most accurate)
                    taxRate = parseFloat(item.tax_rate);
                  } else {
                    // Fallback: Calculate based on order type and category
                    const categoryName = (item.category_name || item.category || '').toLowerCase();
                    const itemNameLower = (item.name || '').toLowerCase();

                    // CRITICAL: Check if item is a drink (always 19% VAT in Germany - TSE rule)
                    // German TSE rules: ALL drinks = 19% VAT regardless of order type (dine-in, takeaway, delivery)
                    // and regardless of payment method (cash, card, PayPal)
                    const isDrink = categoryName.includes('drink') ||
                      categoryName.includes('getränk') ||
                      categoryName.includes('beverage') ||
                      categoryName.includes('getränke') ||
                      itemNameLower.includes('cola') ||
                      itemNameLower.includes('coca') ||
                      itemNameLower.includes('pepsi') ||
                      itemNameLower.includes('sprite') ||
                      itemNameLower.includes('fanta') ||
                      itemNameLower.includes('wasser') ||
                      itemNameLower.includes('water') ||
                      itemNameLower.includes('saft') ||
                      itemNameLower.includes('juice') ||
                      itemNameLower.includes('limo') ||
                      itemNameLower.includes('softdrink') ||
                      itemNameLower.includes('bier') ||
                      itemNameLower.includes('beer') ||
                      itemNameLower.includes('wein') ||
                      itemNameLower.includes('wine') ||
                      itemNameLower.includes('kaffee') ||
                      itemNameLower.includes('coffee') ||
                      itemNameLower.includes('tee') ||
                      itemNameLower.includes('tea');

                    // CRITICAL GERMAN TSE RULES:
                    // - Drinks: ALWAYS 19% VAT (regardless of order type: dine-in, takeaway, delivery)
                    // - Drinks: ALWAYS 19% VAT (regardless of payment method: cash, card, PayPal)
                    // - Food (Takeaway/Delivery): 7% VAT
                    // - Food (Dine-in): 19% VAT
                    if (isDrink) {
                      taxRate = 19; // CRITICAL: Drinks ALWAYS 19% VAT in Germany (TSE rule)
                    } else {
                      taxRate = (orderType === 'takeaway' || orderType === 'delivery') ? 7 : 19;
                    }
                  }

                  const basePrice = itemTotal / (1 + taxRate / 100);
                  return sum + basePrice;
                }, 0);

                const calculatedTax = itemsForCalculation.reduce((sum: number, item: any) => {
                  const itemPrice = (paypalOriginalCartItems && paypalOriginalCartItems.length > 0)
                    ? parseFloat(item.price || 0)
                    : parseFloat(item.unit_price || item.price || 0);
                  const itemQuantity = parseInt(item.quantity || 1);
                  const itemTotal = itemPrice * itemQuantity;

                  // CRITICAL: Use stored tax_rate from Firebase if available (it was calculated correctly at order creation)
                  // Otherwise, calculate based on order type and item category
                  let taxRate: number;
                  if (item.tax_rate !== undefined && item.tax_rate !== null) {
                    // Use stored tax_rate from Firebase (most accurate)
                    taxRate = parseFloat(item.tax_rate);
                  } else {
                    // Fallback: Calculate based on order type and category
                    const categoryName = (item.category_name || item.category || '').toLowerCase();
                    const itemNameLower = (item.name || '').toLowerCase();

                    // CRITICAL: Check if item is a drink (always 19% VAT in Germany - TSE rule)
                    // German TSE rules: ALL drinks = 19% VAT regardless of order type (dine-in, takeaway, delivery)
                    // and regardless of payment method (cash, card, PayPal)
                    const isDrink = categoryName.includes('drink') ||
                      categoryName.includes('getränk') ||
                      categoryName.includes('beverage') ||
                      categoryName.includes('getränke') ||
                      itemNameLower.includes('cola') ||
                      itemNameLower.includes('coca') ||
                      itemNameLower.includes('pepsi') ||
                      itemNameLower.includes('sprite') ||
                      itemNameLower.includes('fanta') ||
                      itemNameLower.includes('wasser') ||
                      itemNameLower.includes('water') ||
                      itemNameLower.includes('saft') ||
                      itemNameLower.includes('juice') ||
                      itemNameLower.includes('limo') ||
                      itemNameLower.includes('softdrink') ||
                      itemNameLower.includes('bier') ||
                      itemNameLower.includes('beer') ||
                      itemNameLower.includes('wein') ||
                      itemNameLower.includes('wine') ||
                      itemNameLower.includes('kaffee') ||
                      itemNameLower.includes('coffee') ||
                      itemNameLower.includes('tee') ||
                      itemNameLower.includes('tea');

                    // CRITICAL GERMAN TSE RULES:
                    // - Drinks: ALWAYS 19% VAT (regardless of order type: dine-in, takeaway, delivery)
                    // - Drinks: ALWAYS 19% VAT (regardless of payment method: cash, card, PayPal)
                    // - Food (Takeaway/Delivery): 7% VAT
                    // - Food (Dine-in): 19% VAT
                    if (isDrink) {
                      taxRate = 19; // CRITICAL: Drinks ALWAYS 19% VAT in Germany (TSE rule)
                    } else {
                      taxRate = (orderType === 'takeaway' || orderType === 'delivery') ? 7 : 19;
                    }
                  }

                  const basePrice = itemTotal / (1 + taxRate / 100);
                  const itemTax = itemTotal - basePrice;
                  return sum + itemTax;
                }, 0);

                const calculatedTotal = itemsForCalculation.reduce((sum: number, item: any) => {
                  const itemPrice = (paypalOriginalCartItems && paypalOriginalCartItems.length > 0)
                    ? parseFloat(item.price || 0)
                    : parseFloat(item.unit_price || item.price || 0);
                  const itemQuantity = parseInt(item.quantity || 1);
                  return sum + (itemPrice * itemQuantity);
                }, 0);

                console.log('🧮 [PAYMENT CONFIRMATION] Calculated totals:', {
                  subtotal: calculatedSubtotal,
                  tax: calculatedTax,
                  total: calculatedTotal,
                  fromOrder: {
                    subtotal: orderData.subtotal,
                    tax: orderData.tax,
                    total: orderData.total,
                  },
                  match: {
                    subtotal: Math.abs(orderData.subtotal - calculatedSubtotal) < 0.01,
                    tax: Math.abs(orderData.tax - calculatedTax) < 0.01,
                    total: Math.abs(orderData.total - calculatedTotal) < 0.01,
                  },
                });

                // Prepare receipt data with payment completed status
                // Determine customer name: if no name and no address, show order type label
                let confirmationCustomerName = orderData.customer_name;
                const confirmationCustomerAddress = orderData.customer_address || undefined;
                if (!confirmationCustomerName && !confirmationCustomerAddress) {
                  // No name and no address - show order type label:
                  // - Takeaway → "Abholung"
                  // - Delivery → "Lieferung"
                  // - Dine In → "Bestellung"
                  if (orderType === 'takeaway') {
                    confirmationCustomerName = 'Abholung';
                  } else if (orderType === 'delivery') {
                    confirmationCustomerName = 'Lieferung';
                  } else {
                    confirmationCustomerName = 'Bestellung'; // Dine In order without name/address
                  }
                } else if (!confirmationCustomerName) {
                  confirmationCustomerName = 'Bestellung'; // Has address but no name (for dine_in)
                }

                const receiptData = {
                  orderNumber: orderData.order_number || paypalOrderId,
                  customerName: confirmationCustomerName,
                  customerNumber: orderData.customer_number || '56447',
                  customerAddress: confirmationCustomerAddress,
                  customerPhone: orderData.customer_phone || undefined,
                  orderDate: orderData.created_at?.toDate ?
                    orderData.created_at.toDate().toLocaleDateString('en-US') :
                    new Date().toLocaleDateString('en-US'),
                  orderTime: orderData.created_at?.toDate ?
                    orderData.created_at.toDate().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) :
                    new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
                  items: (paypalOriginalCartItems && paypalOriginalCartItems.length > 0
                    ? paypalOriginalCartItems
                    : (orderData.items || [])
                  ).map((item: any, index: number) => {
                    // CRITICAL FIX: Use ORIGINAL cart items if available (source of truth!)
                    // Otherwise fall back to Firebase data
                    let itemName: string;
                    let itemPrice: number;
                    let itemQuantity: number;

                    if (paypalOriginalCartItems && paypalOriginalCartItems.length > 0) {
                      // Use ORIGINAL cart data - this is the source of truth!
                      itemName = item.name || 'Unbekanntes Produkt';
                      itemPrice = parseFloat(item.price) || 0;
                      itemQuantity = parseInt(item.quantity) || 1;
                      console.log(`✅ [PAYMENT CONFIRMATION] Item ${index + 1} using ORIGINAL cart data:`, {
                        name: itemName,
                        price: itemPrice,
                        quantity: itemQuantity,
                      });
                    } else {
                      // Fall back to Firebase data (shouldn't happen, but safety net)
                      console.log(`⚠️ [PAYMENT CONFIRMATION] Item ${index + 1} using FIREBASE data (fallback):`, {
                        rawItem: item,
                        allKeys: Object.keys(item || {}),
                      });

                      itemName = item.menu_item_name || item.name || item.item_name || item.title || item.product_name || 'Unbekanntes Produkt';
                      itemQuantity = parseInt(item.quantity || item.qty || 1);

                      // Try unit_price first (this is what we save)
                      itemPrice = 0;
                      if (item.unit_price !== undefined && item.unit_price !== null) {
                        const parsed = parseFloat(item.unit_price);
                        if (!isNaN(parsed) && parsed > 0) {
                          itemPrice = parsed;
                        }
                      }

                      // Fallback to price
                      if (itemPrice === 0 && item.price !== undefined && item.price !== null) {
                        const parsed = parseFloat(item.price);
                        if (!isNaN(parsed) && parsed > 0) {
                          itemPrice = parsed;
                        }
                      }

                      // Last resort: calculate from total
                      if (itemPrice === 0 && item.total && item.quantity) {
                        const parsed = parseFloat(item.total) / parseInt(item.quantity);
                        if (!isNaN(parsed) && parsed > 0) {
                          itemPrice = parsed;
                        }
                      }

                      // Validate price is a valid number
                      if (isNaN(itemPrice) || itemPrice <= 0) {
                        console.error(`❌ [RECEIPT] Invalid price for payment confirmation item ${index + 1}:`, {
                          item,
                          parsedPrice: itemPrice,
                        });
                        itemPrice = 0;
                      }
                    }

                    const itemTotal = itemPrice * itemQuantity;

                    console.log(`📋 [RECEIPT] Payment confirmation item ${index + 1} final:`, {
                      source: paypalOriginalCartItems && paypalOriginalCartItems.length > 0 ? 'ORIGINAL CART' : 'FIREBASE',
                      name: itemName,
                      quantity: itemQuantity,
                      price: itemPrice,
                      total: itemTotal,
                    });

                    // CRITICAL: Get tax_rate from Firebase order item if using original cart items
                    let itemTaxRate: number | undefined;
                    let itemCategoryName: string;

                    if (paypalOriginalCartItems && paypalOriginalCartItems.length > 0) {
                      // Get tax_rate and category_name from corresponding Firebase order item
                      const firebaseItem = (orderData.items || []).find((fi: any) =>
                        (fi.menu_item_name || fi.name) === itemName ||
                        fi.menu_item_id === item.id
                      );

                      if (firebaseItem) {
                        itemTaxRate = firebaseItem.tax_rate;
                        itemCategoryName = firebaseItem.category_name || item.category_name || item.category || '';
                      } else {
                        itemTaxRate = item.tax_rate;
                        itemCategoryName = item.category_name || item.category || '';
                      }
                    } else {
                      itemTaxRate = item.tax_rate;
                      itemCategoryName = item.category_name || item.category || '';
                    }

                    return {
                      name: itemName,
                      quantity: itemQuantity,
                      price: itemPrice, // This is the unit price (price_with_tax per unit)
                      category_name: itemCategoryName,
                      tax_rate: itemTaxRate, // CRITICAL: Include tax_rate for receipt generation
                    };
                  }),
                  subtotal: Math.abs(orderData.subtotal - calculatedSubtotal) < 0.01 ? orderData.subtotal : calculatedSubtotal,
                  tax: Math.abs(orderData.tax - calculatedTax) < 0.01 ? orderData.tax : calculatedTax,
                  total: Math.abs(orderData.total - calculatedTotal) < 0.01 ? orderData.total : calculatedTotal,
                  // CRITICAL: Use payment_method directly from orderData (don't convert card to paypal)
                  paymentMethod: orderData.payment_method || 'cash',
                  paymentStatus: 'completed', // CRITICAL: Payment is completed
                  order_type: paypalOrderType || orderData.order_type || 'dine_in', // CRITICAL: Use stored order_type (most reliable)
                  paypalApprovalUrl: undefined, // Don't show QR code on payment confirmation receipt
                  isCopy: true, // CRITICAL: Confirmation receipt IS a copy (shows "Kopie")
                  tableNumber: orderData.table_number || null,
                };

                console.log('🧾 [CONFIRMATION RECEIPT] Order type:', {
                  fromState: paypalOrderType,
                  fromFirebase: orderData.order_type,
                  final: paypalOrderType || orderData.order_type || 'dine_in'
                });

                // CRITICAL FIX: Prevent multiple prints - use a flag
                const printFlagKey = `payment_receipt_printed_${paypalOrderId}`;
                const alreadyPrinted = sessionStorage.getItem(printFlagKey) === 'true';

                if (alreadyPrinted) {
                  console.log('⏸️ [DASHBOARD] Payment confirmation receipt already printed, skipping...');
                } else {
                  // Mark as printed immediately to prevent multiple prints
                  sessionStorage.setItem(printFlagKey, 'true');

                  console.log('🖨️ [CONFIRMATION PRINT] Starting payment confirmation receipt generation...');
                  console.log('🖨️ [CONFIRMATION PRINT] Receipt data:', {
                    orderNumber: receiptData.orderNumber,
                    order_type: receiptData.order_type,
                    items_count: receiptData.items.length,
                    total: receiptData.total,
                    paymentStatus: receiptData.paymentStatus
                  });

                  // Generate receipt HTML with "BEREITS GEZAHLT"
                  const receiptHTML = generateCashierReceiptHTML(receiptData);

                  // STEP 1: Save PDF to receipts folder (same as QR receipt)
                  console.log('📄 [CONFIRMATION PRINT] Step 1: Saving confirmation receipt as PDF...');
                  const confirmationOrderNumber = receiptData.orderNumber || paypalOrderId || 'UNKNOWN';
                  savePDFForTesting(receiptHTML, `Kassenbeleg-${confirmationOrderNumber}-BEREITS-GEZAHLT.pdf`);
                  console.log(`📄 [CONFIRMATION PRINT] PDF save dialog opened for confirmation receipt: Kassenbeleg-${confirmationOrderNumber}-BEREITS-GEZAHLT.pdf`);

                  // Open receipt in new window (ONCE) - ALWAYS try to open
                  try {
                    const receiptWindow = window.open('', '_blank', 'width=400,height=600');
                    if (receiptWindow) {
                      receiptWindow.document.write(receiptHTML);
                      receiptWindow.document.close();
                      console.log('✅ [CONFIRMATION PRINT] Payment confirmation receipt opened in new window');

                      // Auto-print after a short delay
                      setTimeout(() => {
                        try {
                          receiptWindow.print();
                          console.log('✅ [CONFIRMATION PRINT] Browser print dialog opened');
                        } catch (printErr) {
                          console.warn('⚠️ [CONFIRMATION PRINT] Could not auto-print:', printErr);
                        }
                      }, 500);
                    } else {
                      console.warn('⚠️ [CONFIRMATION PRINT] Popup blocked - receipt window could not be opened');
                    }
                  } catch (windowError) {
                    console.error('❌ [CONFIRMATION PRINT] Error opening receipt window:', windowError);
                  }

                  // STEP 2: Create Firestore print job for confirmation receipt
                  try {
                    console.log('[PRINT] Creating confirmation receipt print job...');
                    const confirmResults = await firestorePrintService.printCashierReceipt(
                      paypalOrderId || '',
                      receiptData.orderNumber,
                      receiptData
                    );
                    for (const confirmResult of confirmResults) {
                      if (confirmResult.success) {
                        console.log('[PRINT] Confirmation receipt job created:', confirmResult.jobId);
                      } else {
                        console.warn('[PRINT] Confirmation receipt job failed:', confirmResult.message);
                      }
                    }
                  } catch (printError) {
                    console.error('[PRINT] Error creating confirmation print job:', printError);
                  }
                }
              } else {
                console.error('❌ [DASHBOARD] Order not found:', paypalOrderId);
              }
            } catch (error) {
              console.error('❌ [DASHBOARD] Error generating payment confirmation receipt:', error);
            }
          }

          // Close dialog and clear state
          setPaypalQRDialogOpen(false);
          setPaypalOrderId(null);
          setPaypalApprovalUrl(null);
          setPaypalAmount(0);
          setPaypalOriginalCartItems([]); // Clear cart items
          setPaypalOrderType(null); // Clear order_type
        }}
        onPaymentFailed={(error) => {
          console.error('❌ [DASHBOARD] PayPal payment failed:', error);
          // Optionally show error message
        }}
        onPaymentCancelled={async () => {
          console.log('❌ [DASHBOARD] PayPal payment cancelled');
          if (paypalOrderId) {
            try {
              // Order is already marked as cancelled in PayPalQRPayment component
              // Just close dialog and clear state
              console.log('✅ [DASHBOARD] Payment cancelled, closing dialog');
            } catch (error) {
              console.error('❌ [DASHBOARD] Error handling payment cancellation:', error);
            }
          }

          // Close dialog and clear state
          setPaypalQRDialogOpen(false);
          setPaypalOrderId(null);
          setPaypalApprovalUrl(null);
          setPaypalAmount(0);
          setPaypalOriginalCartItems([]);
        }}
      />

      {/* Split Table Dialog - New Split */}
      {splitDialogOpen && cart.length > 0 && !selectedDraft && (
        <SplitTableDialog
          open={splitDialogOpen}
          onClose={() => setSplitDialogOpen(false)}
          tableId={0}
          tableNumber=""
          orderId=""
          orderTotal={cartTotals.total}
          orderItems={cart}
          staffMember="POS System"
        />
      )}

      {/* Split Table Dialog - Resume from Draft */}
      {splitDialogOpen && selectedDraft && (
        <SplitTableDialog
          open={splitDialogOpen}
          onClose={() => {
            setSplitDialogOpen(false);
            setSelectedDraft(null);
          }}
          tableId={selectedDraft.table_id}
          tableNumber={selectedDraft.table_number}
          orderId={selectedDraft.order_id}
          orderTotal={selectedDraft.original_order_total}
          orderItems={[]} // Will be loaded from draft
          staffMember={selectedDraft.created_by || 'POS System'}
        />
      )}

      {/* Add Item with Add-ons Modal */}
      {selectedProductForAddOns && (
        <AddItemWithAddOnsModal
          open={addItemModalOpen}
          onClose={() => {
            setAddItemModalOpen(false);
            setSelectedProductForAddOns(null);
          }}
          product={selectedProductForAddOns}
          onAddToCart={addToCart}
          onUpdateCartItem={updateCartItem}
          language={language}
          cartItem={selectedProductForAddOns.cartId ? selectedProductForAddOns : undefined}
          cartId={selectedProductForAddOns.cartId}
        />
      )}

      {/* QR Order Notification — Centered Popup */}
      <Dialog
        open={qrNotification.open}
        onClose={() => setQrNotification({open: false, message: ''})}
        PaperProps={{
          sx: {
            borderRadius: 4,
            padding: '32px 40px',
            textAlign: 'center',
            minWidth: 360,
            maxWidth: 460,
            background: 'linear-gradient(135deg, rgba(0, 200, 150, 0.08) 0%, #252838 100%)',
            border: '1px solid #3a3d52',
            boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
            animation: 'qrPopIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
            '@keyframes qrPopIn': {
              '0%': { transform: 'scale(0.5)', opacity: 0 },
              '100%': { transform: 'scale(1)', opacity: 1 },
            },
          }
        }}
        BackdropProps={{
          sx: { backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }
        }}
      >
        <Box sx={{ fontSize: 64, mb: 1, lineHeight: 1 }}>🔔</Box>
        <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, fontFamily: '"Poppins"', color: '#00C896 !important', mb: 1 }}>
          Neue QR-Bestellung!
        </Typography>
        <Typography sx={{ fontSize: '1.1rem', fontWeight: 600, fontFamily: '"Poppins"', color: '#e0e0e0 !important', mb: 2 }}>
          {qrNotification.message}
        </Typography>
        <Button
          variant="contained"
          onClick={() => setQrNotification({open: false, message: ''})}
          sx={{
            backgroundColor: '#00C896 !important',
            color: '#1a1d2e !important',
            fontWeight: 700,
            fontFamily: '"Poppins"',
            fontSize: '1rem',
            borderRadius: 3,
            px: 5, py: 1.2,
            '&:hover': { backgroundColor: '#7bc912 !important' },
          }}
        >
          OK
        </Button>
      </Dialog>

      {/* FRAUD PREVENTION #2: Manager PIN dialog */}
      {managerPinAction && (
        <ManagerPinDialog
          open={managerPinOpen}
          actionLabel={managerPinAction.label}
          requireReason={managerPinAction.requireReason}
          onApproved={(email, reason) => {
            setManagerPinOpen(false);
            const cb = managerPinAction.onApproved;
            setManagerPinAction(null);
            cb(email, reason);
          }}
          onClose={() => {
            setManagerPinOpen(false);
            setManagerPinAction(null);
          }}
        />
      )}
    </>
  );
};

export default MinimalistDashboard;